import React, { useState, useEffect } from "react"
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  Collapse,
  Grid,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  alpha,
  Card,
  styled,
  Checkbox,
  TableSortLabel,
  TextField,
  Alert,
  Menu,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  FormControlLabel,
  Chip,
  Radio,
  RadioGroup,
  Stack,
  Skeleton,
} from "@mui/material"
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Payment,
  Person,
  LocationOn,
  LocationCity,
  Home,
  PinDrop,
  Public,
  ShoppingCart,
  Update as UpdateIcon,
  CheckCircle,
  Schedule,
  LocalShippingOutlined,
  Cancel,
  Refresh,
  FileDownload,
  SelectAll,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  LocalShipping as DeliveryIcon,
  Assignment as ChallanIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Add as AddIcon,
  CameraAlt as CameraAltIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { markBulkOrderExpectReturn } from "./bulkOrderListCache"
import apiService from "Network/apiService"
import { API_ENDPOINTS } from "Network/apiEndpoints"
import buttonConfig from "config/ConfigAccessButton"
import {
  submitNewPaidPaymentStatus,
  buildNewPaidPaymentFormData,
  extractPaymentApiError,
  getOrderPaymentDetailsFromOrder,
  validateNewPaidPaymentRequest,
  validatePaidPaymentFormDetails,
  isZeroRemainingPayment,
  PAYMENT_UPDATE_LOCKED_ORDER_STATUSES,
  getOrderPaymentStatusValue,
  isPaymentUpdateDisabledForOrder,
  getPaymentUpdateDisabledReason,
  DEFAULT_PAYMENT_UPDATE_MODAL,
  getPaidPaymentStepState,
  getAdvancePaymentEntries,
} from "Network/paymentStatusService"
import {
  fetchAdvancePaymentMethods,
  buildPaymentMethodsById,
} from "Network/advancePaymentMethodService"
import { useToast } from "OtherReusableFunctionality/ToastContext"
import {
  PaymentDetailsStepContent,
  PAYMENT_DETAILS_DIALOG_CONTENT_SX,
  PAYMENT_DETAILS_DIALOG_ACTIONS_SX,
  PAYMENT_DETAILS_DIALOG_PAPER_PROPS,
} from "../Products/productList"

const BULK_ORDER_STATUS_TAB_STORAGE_KEY = 'bulk_order_status_tab';
const BULK_ORDER_LAST_OPENED_STORAGE_KEY = 'bulk_order_last_opened_id';
const BULK_ORDER_LIST_STATE_STORAGE_KEY = 'bulk_order_list_state';

export const rememberLastOpenedBulkOrder = (orderOrId) => {
  const orderId =
    typeof orderOrId === 'object' && orderOrId != null
      ? orderOrId.order_id || orderOrId.id
      : orderOrId;
  if (orderId == null || orderId === '') return;
  sessionStorage.setItem(BULK_ORDER_LAST_OPENED_STORAGE_KEY, String(orderId));
};

export const getLastOpenedBulkOrderId = () =>
  sessionStorage.getItem(BULK_ORDER_LAST_OPENED_STORAGE_KEY);

export const saveBulkOrderListState = (state = {}) => {
  try {
    sessionStorage.setItem(BULK_ORDER_LIST_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save Bulk Order list state:', error);
  }
};

export const consumeSavedBulkOrderListState = () => {
  try {
    const raw = sessionStorage.getItem(BULK_ORDER_LIST_STATE_STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(BULK_ORDER_LIST_STATE_STORAGE_KEY);
    return JSON.parse(raw);
  } catch (error) {
    console.error('Failed to restore Bulk Order list state:', error);
    sessionStorage.removeItem(BULK_ORDER_LIST_STATE_STORAGE_KEY);
    return null;
  }
};

export const consumeSavedBulkOrderStatusTab = () => {
  const savedTab = sessionStorage.getItem(BULK_ORDER_STATUS_TAB_STORAGE_KEY);
  if (!savedTab) return null;
  sessionStorage.removeItem(BULK_ORDER_STATUS_TAB_STORAGE_KEY);
  return savedTab;
};

const StatusChip = styled(Card)(({ status, theme }) => {
  const getStatusConfig = (status) => {
    const statusMap = {
      pending: {
        bg: theme.palette.warning.light,
        text: theme.palette.warning.dark,
        icon: <Schedule />
      },
      confirmed: {
        bg: theme.palette.info.light,
        text: theme.palette.info.dark,
        icon: <CheckCircle />
      },
      processing: {
        bg: theme.palette.primary.light,
        text: theme.palette.primary.dark,
        icon: <Schedule />
      },
      shipped: {
        bg: theme.palette.secondary.light,
        text: theme.palette.secondary.dark,
        icon: <LocalShippingOutlined />
      },
      delivered: {
        bg: theme.palette.success.light,
        text: theme.palette.success.dark,
        icon: <CheckCircle />
      },
      cancelled: {
        bg: theme.palette.error.light,
        text: theme.palette.error.dark,
        icon: <Cancel />
      },
      returned: {
        bg: theme.palette.error.dark,
        text: theme.palette.common.white,
        icon: <Refresh />
      }
    }
    return statusMap[status?.toLowerCase()] || {
      bg: theme.palette.grey[300],
      text: theme.palette.grey[800],
      icon: null
    }
  }

  const config = getStatusConfig(status)
  return {
    backgroundColor: config.bg,
    color: config.text,
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: 16,
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'capitalize',
    '& svg': {
      fontSize: '1rem',
      marginRight: 4
    }
  }
})

const PaymentStatusChip = styled(Chip)(({ status, theme }) => {
  const getPaymentStatusConfig = (status) => {
    const statusMap = {
      pending: {
        bg: '#fcc874', // Orange/amber - matching image
        text: '#FFFFFF',
        icon: <Schedule sx={{ fontSize: '16px !important' }} />
      },
      outstanding: {
        bg: '#888cff', // Moderate purple - matching image
        text: '#FFFFFF',
        icon: <Schedule sx={{ fontSize: '16px !important' }} />
      },
      paid: {
        bg: '#22d28a', // Vibrant green - matching image
        text: '#FFFFFF',
        icon: <CheckCircle sx={{ fontSize: '16px !important' }} />
      },
      failed: {
        bg: '#f4346e', // Vibrant red - matching image
        text: '#FFFFFF',
        icon: <Cancel sx={{ fontSize: '16px !important' }} />
      },
      refunded: {
        bg: '#888cff', // Lighter purple/lavender - matching image
        text: '#FFFFFF',
        icon: <CheckCircle sx={{ fontSize: '16px !important' }} />
      }
    }
    return statusMap[status?.toLowerCase()] || {
      bg: theme.palette.grey[400],
      text: '#FFFFFF',
      icon: null
    }
  }

  const config = getPaymentStatusConfig(status)
  return {
    backgroundColor: config.bg,
    color: config.text,
    fontWeight: 600,
    textTransform: 'capitalize',
    borderRadius: theme.shape.borderRadius,
    height: 'auto',
    padding: '4px 10px',
    minWidth: 'auto',
    '& .MuiChip-icon': {
      color: config.text,
      marginLeft: '4px',
      marginRight: '4px'
    },
    '& .MuiChip-label': {
      padding: '0 4px',
      fontSize: '0.875rem',
      color: config.text,
      fontWeight: 600
    }
  }
})

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "16px 20px",
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  whiteSpace: "nowrap",
  "&.header": {
      fontWeight: 600,
      fontSize: "0.875rem",
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      color: theme.palette.text.primary,
      position: 'sticky',
      top: 0,
      zIndex: 1,
  },
}));

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => prop !== 'highlighted',
})(({ theme, highlighted }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  ...(highlighted && {
    backgroundColor: alpha(theme.palette.grey[500], 0.14),
  }),
  "&:hover": {
      backgroundColor: highlighted
        ? alpha(theme.palette.grey[500], 0.2)
        : alpha(theme.palette.primary.main, 0.04),
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  },
  "& > .MuiTableCell-root": {
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  },
}));

const ExpandableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  '& > .MuiTableCell-root': {
    borderBottom: 'none',
  }
}));

const DetailCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  '&:last-child': {
    marginBottom: 0
  },
  '& h6': {
    marginBottom: theme.spacing(2)
  },
  '& .MuiDivider-root': {
    margin: theme.spacing(2, 0)
  }
}))

const InvoiceModal = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '90vw',
    maxHeight: '90vh',
    width: '1200px',
    height: '800px',
    margin: theme.spacing(2),
  },
}));

const InvoiceFrame = styled('iframe')(({ theme }) => ({
  width: '100%',
  height: '100%',
  border: 'none',
  borderRadius: theme.shape.borderRadius,
}));

const VEHICLE_REFERENCE_NO_REGEX = /^[A-Z]{2}\s\d{2}\s[A-Z]{2}\s\d{4}$/;
const VEHICLE_REFERENCE_NO_ERROR = 'Enter valid format: MH 12 AB 1234';
const formatVehicleReferenceNo = (value = '') => {
  const cleanedValue = String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
  const part1 = cleanedValue.slice(0, 2).replace(/[^A-Z]/g, '');
  const part2 = cleanedValue.slice(2, 4).replace(/[^0-9]/g, '');
  const part3 = cleanedValue.slice(4, 6).replace(/[^A-Z]/g, '');
  const part4 = cleanedValue.slice(6, 10).replace(/[^0-9]/g, '');
  return [part1, part2, part3, part4].filter(Boolean).join(' ');
};
const isVehicleReferenceNoInvalid = (value = '') => {
  const normalizedValue = String(value || '').trim();
  return normalizedValue !== '' && !VEHICLE_REFERENCE_NO_REGEX.test(normalizedValue);
};

const isTruthyFlag = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  const normalized = String(value ?? '').trim().toLowerCase();
  return ['true', '1', 'yes', 'on'].includes(normalized);
};

// API always requires vehicle_type. For Truck use the selected type;
// for Post/Courier/International (field hidden) fall back to order_by.
const resolveVehicleTypeForPayload = (orderBy, vehicleType) => {
  const selectedType = String(vehicleType || '').trim();
  if (selectedType) return selectedType;
  return String(orderBy || '').trim();
};

const getOrderChallan = (order) =>
  order?.delivery_challan || order?.deliveryChallan || order?.challan;

const isOrderByTruck = (order) => {
  if (!order) return false;
  const challan = getOrderChallan(order);
  const orderBy = String(
    order?.order_by || order?.order?.order_by || challan?.order_by || ''
  )
    .trim()
    .toLowerCase();
  return orderBy === 'truck' || orderBy === 'by truck';
};
  // Added new function to title-case an address
  /**
   * Title-case an address: capitalize first letter and first letter after every space.
   * If input is falsy, returns it as-is.
   */
  const titleCaseAddress = (value) => {
    if (value === null || value === undefined) return value;
    const str = String(value).trim();
    if (!str) return str;
    return str
      .split(" ")
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ""))
      .join(" ");
  };

const DEFAULT_DISPATCH_LOGISTICS = {
  meter_not_working: false,
  google_maps_km: '',
  diesel_receipt_number: '',
  diesel_amount: '',
  crates_dispatched: '0',
};

const DEFAULT_DELIVERED_LOGISTICS = {
  final_km: '',
  km_excess_reason: '',
  crates_returned: '',
  crates_short_reason: '',
  crates_returned_person: '',
  extra_plants: '0',
  plants_returned: '0',
  plants_returned_reason: '',
};

const getChallanLogisticsDefaults = (challan = {}) => ({
  meter_not_working: isTruthyFlag(challan?.meter_not_working),
  google_maps_km:
    challan?.google_maps_km !== null && challan?.google_maps_km !== undefined
      ? String(challan.google_maps_km)
      : '',
  diesel_receipt_number: challan?.diesel_receipt_number || '',
  diesel_amount:
    challan?.diesel_amount !== null && challan?.diesel_amount !== undefined
      ? String(challan.diesel_amount)
      : '',
  crates_dispatched:
    challan?.crates_dispatched !== null && challan?.crates_dispatched !== undefined
      ? String(challan.crates_dispatched)
      : '0',
  final_km:
    challan?.final_km !== null && challan?.final_km !== undefined
      ? String(challan.final_km)
      : '',
  km_excess_reason: challan?.km_excess_reason || '',
  crates_returned:
    challan?.crates_returned !== null && challan?.crates_returned !== undefined
      ? String(challan.crates_returned)
      : '',
  crates_short_reason: challan?.crates_short_reason || '',
  crates_returned_person: challan?.crates_returned_person || challan?.crates_return_person || '',
  extra_plants:
    challan?.extra_plants !== null && challan?.extra_plants !== undefined
      ? String(challan.extra_plants)
      : challan?.extra_plant !== null && challan?.extra_plant !== undefined
        ? String(challan.extra_plant)
        : '0',
  plants_returned:
    challan?.plants_returned !== null && challan?.plants_returned !== undefined
      ? String(challan.plants_returned)
      : '0',
  plants_returned_reason: challan?.plants_returned_reason || '',
});

const buildDispatchLogisticsPayload = (form = {}) => {
  const meterNotWorking = isTruthyFlag(form.meter_not_working);
  const cratesRaw = String(form.crates_dispatched ?? '').trim();
  const crates = cratesRaw === '' ? 0 : Number(cratesRaw);
  const extraPlantsRaw = String(form.extra_plants ?? form.extra_plant ?? '').trim();
  const extraPlants = extraPlantsRaw === '' ? 0 : Number(extraPlantsRaw);
  return {
    meter_not_working: meterNotWorking,
    diesel_receipt_number: String(form.diesel_receipt_number || '').trim(),
    diesel_amount: Number(form.diesel_amount),
    crates_dispatched: Number.isFinite(crates) && crates >= 0 ? crates : 0,
    extra_plants:
      Number.isFinite(extraPlants) && extraPlants >= 0 ? extraPlants : 0,
    ...(meterNotWorking ? { google_maps_km: Number(form.google_maps_km) } : {}),
  };
};

const validateDispatchLogistics = (form = {}) => {
  const isTruckOrderBy = String(form.orderBy || '').trim().toLowerCase() === 'truck';
  if (isTruckOrderBy) {
    if (!String(form.diesel_receipt_number || '').trim()) {
      return 'Diesel receipt number is required';
    }
    if (!(form.diesel_receipt_upload instanceof File)) {
      return 'Receipt upload is required';
    }
    const dieselAmount = Number(form.diesel_amount);
    if (
      String(form.diesel_amount ?? '').trim() === '' ||
      Number.isNaN(dieselAmount) ||
      dieselAmount < 0
    ) {
      return 'Diesel amount is required and must be ≥ 0';
    }
    if (isTruthyFlag(form.meter_not_working)) {
      const mapsKm = Number(form.google_maps_km);
      if (
        String(form.google_maps_km ?? '').trim() === '' ||
        Number.isNaN(mapsKm) ||
        mapsKm <= 0
      ) {
        return 'Google Maps KM is required and must be greater than 0 when meter is not working';
      }
    }
  }
  const cratesRaw = String(form.crates_dispatched ?? '').trim();
  if (cratesRaw !== '') {
    const crates = Number(cratesRaw);
    if (!Number.isInteger(crates) || crates < 0) {
      return 'Crates dispatched must be an integer ≥ 0';
    }
  }
  return '';
};

const isProcessingDispatchSelection = (currentTab = '', selectedStatus = '') =>
  String(currentTab || '').trim().toLowerCase() === 'processing' &&
  String(selectedStatus || '').trim().toLowerCase() === 'dispatch';

const isDispatchPaymentSatisfied = (paymentStatus = '') => {
  const normalized = String(paymentStatus || '').trim().toLowerCase();
  return normalized === 'paid' || normalized === 'credit';
};

const getDeliveredLogisticsContext = (form = {}, challan = {}) => {
  const meterNotWorking = isTruthyFlag(
    form.meter_not_working !== undefined && form.meter_not_working !== ''
      ? form.meter_not_working
      : challan?.meter_not_working
  );
  const googleMapsKm = Number(
    form.google_maps_km !== undefined && String(form.google_maps_km).trim() !== ''
      ? form.google_maps_km
      : challan?.google_maps_km
  );
  const cratesDispatched = Number(
    form.crates_dispatched !== undefined && String(form.crates_dispatched).trim() !== ''
      ? form.crates_dispatched
      : (challan?.crates_dispatched ?? 0)
  );
  return {
    meterNotWorking,
    googleMapsKm,
    cratesDispatched: Number.isFinite(cratesDispatched) ? cratesDispatched : 0,
  };
};

const validateDeliveredLogistics = (form = {}, challan = {}) => {
  const { meterNotWorking, googleMapsKm, cratesDispatched } =
    getDeliveredLogisticsContext(form, challan);

  if (String(form.crates_returned ?? '').trim() === '') {
    return 'Crates returned is required';
  }
  const cratesReturned = Number(form.crates_returned);
  if (
    Number.isNaN(cratesReturned) ||
    cratesReturned < 0 ||
    !Number.isInteger(cratesReturned)
  ) {
    return 'Crates returned must be an integer ≥ 0';
  }
  if (
    !String(form.crates_returned_person || form.crates_return_person || '').trim()
  ) {
    return 'Crates return person is required';
  }
  if (
    cratesReturned !== cratesDispatched &&
    !String(form.crates_short_reason || '').trim()
  ) {
    return 'Crate shortage reason is required when returned crates differ from dispatched';
  }

  const plantsReturnedRaw = String(form.plants_returned ?? '').trim();
  const plantsReturned = plantsReturnedRaw === '' ? 0 : Number(plantsReturnedRaw);
  if (
    Number.isNaN(plantsReturned) ||
    plantsReturned < 0 ||
    !Number.isInteger(plantsReturned)
  ) {
    return 'Plants returned must be an integer ≥ 0';
  }
  if (plantsReturned > 0 && !String(form.plants_returned_reason || '').trim()) {
    return 'Plants returned reason is required when plants returned is greater than 0';
  }

  if (meterNotWorking) {
    if (String(form.final_km ?? '').trim() === '') {
      return 'Final KM is required when meter was not working at dispatch';
    }
    const finalKm = Number(form.final_km);
    if (Number.isNaN(finalKm) || finalKm < 0) {
      return 'Final KM must be a valid number ≥ 0';
    }
    if (
      !Number.isNaN(googleMapsKm) &&
      finalKm !== googleMapsKm &&
      !String(form.km_excess_reason || '').trim()
    ) {
      return 'KM excess reason is required when final KM differs from Google Maps KM';
    }
  }
  return '';
};

const buildDeliveredLogisticsPayload = (form = {}, challan = {}) => {
  const { meterNotWorking, googleMapsKm, cratesDispatched } =
    getDeliveredLogisticsContext(form, challan);
  const cratesReturned = Number(form.crates_returned);
  const plantsReturnedRaw = String(form.plants_returned ?? '').trim();
  const plantsReturned = plantsReturnedRaw === '' ? 0 : Number(plantsReturnedRaw);
  const finalKm = Number(form.final_km);

  const payload = {
    crates_returned: cratesReturned,
    crates_returned_person: String(
      form.crates_returned_person || form.crates_return_person || ''
    ).trim(),
    plants_returned:
      Number.isFinite(plantsReturned) && plantsReturned >= 0 ? plantsReturned : 0,
  };

  if (cratesReturned !== cratesDispatched) {
    payload.crates_short_reason = String(form.crates_short_reason || '').trim();
  }
  if (plantsReturned > 0) {
    payload.plants_returned_reason = String(form.plants_returned_reason || '').trim();
  }
  if (meterNotWorking) {
    payload.final_km = finalKm;
    if (!Number.isNaN(googleMapsKm) && finalKm !== googleMapsKm) {
      payload.km_excess_reason = String(form.km_excess_reason || '').trim();
    }
  }
  return payload;
};

const getEntryNote = (entry = {}, orderOrProduct = null) => {
  if (entry?.comment != null && String(entry.comment).trim() !== '') {
    return String(entry.comment).trim();
  }
  const notes = entry?.order?.notes ?? orderOrProduct?.notes ?? orderOrProduct?.order?.notes;
  if (notes != null && String(notes).trim() !== '') {
    return String(notes).trim();
  }
  return '';
};

const formatBulkDetailCurrency = (value) => {
  const amount = Number(value || 0)
  return `₹${Number.isFinite(amount) ? amount.toFixed(2) : '0.00'}`
}

export const formatBulkProductData = (bulkProduct) => {
  if (!bulkProduct) return {}
  
  const statusNameRaw = bulkProduct.status?.name || (typeof bulkProduct.status === 'string' ? bulkProduct.status : '')
  const statusCategoryRaw = bulkProduct.status?.category || statusNameRaw
  const statusName = (statusNameRaw || '').toString().trim()
  const statusCategory = (statusCategoryRaw || '').toString().trim()
  const normalizedStatus = statusName.toLowerCase() || 'pending'

  const paymentStatusNameRaw = bulkProduct.payment_status?.name || (typeof bulkProduct.payment_status === 'string' ? bulkProduct.payment_status : '')
  const paymentStatusName = (paymentStatusNameRaw || '').toString().trim()
  const normalizedPaymentStatus = paymentStatusName.toLowerCase() || 'pending'
  const quantity = Number(bulkProduct.quantity || 0)
  const unitPrice = Number(bulkProduct.discounted_price || 0)
  const orderSubtotal = Number(bulkProduct.order?.subtotal || (unitPrice * quantity) || 0)
  const orderGstAmount = Number(bulkProduct.order?.gst_amount || 0)
  const orderDeliveryCharge = Number(bulkProduct.order?.delivery_charge || 0)
  const orderDiscountAmount = Number(bulkProduct.order?.discount_amount || 0)
  const orderTotalAmount = Number(bulkProduct.order?.total_amount || orderSubtotal || 0)
  
  return {
    id: bulkProduct.id,
    order_id: bulkProduct.order?.order_id || bulkProduct.order?.id,
    product_name: bulkProduct.product?.name || 'N/A',
    sku: bulkProduct.product?.sku || 'N/A',
    quantity: bulkProduct.quantity || 0,
    uom: bulkProduct.uom || 'pcs',
    discounted_price: parseFloat(bulkProduct.discounted_price?.replace?.('₹', '')?.trim?.() || bulkProduct.discounted_price || 0).toFixed(2),
    category: bulkProduct.category || 'N/A',
    company_name:
      bulkProduct.company_name ||
      bulkProduct.company?.name ||
      bulkProduct.order?.company_name ||
      'N/A',
    category_name:
      bulkProduct.category_name ||
      bulkProduct.product?.category_name ||
      'N/A',
    source: bulkProduct.source || 'N/A',
    status: normalizedStatus,
    status_name: statusName,
    status_category: statusCategory,
    payment_status: normalizedPaymentStatus,
    payment_status_name: paymentStatusName,
    created_at: bulkProduct.created_at || new Date().toISOString(),
    expected_delivery_date: bulkProduct.order?.expected_delivery_date || bulkProduct.expected_delivery_date || null,
    order_date_formatted: bulkProduct.order?.order_date_formatted || bulkProduct.order_date_formatted || null,
    customer_name: bulkProduct.end_user?.full_name || bulkProduct.customer_name || 'N/A',
    mobile_number: bulkProduct.end_user?.mobile_number || bulkProduct.mobile_number || 'N/A',
    alternate_number: bulkProduct.alternate_number || '',
    email: bulkProduct.end_user?.email || bulkProduct.email || '',
    gst_no: bulkProduct.gst_no || '',
    description: bulkProduct.description || 'No description available',
    reason: bulkProduct.order?.reason || bulkProduct.reason || '',
    product_image: bulkProduct.product?.image || bulkProduct.image_url || '',
    invoice: bulkProduct.invoice || null,
    delivery_challan: bulkProduct.delivery_challan || null,
    advance_payment: bulkProduct.advance_payment || null,
    order: {
      id: bulkProduct.order?.id || null,
      order_id: bulkProduct.order?.order_id || bulkProduct.order?.id || null,
      order_date_formatted: bulkProduct.order?.order_date_formatted || bulkProduct.order_date_formatted || null,
      delivery_date_formatted: bulkProduct.order?.delivery_date_formatted || null,
      delivery_date: bulkProduct.order?.delivery_date || null,
      expected_delivery_date: bulkProduct.order?.expected_delivery_date || null,
      order_by: bulkProduct.order?.order_by || null,
      order_by_entity: bulkProduct.order?.order_by_entity || null,
      notes: bulkProduct.order?.notes || null,
      reason: bulkProduct.order?.reason || bulkProduct.reason || null,
      cancelled_at: bulkProduct.order?.cancelled_at || null,
      returned_at: bulkProduct.order?.returned_at || null,
      refunded_amount: bulkProduct.order?.refunded_amount || null,
      refunded_at: bulkProduct.order?.refunded_at || null,
      subtotal: orderSubtotal,
      gst_amount: orderGstAmount,
      delivery_charge: orderDeliveryCharge,
      discount_amount: orderDiscountAmount,
      total_amount: orderTotalAmount,
      total_items: 1,
      total_quantity: quantity
    },
    address: bulkProduct.end_user ? {
      pincode: bulkProduct.end_user.pincode,
      country: bulkProduct.end_user.country,
      state: bulkProduct.end_user.state,
      district: bulkProduct.end_user.district,
      tehsil: bulkProduct.end_user.tehsil,
      village: bulkProduct.end_user.village
    } : null
  }
}

export function BulkOrderDetailsView({ product }) {
  const theme = useTheme()
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null)
  const dieselReceiptPhotoUrl = product?.delivery_challan?.diesel_receipt_photo
    ? apiService.resolveAuthenticatedFileUrl(product.delivery_challan.diesel_receipt_photo)
    : ''
  const isDieselReceiptPdf = /\.pdf($|\?)/i.test(String(product?.delivery_challan?.diesel_receipt_photo || ''))

  return (
<Box sx={{ width: '100%', padding: 2 }}>
            <Grid container spacing={2.5} alignItems="stretch">
              <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                <DetailCard 
                  elevation={2} 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'background.paper', 
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, 
                    borderRadius: 3,
                    p: 2.5
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', mb: 2 }}>
                    <Person sx={{ mr: 1, color: theme.palette.primary.main, flexShrink: 0 }} />
                    <Typography variant="h6" component="span" fontWeight={600} sx={{ mb: 0 }}>Customer Information</Typography>
                  </Box>
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Name:</strong> {product.customer_name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Order ID:</strong> {product.order_id || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Email:</strong> {product.email}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Order Date:</strong> {product.order_date_formatted || product.order?.order_date_formatted || "N/A"}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Mobile:</strong> {product.mobile_number}
                  </Typography>

                  {(product.order?.farmer_name || getAdvancePaymentEntries(product)[0]?.order?.farmer_name || product.advance_payment?.order?.farmer_name) && (
                    <Box sx={{ mt: 1, mb: 1, p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "grey.600", mb: 0.5 }}>
                        Order on Behalf of : {product.order?.farmer_name || getAdvancePaymentEntries(product)[0]?.order?.farmer_name || product.advance_payment?.order?.farmer_name}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center',flexWrap: 'nowrap', mt: 2, mb: 1 }}>
                    <LocationOn sx={{ mr: 1, color: theme.palette.secondary.main }} />
                    <Typography variant="subtitle2" component="span" fontWeight={600} sx={{ mb: 0 }}>Delivery Address</Typography>
                  </Box>
                  <Tooltip
                    title={titleCaseAddress(
                      product.advance_payment?.order?.delivery_address
                      || getAdvancePaymentEntries(product)[0]?.order?.delivery_address
                      || (product.address
                        ? [product.address.village, product.address.tehsil, product.address.district, product.address.state, product.address.pincode].filter(Boolean).join(', ')
                        : 'Address not available')
                    )}
                    arrow
                    placement="top"
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5), padding: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                      {titleCaseAddress(
                        product.advance_payment?.order?.delivery_address
                        || getAdvancePaymentEntries(product)[0]?.order?.delivery_address
                        || (product.address
                          ? [product.address.village, product.address.tehsil, product.address.district, product.address.state, product.address.pincode].filter(Boolean).join(', ')
                          : 'Address not available')
                      )}
                    </Typography>
                  </Tooltip>

                  {product.order?.notes && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }} fontWeight={600}>Special Instructions:</Typography>
                      <Tooltip title={product.order.notes} arrow placement="top">
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 1.5, backgroundColor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                          {product.order.notes}
                        </Typography>
                      </Tooltip>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', mt: 2, mb: 1 }}>
                    <LocationOn sx={{ mr: 1, color: theme.palette.warning.main, flexShrink: 0 }} />
                    <Typography variant="subtitle2" component="span" fontWeight={600} sx={{ mb: 0 }}>Billing Address</Typography>
                  </Box>
                  <Tooltip
                    title={titleCaseAddress(
                      product.advance_payment?.order?.billing_address
                      || getAdvancePaymentEntries(product)[0]?.order?.billing_address
                      || product.advance_payment?.order?.delivery_address
                      || getAdvancePaymentEntries(product)[0]?.order?.delivery_address
                      || (product.address
                        ? [product.address.village, product.address.tehsil, product.address.district, product.address.state, product.address.pincode].filter(Boolean).join(', ')
                        : 'Billing address not available')
                    )}
                    arrow
                    placement="top"
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ backgroundColor: alpha(theme.palette.background.default, 0.5), padding: 1.5, borderRadius: 1, border: `1px solid ${alpha(theme.palette.divider, 0.1)}`, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
                      {titleCaseAddress(
                        product.advance_payment?.order?.billing_address
                        || getAdvancePaymentEntries(product)[0]?.order?.billing_address
                        || product.advance_payment?.order?.delivery_address
                        || getAdvancePaymentEntries(product)[0]?.order?.delivery_address
                        || (product.address
                          ? [product.address.village, product.address.tehsil, product.address.district, product.address.state, product.address.pincode].filter(Boolean).join(', ')
                          : 'Billing address not available')
                      )}
                    </Typography>
                  </Tooltip>
                </DetailCard>
              </Grid>
              <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                <DetailCard elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2.5, backgroundColor: 'background.paper', border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, borderRadius: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', mb: 2.5 }}>
                    <LocationOn sx={{ mr: 1.25, fontSize: '1.25rem', color: theme.palette.success.main, flexShrink: 0 }} />
                    <Typography variant="h6" component="span" fontWeight={600} sx={{ mb: 0 }}>Source Information</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1.25 }}>
                    <strong>Source:</strong> {product.source}
                  </Typography>
                  {(product.expected_delivery_date || product.order?.expected_delivery_date) && (
                    <Typography variant="body2" sx={{ mb: 1.25 }}>
                      <strong>Expected Delivery Date (Farmer):</strong>{' '}
                      <Typography component="span" variant="body2" color="primary" fontWeight={600}>
                        {new Date(product.expected_delivery_date || product.order?.expected_delivery_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </Typography>
                    </Typography>
                  )}
                  {(product.delivery_date || product.order?.delivery_date) && ['delivered', 'returned'].includes(
                    (product.status_category || product.status_name || product.status?.category || product.status?.name || product.status || '').toString().toLowerCase()
                  ) && (
                    <Typography variant="body2" sx={{ mb: 1.25 }}>
                      <strong>Actual Delivery Date:</strong>{' '}
                      <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                        {(() => {
                          const deliveryDate = product.delivery_date || product.order?.delivery_date;
                          try {
                            return new Date(deliveryDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            });
                          } catch (e) {
                            return product.order?.delivery_date_formatted || deliveryDate;
                          }
                        })()}
                      </Typography>
                    </Typography>
                  )}
                  {product.gst_no && (
                    <Typography variant="body2" sx={{ mb: 1.25 }}>
                      <strong>GST No:</strong> {product.gst_no}
                    </Typography>
                  )}
                  {['cancelled', 'returned'].includes(
                    (product.status_category || product.status_name || product.status?.category || product.status?.name || product.status || '').toString().toLowerCase()
                  ) && (
                    <>
                      {(product.status_category || product.status_name || '').toString().toLowerCase() === 'cancelled' && (
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Cancelled At:</strong>{' '}
                          {(() => {
                            const dt = product.order?.cancelled_at;
                            if (!dt) return 'N/A';
                            try {
                              return new Date(dt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                      )}
                      {(product.status_category || product.status_name || '').toString().toLowerCase() === 'returned' && (
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Returned At:</strong>{' '}
                          {(() => {
                            const dt = product.order?.returned_at;
                            if (!dt) return 'N/A';
                            try {
                              return new Date(dt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              });
                            } catch (e) {
                              return String(dt);
                            }
                          })()}
                        </Typography>
                      )}
                      {(product.reason || product.order?.reason) && (
                        <Typography variant="body2" sx={{ mb: 1.25 }}>
                          <strong>Reason:</strong> {product.reason || product.order?.reason}
                        </Typography>
                      )}
                    </>
                  )}
                  {/* <Typography variant="body2" sx={{ mb: 1.25 }}>
                    <strong>Order Date:</strong> {product.order_date_formatted || product.order?.order_date_formatted || new Date(product.created_at).toLocaleString()}
                  </Typography> */}
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center',flexWrap: 'nowrap' }}>
                    <Payment sx={{ mr: 1.25, fontSize: '1.25rem', color: theme.palette.info.main }} />
                    <Typography variant="subtitle1" component="span" fontWeight={600} sx={{ mb: 0 }}>Payment Information</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1.25 }}>
                    <strong>Payment Status:</strong>
                    <PaymentStatusChip
                      label={product.payment_status_name || (product.payment_status ? (product.payment_status.charAt(0).toUpperCase() + product.payment_status.slice(1)) : 'Pending')}
                      status={product.payment_status}
                      size="small"
                      sx={{ ml: 1.25 }}
                    />
                  </Typography>
                  {['cancelled', 'returned'].includes(
                    (product.status_category || product.status_name || product.status?.category || product.status?.name || product.status || '').toString().toLowerCase()
                  ) && (
                    <>
                      <Typography variant="body2" sx={{ mb: 1.25 }}>
                        <strong>Refunded Amount:</strong>{' '}
                        {product.order?.refunded_amount != null && product.order?.refunded_amount !== ''
                          ? `₹${product.order.refunded_amount}`
                          : 'N/A'}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1.25 }}>
                        <strong>Refunded At:</strong>{' '}
                        {(() => {
                          const dt = product.order?.refunded_at;
                          if (!dt) return 'N/A';
                          try {
                            return new Date(dt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            });
                          } catch (e) {
                            return String(dt);
                          }
                        })()}
                      </Typography>
                    </>
                  )}
                </DetailCard>
              </Grid>
              {product.delivery_challan && (
                <Grid item xs={12} md={4} sx={{ minWidth: 0 }}>
                  <DetailCard 
                    elevation={2} 
                    sx={{ 
                      backgroundColor: 'background.paper', 
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, 
                      borderRadius: 3,
                      p: 2.5
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', mb: 2 }}>
                      <ChallanIcon sx={{ mr: 1, color: theme.palette.primary.main, flexShrink: 0 }} />
                      <Typography variant="h6" component="span" fontWeight={600} sx={{ mb: 0 }}>Delivery Challan Details</Typography>
                    </Box>
                    
                    {product.delivery_challan.vehicle_number && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Vehicle Number:</strong> {product.delivery_challan.vehicle_number}
                      </Typography>
                    )}
                    {product.delivery_challan.vehicle_name && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Vehicle Name:</strong> {product.delivery_challan.vehicle_name}
                      </Typography>
                    )}
                    {product.delivery_challan.driver_name && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Driver Name:</strong> {product.delivery_challan.driver_name}
                      </Typography>
                    )}
                    {isOrderByTruck(product) && product.delivery_challan.vehicle_type && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Vehicle Type:</strong> {product.delivery_challan.vehicle_type}
                      </Typography>
                    )}
                    {product.delivery_challan.dispatch_person_name && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Dispatch Person:</strong> {product.delivery_challan.dispatch_person_name}
                      </Typography>
                    )}

                    {product.order?.order_by && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Order By:</strong> {product.order.order_by}
                      </Typography>
                    )}
                    {product.order?.order_by_entity && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Delivery Partner Name:</strong> {product.order.order_by_entity}
                      </Typography>
                    )}
                    {(product.dealer_name || product?.user?.dealer_name || product.order?.dealer_name) && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Dealer:</strong>{' '}
                        {product.dealer_name || product?.user?.dealer_name || product.order?.dealer_name}
                      </Typography>
                    )}

                    {isOrderByTruck(product) && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Kilometer Information
                        </Typography>
                        {!isTruthyFlag(product.delivery_challan.meter_not_working) && (
                          <>
                            {product.delivery_challan.start_km !== null && product.delivery_challan.start_km !== undefined && (
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Start KM:</strong> {product.delivery_challan.start_km}
                              </Typography>
                            )}
                            {product.delivery_challan.end_km !== null && product.delivery_challan.end_km !== undefined && (
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>End KM:</strong> {product.delivery_challan.end_km}
                              </Typography>
                            )}
                            {product.delivery_challan.total_km !== null && product.delivery_challan.total_km !== undefined && (
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Total KM:</strong> {product.delivery_challan.total_km}
                              </Typography>
                            )}
                          </>
                        )}
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Meter not  Working:</strong>{' '}
                          {isTruthyFlag(product.delivery_challan.meter_not_working) ? 'Yes' : 'No'}
                        </Typography>
                        {product.delivery_challan.google_maps_km !== null && product.delivery_challan.google_maps_km !== undefined && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Google Maps KM:</strong> {product.delivery_challan.google_maps_km}
                          </Typography>
                        )}
                        {product.delivery_challan.final_km !== null && product.delivery_challan.final_km !== undefined && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Final KM:</strong> {product.delivery_challan.final_km}
                          </Typography>
                        )}
                        {product.delivery_challan.km_excess_reason && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>KM Excess Reason:</strong> {product.delivery_challan.km_excess_reason}
                          </Typography>
                        )}
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Diesel Information
                        </Typography>
                        {product.delivery_challan.diesel_receipt_number && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Diesel Receipt No:</strong> {product.delivery_challan.diesel_receipt_number}
                          </Typography>
                        )}
                        {product.delivery_challan.diesel_amount !== null && product.delivery_challan.diesel_amount !== undefined && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Diesel Amount:</strong> {product.delivery_challan.diesel_amount}
                          </Typography>
                        )}
                        {product.delivery_challan.diesel_receipt_photo && (
                          <Typography
                            variant="body2"
                            component="div"
                            sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                          >
                            <strong>Receipt Upload:</strong>
                            <Button
                              size="small"
                              variant="outlined"
                              {...(isDieselReceiptPdf
                                ? {
                                    href: dieselReceiptPhotoUrl,
                                    target: '_blank',
                                    rel: 'noopener noreferrer',
                                  }
                                : {
                                    onClick: () => setReceiptPreviewUrl(dieselReceiptPhotoUrl),
                                  })}
                              sx={{
                                textTransform: 'none',
                                border: '1px solid #1e293b',
                                borderRadius: '8px',
                                px: '18px',
                                py: '6px',
                                fontSize: '13px',
                                fontWeight: 500,
                                color: '#1e293b',
                                bgcolor: '#fff',
                                '&:hover': {
                                  border: '1px solid #1e293b',
                                  bgcolor: '#f1f5f9',
                                },
                              }}
                            >
                              View
                            </Button>
                          </Typography>
                        )}
                      </>
                    )}

                    {(
                      (product.delivery_challan.crates_dispatched !== null && product.delivery_challan.crates_dispatched !== undefined) ||
                      (product.delivery_challan.crates_returned !== null && product.delivery_challan.crates_returned !== undefined) ||
                      product.delivery_challan.crates_short_reason ||
                      (product.delivery_challan.plants_returned !== null && product.delivery_challan.plants_returned !== undefined) ||
                      product.delivery_challan.plants_returned_reason
                    ) && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Crates / Plants
                        </Typography>
                        {product.delivery_challan.crates_dispatched !== null && product.delivery_challan.crates_dispatched !== undefined && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Crates Dispatched:</strong> {product.delivery_challan.crates_dispatched}
                          </Typography>
                        )}
                        {product.delivery_challan.crates_returned !== null && product.delivery_challan.crates_returned !== undefined && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Crates Returned:</strong> {product.delivery_challan.crates_returned}
                          </Typography>
                        )}
                        {product.delivery_challan.crates_short_reason && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Crates Short Reason:</strong> {product.delivery_challan.crates_short_reason}
                          </Typography>
                        )}
                        {product.delivery_challan.plants_returned !== null && product.delivery_challan.plants_returned !== undefined && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Plants Returned:</strong> {product.delivery_challan.plants_returned}
                          </Typography>
                        )}
                        {product.delivery_challan.plants_returned_reason && (
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Plants Returned Reason:</strong> {product.delivery_challan.plants_returned_reason}
                          </Typography>
                        )}
                      </>
                    )}
                  </DetailCard>
                </Grid>
              )}
              <Grid item xs={12} sx={{ minWidth: 0 }}>
                  
                  {/* Expected Delivery Date (from farmer) */}
                  {product.expected_delivery_date && (
                    <Box sx={{ mt: 2, mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Expected Delivery Date (Farmer):</strong>{' '}
                        <Typography component="span" variant="body2" color="primary" fontWeight={600}>
                          {new Date(product.expected_delivery_date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </Typography>
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Actual Delivery Date (set by admin) */}
                  {product.delivery_date && ['delivered', 'returned'].includes(
                    (product.status_category || product.status_name || product.status?.category || product.status?.name || product.status || '').toString().toLowerCase()
                  ) && (
                    <Box sx={{ mt: 2, mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Actual Delivery Date:</strong>{' '}
                        <Typography component="span" variant="body2" color="success.main" fontWeight={600}>
                          {(() => {
                            try {
                              const date = new Date(product.delivery_date);
                              return date.toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              });
                            } catch (e) {
                              return product.delivery_date_formatted || product.delivery_date;
                            }
                          })()}
                        </Typography>
                      </Typography>
                    </Box>
                  )}

                  {/* Status reason returned by API (order.reason) **/}
                  {product.reason && (
                    <Box sx={{ mt: 2, mb: 1 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>
                          {(product.status_name || '').toLowerCase() === 'returned' ? 'Return Reason:' : 'Reason:'}
                        </strong>{' '}
                        <Typography component="span" variant="body2" color="text.secondary">
                          {product.reason}
                        </Typography>
                      </Typography>
                    </Box>
                  )}

              </Grid>
              {getAdvancePaymentEntries(product).length > 0 && (
                <Grid item xs={12}>
                  <DetailCard
                    elevation={2}
                    sx={{
                      backgroundColor: 'background.paper',
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      borderRadius: 3,
                      p: 2.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Payment sx={{ mr: 1, color: theme.palette.warning.main }} />
                      <Typography variant="h6" component="span" fontWeight={600} sx={{ mb: 0 }}>
                        Payment Details
                      </Typography>
                    </Box>
                    {getAdvancePaymentEntries(product).map((entry, index) => {
                      const fields = entry.fields || {}
                      const formatAdvanceDate = (value) => {
                        if (!value) return 'N/A'
                        try {
                          return new Date(value).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })
                        } catch {
                          return value
                        }
                      }
                      return (
                        <Box
                          key={entry.id ?? `advance-${index}`}
                          sx={{
                            p: 2,
                            mb: index < getAdvancePaymentEntries(product).length - 1 ? 2 : 0,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.warning.main, 0.04),
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {entry.is_full_payment ? 'Full Payment' : 'Advance Payment'}
                            </Typography>
                            {entry.method?.name ? (
                              <Chip
                                size="small"
                                label={entry.method.name}
                                color="warning"
                                variant="outlined"
                              />
                            ) : null}
                          </Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{entry.is_full_payment ? 'Total Amount:' : 'Advance Amount:'}</strong>{' '}
                            {formatBulkDetailCurrency(entry.advance_amount)}
                          </Typography>
                          {!entry.is_full_payment && (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Pending Amount:</strong> {formatBulkDetailCurrency(entry.remaining_amount)}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Note:</strong>{' '}
                            {getEntryNote(entry, product) || 'N/A'}
                          </Typography>
                          {/* {entry.transaction_id ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Transaction ID:</strong> {entry.transaction_id}
                            </Typography>
                          ) : null} */}
                          {fields.cheque_number ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Cheque No:</strong> {fields.cheque_number}
                            </Typography>
                          ) : null}
                          {fields.cheque_date ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Cheque Date:</strong> {formatAdvanceDate(fields.cheque_date)}
                            </Typography>
                          ) : null}
                          {fields.cheque_amount ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Cheque Amount:</strong> {formatBulkDetailCurrency(fields.cheque_amount)}
                            </Typography>
                          ) : null}
                          {fields.cash_receipt_number ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Cash Receipt No:</strong> {fields.cash_receipt_number}
                            </Typography>
                          ) : null}
                          {fields.receiver_name ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Receiver Name:</strong> {fields.receiver_name}
                            </Typography>
                          ) : null}
                          {fields.transaction_id ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Transaction ID:</strong> {fields.transaction_id}
                            </Typography>
                          ) : null}
                          {fields.transaction_reference_no ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Reference No:</strong> {fields.transaction_reference_no}
                            </Typography>
                          ) : null}
                          {fields.sender_bank_name ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Sender Bank:</strong> {fields.sender_bank_name}
                            </Typography>
                          ) : null}
                          {fields.bank_name_branch ? (
                            <Typography variant="body2" sx={{ mb: 1 }}>
                              <strong>Bank Branch:</strong> {fields.bank_name_branch}
                            </Typography>
                          ) : null}
                          {(fields.payment_confirmation_file || fields.cheque_image) && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                              {fields.payment_confirmation_file ? (
                                <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <strong>Payment Confirmation File:</strong>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => window.open(fields.payment_confirmation_file, '_blank', 'noopener,noreferrer')}
                                  >
                                    View Confirmation
                                  </Button>
                                </Typography>
                              ) : null}
                              {fields.cheque_image ? (
                                <Typography variant="body2" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <strong>Payment Confirmation File:</strong>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => window.open(fields.cheque_image, '_blank', 'noopener,noreferrer')}
                                  >
                                    View Cheque
                                  </Button>
                                </Typography>
                              ) : null}
                            </Box>
                          )}
                        </Box>
                      )
                    })}
                  </DetailCard>
                </Grid>
              )}

              {/* Delivery Challan Details - Show whenever delivery challan exists */}
              {product.order && (
                <Grid item xs={12}>
                  <DetailCard
                    elevation={2}
                    sx={{
                      backgroundColor: 'background.paper',
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      borderRadius: 3,
                      p: 2.5
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <ShoppingCart sx={{ mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="h6" component="span" fontWeight={600} sx={{ mb: 0 }}>
                        Order Items ({product.order.total_items || 1})
                      </Typography>
                    </Box>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell align="center" sx={{ fontWeight: 600, width: '80px' }}>Sr. No.</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>Product</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>Category</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>SKU</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>GST Rate</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600 }}>Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow sx={{ '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}>
                            <TableCell align="center">1</TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {product.product_name}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                                title={product.category_name}
                              >
                                {product.category_name || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" color="text.secondary">
                                {product.sku || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{formatBulkDetailCurrency(product.discounted_price)}</TableCell>
                            <TableCell align="center">{Number(product.quantity || 0)}</TableCell>
                            <TableCell align="center">
                              {product.order.gst_amount > 0 && product.order.subtotal > 0
                                ? `${((product.order.gst_amount / product.order.subtotal) * 100).toFixed(2)}%`
                                : '0%'}
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                {formatBulkDetailCurrency(Number(product.discounted_price || 0) * Number(product.quantity || 0))}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>Total Items:</strong> {product.order.total_items || 1}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Total Quantity:</strong> {Number(product.order.total_quantity || product.quantity || 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Subtotal:</strong> {formatBulkDetailCurrency(product.order.subtotal)}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>GST Amount:</strong> {formatBulkDetailCurrency(product.order.gst_amount)}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Delivery Charge:</strong> {formatBulkDetailCurrency(product.order.delivery_charge)}
                          </Typography>
                          {Number(product.order.discount_amount || 0) > 0 && (
                            <Typography variant="body2" color="success.main" sx={{ mb: 0.5 }}>
                              <strong>Discount:</strong> -{formatBulkDetailCurrency(product.order.discount_amount)}
                            </Typography>
                          )}
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="h6" color="primary.main" fontWeight={700}>
                            Total: {formatBulkDetailCurrency(product.order.total_amount)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </DetailCard>
                </Grid>
              )}

            </Grid>

            <Dialog
              open={Boolean(receiptPreviewUrl)}
              onClose={() => setReceiptPreviewUrl(null)}
              maxWidth={false}
              PaperProps={{
                sx: {
                  borderRadius: '12px',
                  p: 2,
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                },
              }}
              BackdropProps={{
                sx: {
                  backgroundColor: 'rgba(0,0,0,0.6)',
                },
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <IconButton
                  onClick={() => setReceiptPreviewUrl(null)}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    zIndex: 1,
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Box
                  component="img"
                  src={receiptPreviewUrl || ''}
                  alt="Diesel Receipt"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    borderRadius: '10px',
                  }}
                />
              </Box>
            </Dialog>
          </Box>
  )
}

const BulkProductList = ({ 
  data: bulkProducts = [], 
  searchQuery = '', 
  statusFilter = 'all', 
  isLoading = false, 
  onRefresh,
  selectedProducts = [],
  onProductSelect,
  onSelectAll,
  isAllSelected = false,
  onExport,
  onZipDownload,
  currentPage = 1,
  pageSize = 10,
  shouldEnableButton,
  listFilters = null,
}) => {
  const [statusUpdateModal, setStatusUpdateModal] = useState({ 
    open: false, 
    order: null,
    cancellationReason: '',
    returnReason: '',
     entityName: '',
    orderBy: '',
    driverName: '',
    vehicleNo: '',
    vehicleType: '',
    shippedDate: new Date().toISOString().slice(0,10),
    generateInvoice: false,
    isCreatingChallan: false,
    challanError: '',
    emails: [],
    emailInput: '',
    dispatchPersonName: '',
    customDispatchPerson: '',
    useCustomDispatchPerson: false,
    start_km: '',   // new
    end_km: '',
    meter_not_working: false,
    google_maps_km: '',
    diesel_receipt_number: '',
    diesel_amount: '',
    crates_dispatched: '0',
    diesel_receipt_upload: null,
    diesel_receipt_upload_error: '',
    ...DEFAULT_DELIVERED_LOGISTICS,
  })
  const [paymentUpdateModal, setPaymentUpdateModal] = useState(() => ({ ...DEFAULT_PAYMENT_UPDATE_MODAL }))
  const [invoiceModal, setInvoiceModal] = useState({ open: false, invoice: null, product: null })
  const [deliveryChallanModal, setDeliveryChallanModal] = useState({ open: false, challan: null, product: null })
  const [bulkChallanModal, setBulkChallanModal] = useState({
    open: false,
    orderBy: '',
    entityName: '',
    driverName: '',
    vehicleNo: '',
    vehicleType: '',
    deliveryDate: new Date().toISOString().slice(0,10),
    generateInvoice: false,
    isCreating: false,
    error: '',
    emails: [],
    emailInput: '',
    selectedStatus: '',
    selectedStatusCategory: '',
    dispatchPersonName: '',
    customDispatchPerson: '',
    useCustomDispatchPerson: false,
     start_km: '',   // new
    end_km: '',
    meter_not_working: false,
    google_maps_km: '',
    diesel_receipt_number: '',
    diesel_amount: '',
    crates_dispatched: '0',
    diesel_receipt_upload: null,
    diesel_receipt_upload_error: '',
    ...DEFAULT_DELIVERED_LOGISTICS,
    cancellationReason: '',
    returnReason: '',
    bulkPaymentStatus: ''  // For bulk payment status update
  })
  const DEFAULT_BULK_PAYMENT_STATUS_MODAL = {
    open: false,
    selectedPaymentStatus: '',
    initialPaymentStatus: '',
    orderPaymentDetails: null,
    error: '',
    isUpdating: false,
    step: 'status',
    paymentOption: '',
    advanceAmount: '',
    paymentMethod: '',
    paymentMethodConfig: null,
    paymentFields: {},
    paymentFiles: {},
    paymentError: '',
    summaryOrder: null,
    installmentAmount: '',
    creditNote: '',
    refundAmount: '',
  }

  const [bulkPaymentStatusModal, setBulkPaymentStatusModal] = useState({
    ...DEFAULT_BULK_PAYMENT_STATUS_MODAL,
  })
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [updatingPayment, setUpdatingPayment] = useState(false)
  const [isUpdatingBulkPayment, setIsUpdatingBulkPayment] = useState(false)
  const [downloadingInvoice, setDownloadingInvoice] = useState(false)
  const [downloadingChallan, setDownloadingChallan] = useState(false)
  const theme = useTheme()
  const navigate = useNavigate()

  useEffect(() => {
    // Status tab + page restore is handled in bulkProduct.jsx (sessionStorage).
    // Only clear a leftover key once the active tab already matches.
    const savedTab = sessionStorage.getItem(BULK_ORDER_STATUS_TAB_STORAGE_KEY);
    if (savedTab && savedTab === statusFilter) {
      sessionStorage.removeItem(BULK_ORDER_STATUS_TAB_STORAGE_KEY);
    }
  }, [statusFilter, isLoading]);
  const toast = useToast()

  const [statusOptions, setStatusOptions] = useState([])
  const [paymentStatusChoices, setPaymentStatusChoices] = useState([])
  const [loadingPaymentStatusChoices, setLoadingPaymentStatusChoices] = useState(false)
  const [advancePaymentMethods, setAdvancePaymentMethods] = useState([])
  const [loadingAdvancePaymentMethods, setLoadingAdvancePaymentMethods] = useState(false)
  const paymentMethodsById = React.useMemo(
    () => buildPaymentMethodsById(advancePaymentMethods),
    [advancePaymentMethods]
  )
  const [dispatchPersons, setDispatchPersons] = useState([])
  const [vehicleTypes, setVehicleTypes] = useState([])
  const [orderByChoices, setOrderByChoices] = useState([])
  const [bulkEntityOptions, setBulkEntityOptions] = useState([])
  const [loadingBulkEntities, setLoadingBulkEntities] = useState(false)
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null)
  const isActionsMenuOpen = Boolean(actionsAnchorEl)
  const openActionsMenu = (e) => setActionsAnchorEl(e.currentTarget)
  const closeActionsMenu = () => setActionsAnchorEl(null)
  const [emailDialog, setEmailDialog] = useState({
    open: false,
    emailInput: '',
    emails: [],
    sendInvoice: true,
    sendDelivery: true,
    isSending: false,
    error: '',
    success: ''
  })
  const [singleEmailDialog, setSingleEmailDialog] = useState({
    open: false,
    order: null,
    emailInput: '',
    emails: [],
    sendInvoice: true,
    sendDelivery: true,
    isSending: false,
    error: '',
    success: ''
  })

  const getCategoryIcon = (category) => {
    const key = (typeof category === 'string' ? category : (category?.name ?? '')).toLowerCase()
    switch (key) {
      case 'pending': return <Schedule />
      case 'confirmed': return <CheckCircle />
      case 'processing': return <Schedule />
      case 'shipped': return <LocalShippingOutlined />
      case 'delivered': return <CheckCircle />
      case 'cancelled': return <Cancel />
      case 'returned': return <Refresh />
      default: return null
    }
  }

  // Helper functions for sequential status validation
  // Build status order dynamically from API response using sequence field
  const getStatusOrder = () => {
    if (!statusOptions || statusOptions.length === 0) {
      return {};
    }
    
    // Build order map using category as key and sequence as value
    const orderMap = {};
    statusOptions.forEach(option => {
      const category = (option.category || '').toLowerCase();
      const sequence = option.sequence || 0;
      if (category) {
        orderMap[category] = sequence;
      }
    });
    
    return orderMap;
  };

  // Get the next sequential status based on current status
  const getNextSequentialStatus = (currentStatusName, currentStatusCategory) => {
    if (!statusOptions || statusOptions.length === 0) {
      return null;
    }

    const statusOrder = getStatusOrder();
    let currentStatusLower = (currentStatusCategory || '').toLowerCase();
    let currentOrder = statusOrder[currentStatusLower] || 0;

    // If category not found, try to find it from statusOptions by name
    if (currentOrder === 0 && currentStatusName) {
      const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
      if (currentStatusOption) {
        currentStatusLower = (currentStatusOption.category || '').toLowerCase();
        currentOrder = statusOrder[currentStatusLower] || 0;
      }
    }

    // Delivered orders can only move to Returned.
    if (currentStatusLower === 'delivered' || (currentStatusName || '').toLowerCase() === 'delivered') {
      return statusOptions.find(option =>
        (option.category || '').toLowerCase() === 'returned' ||
        (option.name || '').toLowerCase() === 'returned'
      ) || null;
    }

    // If current order is 0 or not found, return null (can't determine next)
    if (currentOrder === 0) {
      return null;
    }

    // Find the next sequential status (currentOrder + 1)
    const nextOrder = currentOrder + 1;
    const nextStatus = statusOptions.find(option => {
      const optionCategory = (option.category || '').toLowerCase();
      return statusOrder[optionCategory] === nextOrder;
    });

    return nextStatus || null;
  };

  const getStatusesToDisable = (currentStatusCategory, currentStatusName) => {
    if (!statusOptions || statusOptions.length === 0) {
      return [];
    }
    
    const statusOrder = getStatusOrder();
    let currentStatusLower = (currentStatusCategory || '').toLowerCase();
    let currentOrder = statusOrder[currentStatusLower] || 0;
    
    // If category not found, try to find it from statusOptions by name
    if (currentOrder === 0 && currentStatusName) {
      const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
      if (currentStatusOption) {
        currentStatusLower = (currentStatusOption.category || '').toLowerCase();
        currentOrder = statusOrder[currentStatusLower] || 0;
      }
    }
    
    // Find Dispatch, Processing, and Delivered status orders
    const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
    const processingOrder = statusOrder['processing'] || 0;
    const deliveredOrder = statusOrder['delivered'] || 999;
    
    // Check if current status is Processing (by name or category)
    const isProcessingStatus = currentStatusName?.toLowerCase() === 'processing' || 
                                currentStatusLower === 'processing' ||
                                currentOrder === processingOrder;
    
    // Check if current status is Delivered
    const isDeliveredStatus = currentStatusName?.toLowerCase() === 'delivered' || 
                              currentStatusLower === 'delivered' ||
                              currentOrder === deliveredOrder;
    
    // Check if current status is Cancelled
    const isCancelledStatus = currentStatusName?.toLowerCase() === 'cancelled' || 
                              currentStatusLower === 'cancelled';
    
    // If still not found, disable all
    if (currentOrder === 0) {
      return statusOptions
        .filter(option => {
          const optionCategory = (option.category || '').toLowerCase();
          const optionName = (option.name || '').toLowerCase();
          // Always disable Dispatch if not at Processing
          if (optionCategory === 'shipped' || optionCategory === 'dispatch' || optionName === 'dispatch') {
            return true; // Disable Dispatch
          }
          return true; // Disable all others
        })
        .map(option => option.category?.toLowerCase() || '');
    }
    
    // Special case: If status is Cancelled, no further status changes allowed
    if (isCancelledStatus) {
      return statusOptions.map(option => option.category?.toLowerCase() || '');
    }
    
    // Special case: If status is Delivered, only allow Returned
    if (isDeliveredStatus) {
      return statusOptions.filter(option => {
        const optionCategory = (option.category || '').toLowerCase();
        const optionName = (option.name || '').toLowerCase();
        const isReturned = optionCategory === 'returned' || optionName === 'returned';
        // Only allow Returned, disable everything else
        return !isReturned;
      }).map(option => option.category?.toLowerCase() || '');
    }
    
    // Disable statuses that:
    // 1. Come before the current status (can't go backwards)
    // 2. Come more than 1 step after current status (must go sequentially)
    // 3. Dispatch is only available when current status is Processing
    // 4. Returned is only available from Delivered status
    return statusOptions.filter(option => {
      const optionCategory = (option.category || '').toLowerCase();
      const optionName = (option.name || '').toLowerCase();
      const isReturned = optionCategory === 'returned' || optionName === 'returned';
      const isCancelled = optionCategory === 'cancelled' || optionName === 'cancelled';
      const isDispatch = optionCategory === 'shipped' || optionCategory === 'dispatch' || optionName === 'dispatch';
      
      // Dispatch is only available when current status is Processing
      if (isDispatch) {
        return !isProcessingStatus; // Disable Dispatch if not at Processing
      }
      
      // Returned is only available from Delivered status
      if (isReturned) {
        return !isDeliveredStatus; // Disable Returned if not at Delivered
      }
      
      const statusOrderValue = statusOrder[optionCategory] || 0;
      const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
      
      // Disable if it's before current status (can't go backwards)
      // This includes previous statuses like Placed, Confirmed when current is Processing
      if (statusOrderValue < currentOrder) {
        return true;
      }
      
      // Disable current status itself (must move forward, not stay the same)
      if (statusOrderValue === currentOrder) {
        return true;
      }
      
      // Special case: Cancelled can be selected from any status up to and including Dispatch
      // (Placed, Confirmed, Processing, Dispatch - all can be cancelled)
      if (isCancelled) {
        // Allow Cancelled if current status is up to Dispatch (including Dispatch)
        if (currentOrder <= dispatchOrder) {
          return false; // Allow Cancelled
        } else {
          return true; // Disable Cancelled if already past Dispatch (e.g., Delivered)
        }
      }
      
      // Disable if it's more than 1 step ahead (enforce sequential progression)
      if (statusOrderValue > currentOrder + 1) {
        return true;
      }
      
      // Only allow next sequential status (currentOrder + 1)
      return false;
    }).map(option => option.category?.toLowerCase() || '');
  };

  useEffect(() => {
    console.log("statusUpdateModal.order", statusUpdateModal)
  }, [statusUpdateModal])
  const getAvailableCounts = () => {
    const selected = filteredData.filter(p => selectedProducts.includes(p.id))
    const tab = (statusFilter || '').toLowerCase()
    const useSelectedOrdersAsCounts = ['dispatch', 'shipped', 'delivered', 'returned', 'cancelled'].includes(tab)
    const invoices = useSelectedOrdersAsCounts
      ? selectedProducts.length
      : selected.filter(p => p.invoice && p.invoice.path).length
    const challans = useSelectedOrdersAsCounts
      ? selectedProducts.length
      : selected.filter(p => p.delivery_challan && p.delivery_challan.path).length
    return { invoices, challans }
  }

  const toStoredBulkEmailDate = (date) => {
    if (date == null || date === '') return ''
    if (typeof date === 'string') {
      const trimmed = date.trim()
      if (!trimmed) return ''
      return trimmed.includes('T') ? trimmed.split('T')[0] : trimmed
    }
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    }
    return ''
  }

  const resolveSelectedOrderIdsForEmail = async () => {
    const selectedKeySet = new Set(selectedProducts.map((id) => String(id)))
    const fromCurrentPage = filteredData
      .filter((order) => {
        const idKey = order?.id != null ? String(order.id) : null
        const orderKey = order?.order_id != null ? String(order.order_id) : null
        return (idKey && selectedKeySet.has(idKey)) || (orderKey && selectedKeySet.has(orderKey))
      })
      .map((order) => order.order_id)
      .filter(Boolean)

    if (fromCurrentPage.length >= selectedProducts.length) {
      return [...new Set(fromCurrentPage)]
    }

    const userId = localStorage.getItem('user_id')
    const statusParam =
      !statusFilter || statusFilter === 'all'
        ? ''
        : `&status=${encodeURIComponent(statusFilter)}`
    let url = `${API_ENDPOINTS.GET_bulk_upload_product}?user_id=${userId}${statusParam}`
    const fromDate = toStoredBulkEmailDate(listFilters?.startDate)
    const toDate = toStoredBulkEmailDate(listFilters?.endDate)
    if (fromDate) url += `&from_date=${fromDate}`
    if (toDate) url += `&to_date=${toDate}`
    if (searchQuery && String(searchQuery).trim()) {
      url += `&search=${encodeURIComponent(String(searchQuery).trim())}`
    }
    const companyToUse = listFilters?.selectedCompany
    if (companyToUse && companyToUse !== 'all') {
      url += `&company=${companyToUse}`
    }
    const totalItems = selectedProducts.length || filteredData.length || 1
    url += `&page=1&page_size=${Math.max(totalItems, 1)}`

    const response = await apiService.get(url)
    const allRows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.results)
        ? response.results
        : []

    return [
      ...new Set(
        allRows
          .filter((product) => {
            const idKey = product?.id != null ? String(product.id) : null
            const orderKey = product?.order_id != null ? String(product.order_id) : null
            return (idKey && selectedKeySet.has(idKey)) || (orderKey && selectedKeySet.has(orderKey))
          })
          .map((product) => product.order_id)
          .filter(Boolean)
      ),
    ]
  }

  // Check if any selected product has status that should disable documents/email
  const shouldDisableDocumentsAndEmail = () => {
    const selected = filteredData.filter(p => selectedProducts.includes(p.id))
    const restrictedStatuses = ['Placed', 'Confirmed', 'Processing']
    return selected.some(p => {
      const statusName = p.status?.name || p.status_name || ''
      return restrictedStatuses.includes(statusName)
    })
  }

  const handleOpenEmailDialog = () => {
    setEmailDialog({
      open: true,
      emailInput: '',
      emails: [],
      sendInvoice: true,
      sendDelivery: true,
      isSending: false,
      error: '',
      success: ''
    })
    closeActionsMenu()
  }

  const handleCloseEmailDialog = () => {
    setEmailDialog({
      open: false,
      emailInput: '',
      emails: [],
      sendInvoice: true,
      sendDelivery: true,
      isSending: false,
      error: '',
      success: ''
    })
  }

  const handleEmailDialogChange = (field, value) => {
    setEmailDialog(prev => ({ ...prev, [field]: value, error: '', success: '' }))
  }

  const isValidBulkEmailInput = (value) => {
    const input = String(value || '').trim()
    if (!input) return false
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)
  }

  const addEmailToBulkDialog = () => {
    const input = (emailDialog.emailInput || '').trim()
    if (!input) return
    if (!isValidBulkEmailInput(input)) {
      setEmailDialog(prev => ({ ...prev, error: 'Enter a valid email address' }))
      return
    }
    if (emailDialog.emails.includes(input)) {
      setEmailDialog(prev => ({ ...prev, error: 'Email already added' }))
      return
    }
    setEmailDialog(prev => ({
      ...prev,
      emails: [...prev.emails, input],
      emailInput: '',
      error: ''
    }))
  }

  const removeEmailFromBulkDialog = (email) => {
    setEmailDialog(prev => ({
      ...prev,
      emails: prev.emails.filter((item) => item !== email),
      error: ''
    }))
  }

  const handleEmailSendSubmit = async () => {
    const { emails, emailInput, sendInvoice, sendDelivery } = emailDialog
    const userId = localStorage.getItem('user_id')

    const emailsToSend = [...emails]
    const typedEmail = String(emailInput || '').trim()
    if (typedEmail) {
      if (!isValidBulkEmailInput(typedEmail)) {
        setEmailDialog(prev => ({ ...prev, error: 'Enter a valid email address' }))
        return
      }
      if (!emailsToSend.includes(typedEmail)) {
        emailsToSend.push(typedEmail)
      }
    }
    if (emailsToSend.length === 0) {
      setEmailDialog(prev => ({ ...prev, error: 'Please add at least one email address' }))
      return
    }
    if (!sendInvoice && !sendDelivery) {
      setEmailDialog(prev => ({ ...prev, error: 'Please select at least one document type to send' }))
      return
    }
    const { invoices, challans } = getAvailableCounts()
    if ((sendInvoice && invoices === 0) && (sendDelivery && challans === 0)) {
      setEmailDialog(prev => ({ ...prev, error: 'No available documents to send for selected orders' }))
      return
    }

    setEmailDialog(prev => ({ ...prev, isSending: true, error: '', success: '' }))
    try {
      const selectedOrderIds = await resolveSelectedOrderIdsForEmail()
      if (!selectedOrderIds.length) {
        throw new Error('Selected orders do not have valid order IDs')
      }

      const payload = {
        emails: emailsToSend.join(','),
        order_id: selectedOrderIds.join(','),
        user_id: userId,
        invoice: !!sendInvoice,
        delivery: !!sendDelivery
      }

      const res = await apiService.post(
        API_ENDPOINTS.POST_send_challan_on_email,
        JSON.stringify(payload),
        'application/json'
      )

      if (res?.success) {
        setEmailDialog(prev => ({ ...prev, success: 'Email sent successfully', isSending: false }))
        setTimeout(() => handleCloseEmailDialog(), 1200)
      } else {
        throw new Error(res?.message || 'Failed to send email')
      }
    } catch (err) {
      setEmailDialog(prev => ({ ...prev, error: err?.message || 'Failed to send email', isSending: false }))
    }
  }

  // Individual email dialog handlers
  const openSingleOrderEmail = (product) => {
    const hasInvoice = !!(product?.invoice && product.invoice.path)
    const hasChallan = !!(product?.delivery_challan && product.delivery_challan.path)
    const isDispatchTab = (statusFilter || '').toLowerCase() === 'dispatch'
    setSingleEmailDialog({
      open: true,
      order: product,
      emailInput: '',
      emails: [],
      sendInvoice: isDispatchTab ? false : hasInvoice,
      sendDelivery: hasChallan,
      isSending: false,
      error: '',
      success: ''
    })
  }

  const closeSingleOrderEmail = () => {
    setSingleEmailDialog({
      open: false,
      order: null,
      emailInput: '',
      emails: [],
      sendInvoice: true,
      sendDelivery: true,
      isSending: false,
      error: '',
      success: ''
    })
  }

  const addEmailToSingle = () => {
    const input = (singleEmailDialog.emailInput || '').trim()
    if (!input) return
    // basic email check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(input)) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Enter a valid email address' }))
      return
    }
    if (singleEmailDialog.emails.includes(input)) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Email already added' }))
      return
    }
    setSingleEmailDialog(prev => ({ ...prev, emails: [...prev.emails, input], emailInput: '', error: '' }))
  }

  const removeEmailFromSingle = (email) => {
    setSingleEmailDialog(prev => ({ ...prev, emails: prev.emails.filter(e => e !== email) }))
  }

  const isValidSingleEmailInput = (value) => {
    const input = String(value || '').trim()
    if (!input) return false
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)
  }

  const submitSingleOrderEmail = async () => {
    const { order, emails, emailInput, sendInvoice, sendDelivery } = singleEmailDialog
    if (!order) return
    const isDispatchTab = (statusFilter || '').toLowerCase() === 'dispatch'
    const effectiveSendInvoice = isDispatchTab ? false : sendInvoice
    const emailsToSend = [...emails]
    const typedEmail = String(emailInput || '').trim()
    if (typedEmail) {
      if (!isValidSingleEmailInput(typedEmail)) {
        setSingleEmailDialog(prev => ({ ...prev, error: 'Enter a valid email address' }))
        return
      }
      if (!emailsToSend.includes(typedEmail)) {
        emailsToSend.push(typedEmail)
      }
    }
    if (emailsToSend.length === 0) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Please add at least one email' }))
      return
    }
    if (!effectiveSendInvoice && !sendDelivery) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Please select at least one document type' }))
      return
    }
    // Check availability for requested types
    const hasInvoice = !!(order?.invoice && order.invoice.path)
    const hasChallan = !!(order?.delivery_challan && order.delivery_challan.path)
    if ((effectiveSendInvoice && !hasInvoice) && (sendDelivery && !hasChallan)) {
      setSingleEmailDialog(prev => ({ ...prev, error: 'Selected documents are not available for this order' }))
      return
    }

    setSingleEmailDialog(prev => ({ ...prev, isSending: true, error: '', success: '' }))
    try {
      const userId = localStorage.getItem('user_id')
      const payload = {
        emails: emailsToSend.join(','),
        order_id: order.order_id,
        user_id: userId,
        invoice: !!effectiveSendInvoice,
        delivery: !!sendDelivery
      }
      const res = await apiService.post(
        API_ENDPOINTS.POST_send_challan_on_email,
        JSON.stringify(payload),
        'application/json'
      )
      if (res?.success) {
        setSingleEmailDialog(prev => ({ ...prev, success: 'Email sent successfully', isSending: false }))
        setTimeout(() => closeSingleOrderEmail(), 1200)
      } else {
        throw new Error(res?.message || 'Failed to send email')
      }
    } catch (err) {
      setSingleEmailDialog(prev => ({ ...prev, error: err?.message || 'Failed to send email', isSending: false }))
    }
  }

  useEffect(() => {
    const fetchStatusOptions = async () => {
      try {
        const res = await apiService.get(API_ENDPOINTS.GET_order_status_choices)
        const list = Array.isArray(res?.data) ? res.data : []
        const options = list
          .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
          .map(s => ({
            id: s.id,
            value: (s.name || '').toLowerCase(),
            name: s.name || '',
            label: s.name || '',
            category: s.category || s.name || '',
            sequence: s.sequence || 0
          }))
        setStatusOptions(options)
      } catch (e) {
        console.error('Error fetching status options:', e)
      }
    }
    fetchStatusOptions()
  }, [])

  useEffect(() => {
    const fetchDispatchPersons = async () => {
      try {
        const res = await apiService.get('e-commerce/admin-order/get-dispatch-persons/')
        const list = Array.isArray(res?.data) ? res.data : []
        const mappedList = list
          .map((person, index) => ({
            id: person.value || person.id || index,
            name: person.label || person.value || person.name || '',
          }))
          .filter((person) => person.name)
        setDispatchPersons(mappedList)
      } catch (e) {
        console.error('Error fetching dispatch persons:', e)
      }
    }
    fetchDispatchPersons()
  }, [])

  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        const res = await apiService.get('e-commerce/admin-order/get-vehicle-types/')
        const list = Array.isArray(res?.data) ? res.data : []
        const mappedList = list
          .map((item, index) => ({
            id: item.value || item.id || index,
            value: item.value || '',
            label: item.label || item.value || '',
          }))
          .filter((item) => item.value)
        setVehicleTypes(mappedList)
      } catch (e) {
        console.error('Error fetching vehicle types:', e)
      }
    }
    fetchVehicleTypes()
  }, [])

  useEffect(() => {
    const fetchOrderByChoices = async () => {
      try {
        const res = await apiService.get(API_ENDPOINTS.GET_order_by_choices)
        if (res?.success && Array.isArray(res?.data)) {
          setOrderByChoices(res.data)
        } else {
          console.error('Invalid response format from order by choices API')
          setOrderByChoices([])
        }
      } catch (e) {
        console.error('Error fetching order by choices:', e)
        setOrderByChoices([])
      }
    }
    fetchOrderByChoices()
  }, [])

  const normalizeProductForPayment = (product) => {
    if (!product) return null
    const paymentStatusValue = getOrderPaymentStatusValue(product)
    return {
      ...product,
      total_amount: product.order?.total_amount ?? product.total_amount,
      payment_status: {
        ...(typeof product.payment_status === 'object' ? product.payment_status : {}),
        name: paymentStatusValue,
      },
      status: {
        name: product.status_name || product.status,
        category: product.status_category || product.status_name || product.status,
      },
    }
  }

  const closePaymentUpdateModalState = () => ({ ...DEFAULT_PAYMENT_UPDATE_MODAL })

  const PAYMENT_BANK_NAME_FIELD_KEYS = new Set([
    'sender_bank_name',
    'bank_name_branch',
    'bank_name',
  ])

  const isPaymentBankNameField = (field = {}) => {
    const key = String(field.key || '').toLowerCase()
    const label = String(field.label || '').toLowerCase()
    if (PAYMENT_BANK_NAME_FIELD_KEYS.has(key)) return true
    return label.includes('sender bank') || label.includes('bank name')
  }

  const enforcePaymentMethodRequiredFields = (formConfig) => {
    if (!formConfig?.fields) return formConfig
    const enforcedConfig = {
      ...formConfig,
      fields: formConfig.fields.map((field) =>
        isPaymentBankNameField(field)
          ? { ...field, required: false }
          : { ...field, required: true }
      ),
    }
    if (enforcedConfig.fileField) {
      enforcedConfig.fileField = { ...enforcedConfig.fileField, required: true }
    }
    return enforcedConfig
  }

  const getCreditChequePaymentFields = (paymentFields, installmentAmount, paymentMethodConfig) => {
    if (paymentMethodConfig?.type !== 'cheque') return paymentFields
    return {
      ...paymentFields,
      cheque_amount: installmentAmount ?? '',
    }
  }

  const validatePaymentMethodMandatoryFields = (modalState) => {
    if (isZeroRemainingPayment(modalState)) return ''
    if (!modalState?.paymentMethod || !modalState?.paymentMethodConfig) return ''

    // If this is a credit payment flow, require only the note (creditNote).
    // Other fields (payment method, files, etc.) are not required for Credit.
    if (modalState?.paymentOption === 'credit') {
      if (!String(modalState.creditNote || '').trim()) return 'Please enter a note'
      return ''
    }

    const fields = modalState.paymentFields || {}
    const paymentFiles = modalState.paymentFiles || {}
    const formConfig = enforcePaymentMethodRequiredFields(modalState.paymentMethodConfig)
    const type = formConfig.type

    const missingField = formConfig.fields?.find((field) => {
      if (!field.required) return false
      return !String(fields[field.key] || '').trim()
    })
    if (missingField) {
      return `Please enter ${missingField.label}`
    }

    if (type === 'bank_transfer') {
      const ref = String(fields.transaction_reference_no || fields.transaction_id || '').trim()
      if (!/^[A-Z0-9]+$/.test(ref)) {
        return 'Transaction Reference No. must contain only English letters and numbers (A–Z, 0–9)'
      }
    }
    if (type === 'cash') {
      const receiver = String(fields.receiver_name || '').trim()
      if (!/^[a-zA-Z\s]+$/.test(receiver)) {
        return 'Receiver Name must contain only English letters'
      }
    }
    if (type === 'cheque') {
      const chequeNo = String(fields.cheque_number || '').trim()
      if (!/^\d{1,6}$/.test(chequeNo)) {
        return 'Cheque Number must be 1 to 6 digits'
      }
    }
    if (
      formConfig.fileField?.required &&
      !(paymentFiles[formConfig.fileField.key] instanceof File)
    ) {
      return `Please upload ${formConfig.fileField.label}`
    }
    return ''
  }

  const buildNewCreditPaymentFormData = (modalState, orderId, installmentAmount, creditNote = '') => {
    const paidFormState = {
      ...modalState,
      advanceAmount: String(installmentAmount),
      orderPaymentDetails: modalState?.orderPaymentDetails
        ? { ...modalState.orderPaymentDetails, remainingAmount: installmentAmount }
        : { remainingAmount: installmentAmount, totalAmount: installmentAmount, paidAmount: 0 },
    }
    const paidFormData = buildNewPaidPaymentFormData(paidFormState, orderId)
    const formData = new FormData()
    for (const [key, value] of paidFormData.entries()) {
      if (key === 'payment_status') {
        formData.append(key, 'Credit')
      } else if (key === 'paid_amount') {
        formData.append(key, String(installmentAmount))
      } else {
        formData.append(key, value)
      }
    }
    if (creditNote.trim()) {
      formData.append('note', creditNote.trim())
    }
    return formData
  }

  const getInlinePaidPaymentStepState = (orderPaymentDetails) => ({
    ...getPaidPaymentStepState(orderPaymentDetails),
    step: 'status',
  })

  const getCreditPaymentStepState = (orderPaymentDetails = null) => {
    const remainingAmount = Number(orderPaymentDetails?.remainingAmount ?? 0)
    return {
      step: 'status',
      paymentOption: 'credit',
      installmentAmount: remainingAmount === 0 ? '0' : '',
      creditNote: '',
      paymentMethod: '',
      paymentMethodConfig: null,
      paymentFields: {},
      paymentFiles: {},
      paymentError: '',
    }
  }

  const openPaymentUpdateModalState = (product) => {
    const order = normalizeProductForPayment(product)
    const paymentStatus = getOrderPaymentStatusValue(order)
    const orderDetails = getOrderPaymentDetailsFromOrder(order)
    const base = {
      ...DEFAULT_PAYMENT_UPDATE_MODAL,
      open: true,
      order,
      initialPaymentStatus: paymentStatus,
      refundAmount:
        product?.refund_amount ??
        product?.refunded_amount ??
        product?.order?.total_amount ??
        product?.total_amount ??
        '',
    }

    if (paymentStatus === 'credit') {
      return {
        ...base,
        orderPaymentDetails: orderDetails,
        ...getCreditPaymentStepState(orderDetails),
      }
    }

    return base
  }

  const applyOrderPaymentDetails = (orderId, orderDetails) => {
    const currentStatus = orderDetails?.current_payment_status
      ? String(orderDetails.current_payment_status).toLowerCase()
      : null
    const paidStepPatch = getPaidPaymentStepState(orderDetails)

    setPaymentUpdateModal((prev) => {
      if (!prev.open || prev.order?.order_id !== orderId) return prev
      return {
        ...prev,
        orderPaymentDetails: orderDetails,
        ...(currentStatus
          ? {
              order: {
                ...prev.order,
                payment_status: {
                  ...(typeof prev.order?.payment_status === 'object' ? prev.order.payment_status : {}),
                  name: currentStatus,
                },
              },
            }
          : {}),
        ...(currentStatus === 'credit' || prev.paymentOption === 'credit'
          ? { ...getCreditPaymentStepState(orderDetails), step: 'status' }
          : prev.paymentOption === 'remaining'
            ? { ...paidStepPatch, step: 'status' }
            : {}),
      }
    })

    setBulkPaymentStatusModal((prev) => {
      if (!prev.open || prev.summaryOrder?.order_id !== orderId) return prev
      return {
        ...prev,
        orderPaymentDetails: orderDetails,
        ...(currentStatus === 'credit' ||
        prev.paymentOption === 'credit' ||
        prev.selectedPaymentStatus === 'credit'
          ? { ...getCreditPaymentStepState(orderDetails), step: 'status' }
          : prev.paymentOption === 'remaining' || prev.selectedPaymentStatus === 'paid'
            ? { ...paidStepPatch, step: 'status' }
            : prev.step === 'payment_details'
              ? {
                  ...paidStepPatch,
                  selectedPaymentStatus: prev.selectedPaymentStatus || 'paid',
                }
              : {}),
      }
    })
  }

  const fetchPaymentStatusChoicesForOrder = async (orderId) => {
    if (!orderId) {
      setPaymentStatusChoices([])
      return
    }
    setLoadingPaymentStatusChoices(true)
    try {
      const res = await apiService.get(
        `${API_ENDPOINTS.GET_payment_status_choices}?order_id=${encodeURIComponent(orderId)}`
      )
      if (res?.success && Array.isArray(res?.data)) {
        setPaymentStatusChoices(
          [...res.data].sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
        )
        applyOrderPaymentDetails(orderId, res?.order_details || null)
      } else {
        setPaymentStatusChoices([])
        applyOrderPaymentDetails(orderId, null)
      }
    } catch (e) {
      console.error('Error fetching payment status choices:', e)
      setPaymentStatusChoices([])
      applyOrderPaymentDetails(orderId, null)
    } finally {
      setLoadingPaymentStatusChoices(false)
    }
  }

  const isPaymentUpdateDisabled = (product) => {
    const order = normalizeProductForPayment(product)
    const paymentStatus = getOrderPaymentStatusValue(order)
    if (paymentStatus === 'paid') return true
    if (paymentStatus === 'credit') return false
    return isPaymentUpdateDisabledForOrder(order, statusFilter)
  }

  const getPaymentUpdateTooltip = (product) => {
    if (!shouldEnableButton?.('update_payment')) {
      return "You don't have permission to update payment"
    }
    const order = normalizeProductForPayment(product)
    const paymentStatus = getOrderPaymentStatusValue(order)
    if (paymentStatus === 'paid') {
      return 'Payment status cannot be updated when the order is already paid'
    }
    if (paymentStatus === 'credit') {
      return 'Update Payment'
    }
    const reason = getPaymentUpdateDisabledReason(order, statusFilter)
    if (reason) return reason
    return 'Update Payment'
  }

  const loadAdvancePaymentMethods = async () => {
    setLoadingAdvancePaymentMethods(true)
    try {
      const methods = await fetchAdvancePaymentMethods()
      setAdvancePaymentMethods(methods)
    } catch (error) {
      console.error('Error fetching advance payment methods:', error)
      setAdvancePaymentMethods([])
    } finally {
      setLoadingAdvancePaymentMethods(false)
    }
  }

  const buildPaymentMethodChangePayload = (methodId, orderPaymentDetails) => {
    const config = paymentMethodsById[methodId] || null
    const remaining = Number(orderPaymentDetails?.remainingAmount ?? 0)
    return {
      paymentMethod: methodId,
      paymentMethodConfig: config,
      paymentFields:
        config?.type === 'cheque' && remaining > 0
          ? { cheque_amount: String(remaining) }
          : {},
      paymentFiles: {},
      paymentError: '',
    }
  }

  const openPaymentModalForProduct = (product) => {
    if (!product || isPaymentUpdateDisabled(product)) return
    setPaymentUpdateModal(openPaymentUpdateModalState(product))
    fetchPaymentStatusChoicesForOrder(product.order_id)
    loadAdvancePaymentMethods()
  }

  const getPaymentChoiceValue = (choice) =>
    String(choice.value || choice.key || choice.name || '').toLowerCase()

  const getPaymentChoiceLabel = (choice) =>
    choice.label || choice.name || choice.value || choice.key || ''

  const paymentStatusFallbackOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'outstanding', label: 'Outstanding' },
    { value: 'paid', label: 'Paid' },
    { value: 'failed', label: 'Failed' },
    { value: 'refunded', label: 'Refunded' },
  ]

  const renderPaymentStatusMenuItems = ({ hidePaid = false } = {}) => {
    const shouldHidePaid = (value) =>
      hidePaid && String(value || '').toLowerCase() === 'paid'

    if (loadingPaymentStatusChoices) {
      return (
        <MenuItem disabled>
          <CircularProgress size={18} sx={{ mr: 1 }} />
          Loading payment statuses...
        </MenuItem>
      )
    }
    if (paymentStatusChoices.length > 0) {
      return paymentStatusChoices
        .filter((choice) => !shouldHidePaid(getPaymentChoiceValue(choice)))
        .map((choice) => {
        const value = getPaymentChoiceValue(choice)
        const label = getPaymentChoiceLabel(choice)
        return (
          <MenuItem key={value || label} value={value}>
            <PaymentStatusChip label={label} status={value} size="small" sx={{ mr: 1 }} />
            {label}
          </MenuItem>
        )
      })
    }
    return paymentStatusFallbackOptions
      .filter((option) => !shouldHidePaid(option.value))
      .map((option) => (
      <MenuItem key={option.value} value={option.value}>
        <PaymentStatusChip label={option.label} status={option.value} size="small" sx={{ mr: 1 }} />
        {option.label}
      </MenuItem>
    ))
  }

  const validatePaidPaymentDetails = (modalState) => {
    const apiValidation = validateNewPaidPaymentRequest(modalState)
    if (apiValidation) return apiValidation
    const mandatoryError = validatePaymentMethodMandatoryFields(modalState)
    if (mandatoryError) return mandatoryError
    return validatePaidPaymentFormDetails(
      modalState,
      enforcePaymentMethodRequiredFields(modalState.paymentMethodConfig)
    )
  }

  const canSubmitPaidPayment = (modalState) => {
    if (modalState?.paymentOption === 'credit') return false
    if (!modalState?.orderPaymentDetails) return false
    if (!String(modalState.creditNote || '').trim()) return false
    if (isZeroRemainingPayment(modalState)) return true
    if (!modalState.paymentMethod || !modalState.paymentMethodConfig) return false
    if (validatePaymentMethodMandatoryFields(modalState)) return false
    return !validatePaidPaymentFormDetails(
      modalState,
      enforcePaymentMethodRequiredFields(modalState.paymentMethodConfig)
    )
  }

  const canSubmitCreditPayment = (modalState) => {
    if (modalState?.paymentOption !== 'credit') return false
    if (!String(modalState.creditNote || '').trim()) return false

    const remainingAmount = Number(modalState?.orderPaymentDetails?.remainingAmount ?? 0)
    const hasInstallmentAmount =
      String(modalState?.installmentAmount ?? '').trim() !== ''

    if (remainingAmount > 0 && hasInstallmentAmount) {
      if (!modalState.paymentMethod || !modalState.paymentMethodConfig) return false
      if (validatePaymentMethodMandatoryFields(modalState)) return false
    }

    return true
  }

  const shouldShowCreditSubmitButton = (modalState) => {
    if (modalState?.paymentOption !== 'credit') return false
    const remainingAmount = Number(modalState?.orderPaymentDetails?.remainingAmount ?? 0)
    return remainingAmount >= 0
  }

  const submitPaymentStatusRequest = async (order_id, newStatus, extras = {}) => {
    const normalizedStatus = String(newStatus || '').toLowerCase()

    if (normalizedStatus === 'paid') {
      if (!extras.modalState) {
        throw { success: false, message: 'Payment details are required' }
      }
      return submitNewPaidPaymentStatus(extras.modalState, order_id)
    }

    const userId = localStorage.getItem('user_id')
    const { refundAmount, orderTotalAmount, installmentAmount, creditNote } = extras
    const isRefunded = normalizedStatus === 'refunded'
    const isCredit = normalizedStatus === 'credit'
    const isFailed = normalizedStatus === 'failed'
    const isPending = normalizedStatus === 'pending'
    const isOutstanding = normalizedStatus === 'outstanding'
    const refundVal =
      refundAmount != null && refundAmount !== '' ? refundAmount : orderTotalAmount ?? 0

    const payload = {
      order_id,
      payment_status: newStatus,
      user_id: userId,
      ...(isRefunded && { refund_amount: refundVal }),
      ...(isCredit && installmentAmount != null && { installment_amount: installmentAmount }),
      ...((isCredit || isFailed || isPending || isOutstanding || isRefunded) && creditNote && { note: creditNote }),
    }
    return apiService.put(API_ENDPOINTS.UPDATE_payment_status, payload)
  }

  const getPaymentStatusColor = (statusName) => {
    const status = (statusName || '').toLowerCase()
    switch (status) {
      case 'pending': return 'warning'
      case 'paid': return 'success'
      case 'failed': return 'error'
      case 'refunded': return 'info'
      default: return 'default'
    }
  }

  const openOrderDetails = (product) => {
    const routeId = product?.order_id || product?.id
    if (!routeId) return
    rememberLastOpenedBulkOrder(product)
    if (statusFilter) {
      sessionStorage.setItem(BULK_ORDER_STATUS_TAB_STORAGE_KEY, statusFilter)
    }
    const listPage = Number(currentPage) > 0 ? Number(currentPage) : 1
    const listPageSize = Number(pageSize) > 0 ? Number(pageSize) : 10
    const toStoredDate = (date) => {
      if (date == null || date === '') return ''
      if (typeof date === 'string') {
        const trimmed = date.trim()
        if (!trimmed) return ''
        return trimmed.includes('T') ? trimmed.split('T')[0] : trimmed
      }
      if (date instanceof Date && !Number.isNaN(date.getTime())) {
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
      }
      return ''
    }
    const startDateStored = toStoredDate(listFilters?.startDate)
    const endDateStored = toStoredDate(listFilters?.endDate)
    const listState = {
      page: listPage,
      pageSize: listPageSize,
      searchTerm: typeof searchQuery === 'string' ? searchQuery : '',
      selectedCompany: listFilters?.selectedCompany ?? 'all',
      showFilters: Boolean(listFilters?.showFilters),
      ...(startDateStored ? { startDate: startDateStored } : {}),
      ...(endDateStored ? { endDate: endDateStored } : {}),
      datesCleared: Boolean(listFilters) && !startDateStored && !endDateStored,
    }
    saveBulkOrderListState(listState)
    markBulkOrderExpectReturn()
    navigate(`/dashboard/bulk-upload/${encodeURIComponent(routeId)}`, {
      state: {
        product,
        statusFilter,
        ...listState,
      },
    })
  }

  const normalizedData = bulkProducts.map(formatBulkProductData)
  
  const filteredData = normalizedData
    .filter(product => {
      const query = (searchQuery || '').toString().trim().toLowerCase()
      const matchesSearch =
        !query ||
        (product.product_name || '').toString().trim().toLowerCase().includes(query) ||
        (product.customer_name || '').toString().trim().toLowerCase().includes(query) ||
        (product.order_id || '').toString().trim().toLowerCase().includes(query)
      return matchesSearch
    })

  const getTruckOrderWithStartKm = (orders = []) =>
    orders.find((order) => {
      const challan = order?.delivery_challan || {};
      const orderBy = String(order?.order?.order_by || challan?.order_by || '')
        .trim()
        .toLowerCase();
      const startKm = challan?.start_km;
      return (
        (orderBy === 'truck' || orderBy === 'by truck') &&
        startKm !== null &&
        startKm !== undefined &&
        String(startKm).trim() !== ''
      );
    });

  const isBulkPaymentUpdateDisabled = () => {
    if (PAYMENT_UPDATE_LOCKED_ORDER_STATUSES.has(
      String(statusFilter || '').toLowerCase().trim()
    )) {
      return true
    }
    const selected = filteredData.filter((product) => selectedProducts.includes(product.id))
    if (selected.length === 0) return true
    return selected.every((product) => isPaymentUpdateDisabled(product))
  }

  // Determine if bulk submit should be disabled (mirror single-order status rules)
  const isBulkSubmitDisabled = () => {
    if (bulkChallanModal.isCreating) return true
    const selectedStatus = bulkChallanModal.selectedStatus
    if (!selectedStatus || !selectedStatus.trim()) return true

    // Payment check - if any selected order unpaid, only 'Orders' allowed
    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id))
    const hasUnpaidOrders = selectedOrders.some(order => {
      const paymentStatus = (
        order.payment_status?.name ||
        order.payment_status_name ||
        order.payment_status ||
        ''
      ).toLowerCase()
      if (isProcessingDispatchSelection(statusFilter, selectedStatus)) {
        return !isDispatchPaymentSatisfied(paymentStatus)
      }
      return !(paymentStatus === 'paid' || paymentStatus === 'outstanding')
    })
    if (hasUnpaidOrders && selectedStatus !== 'Orders') return true

    const selCat = (bulkChallanModal.selectedStatusCategory || '').toLowerCase()

    // If Dispatch / Shipped - require orderBy, entityName, driver, vehicle and dispatch person
    if (selCat === 'shipped' || selectedStatus === 'Dispatch') {
      const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson
        ? bulkChallanModal.customDispatchPerson
        : bulkChallanModal.dispatchPersonName
      
      const isTruckOrder = String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck';
      const isProcessingTab = (statusFilter || '').toLowerCase() === 'processing';
      const meterNotWorking = isTruthyFlag(bulkChallanModal.meter_not_working);
      
      if (
        !bulkChallanModal.orderBy?.trim() ||
        !bulkChallanModal.entityName?.trim() ||
        !bulkChallanModal.driverName?.trim() ||
        (isTruckOrder && !bulkChallanModal.vehicleType?.trim()) ||
        !bulkChallanModal.vehicleNo?.trim() ||
        !bulkChallanModal.deliveryDate?.trim() ||
        !dispatchPersonName?.trim() ||
        !!validateDispatchLogistics(bulkChallanModal) ||
        (!meterNotWorking &&
          isTruckOrder &&
          isProcessingTab &&
          !String(bulkChallanModal.start_km || '').trim()) ||
        (!meterNotWorking &&
          isTruckOrder &&
          String(bulkChallanModal.start_km || '').trim() !== '' &&
          Number(bulkChallanModal.start_km) < 1)
      ) {
        return true
      }
    }

    // Delivered - logistics + End KM for Truck orders with Start KM
    if (selectedStatus === 'Delivered') {
      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id))
      const isDispatchTabForBulk = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
      const referenceOrder =
        getTruckOrderWithStartKm(selectedOrders) ||
        selectedOrders.find((order) => getOrderChallan(order)) ||
        selectedOrders[0];
      const referenceChallan = getOrderChallan(referenceOrder);
      if (validateDeliveredLogistics(bulkChallanModal, referenceChallan)) return true;

      const meterNotWorking = isTruthyFlag(
        bulkChallanModal.meter_not_working ?? referenceChallan?.meter_not_working
      );
      const truckOrderWithStartKm = getTruckOrderWithStartKm(selectedOrders);

      if (truckOrderWithStartKm && !meterNotWorking) {
        const startKm = parseFloat(bulkChallanModal.start_km || truckOrderWithStartKm.delivery_challan?.start_km)
        const endKm = parseFloat(bulkChallanModal.end_km)
        if (!String(bulkChallanModal.end_km || '').trim()) return true
        if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) return true
      }
      if (isDispatchTabForBulk && !isOrderByTruck(referenceOrder) && !meterNotWorking) {
        // non-truck on Dispatch: only logistics required (already validated)
      }
    }

    // Cancelled / Returned require reason
    if (selectedStatus === 'Cancelled' && !bulkChallanModal.cancellationReason?.trim()) return true
    if (selectedStatus === 'Returned' && !bulkChallanModal.returnReason?.trim()) return true

    return false
  }

  // Create delivery challan
  const createDeliveryChallan = async (orderId, driverName, vehicleNo) => {
    try {
      const dispatchPersonName = statusUpdateModal.useCustomDispatchPerson 
        ? statusUpdateModal.customDispatchPerson 
        : statusUpdateModal.dispatchPersonName;
      const meterNotWorking = isTruthyFlag(statusUpdateModal.meter_not_working);

      const payload = {
        order_id: orderId,
        driver_name: driverName,
        vehicle_no: vehicleNo || '',
        vehicle_type: resolveVehicleTypeForPayload(
          statusUpdateModal.orderBy,
          statusUpdateModal.vehicleType
        ),
        invoice: statusUpdateModal.generateInvoice ? true : false,
        ...(statusUpdateModal.shippedDate ? { delivery_date: statusUpdateModal.shippedDate } : {}),
        ...(statusUpdateModal.emails?.length > 0 ? { emails: statusUpdateModal.emails.join(',') } : {}),
        ...(dispatchPersonName ? { dispatch_person_name: dispatchPersonName } : {}),
        ...(statusUpdateModal.order?.status_name ? { status: statusUpdateModal.order.status_name } : {}),
        ...(statusUpdateModal.orderBy ? { order_by: statusUpdateModal.orderBy } : {}),
        ...(statusUpdateModal.entityName ? { order_by_entity: statusUpdateModal.entityName } : {}),
        ...(!meterNotWorking && statusUpdateModal.start_km
          ? { start_km: statusUpdateModal.start_km }
          : {}),
        ...buildDispatchLogisticsPayload(statusUpdateModal),
      };

      const response = await apiService.post(
        API_ENDPOINTS.POST_create_delivery_challan,
        payload,
        'application/json'
      );

      return response;
    } catch (error) {
      console.error('Error creating delivery challan:', error);
      throw error;
    }
  };

  const handleStatusUpdate = async (id, statusName, orderId) => {
    try {
      console.log('Updating status for order:', orderId);
      console.log('New status (name):', statusName);

      setUpdatingStatus(true);
      
      // Validate payment status - allow orders to proceed if paid or outstanding
      // Outstanding payment status is allowed to proceed (no restriction)
      const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
      const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding' || paymentStatus === 'credit';
      const isOrdersStatus = statusName === 'Orders';
      
      // Only block if payment is not paid/outstanding/credit AND status is not "Orders"
      // Outstanding and Credit payment status allow orders to proceed
      if (!isPaid && !isOrdersStatus) {
        setStatusUpdateModal(prev => ({
          ...prev,
          challanError: 'This order must be paid before it can be moved to any status other than "Orders". Please update the payment status first.'
        }));
        setUpdatingStatus(false);
        return;
      }
      
      // Validate sequential status update
      // Use originalStatus if available (before user changes), otherwise use current order status
      const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
      const originalStatusName = (statusUpdateModal.originalStatus?.name || '').toLowerCase();
      const currentStatusCategory = originalStatusCategory || (statusUpdateModal.order?.status_category || statusUpdateModal.order?.status_name || '').toLowerCase();
      const currentStatusName = originalStatusName || (statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name || '').toLowerCase();
      const selectedOption = statusOptions.find(opt => opt.name === statusName);
      const newStatusCategory = (selectedOption?.category || '').toLowerCase();
      const newStatusName = (statusName || '').toLowerCase();
      
      const statusOrder = getStatusOrder();
      const currentOrder = statusOrder[currentStatusCategory] || 0;
      const newOrder = statusOrder[newStatusCategory] || 0;
      const processingOrder = statusOrder['processing'] || 0;
      const deliveredOrder = statusOrder['delivered'] || 999;
      
      // Check if current status is Processing
      const isProcessingStatus = currentStatusName === 'processing' || 
                                  currentStatusCategory === 'processing' ||
                                  currentOrder === processingOrder;
      
      // Check if current status is Delivered
      const isDeliveredStatus = currentStatusName === 'delivered' || 
                                currentStatusCategory === 'delivered' ||
                                currentOrder === deliveredOrder;
      
      // Check if current status is Cancelled
      const isCancelledStatus = currentStatusName === 'cancelled' || 
                                currentStatusCategory === 'cancelled';
      
      const isCancelledOrReturned = newStatusCategory === 'cancelled' || newStatusCategory === 'returned' ||
                                    newStatusName === 'cancelled' || newStatusName === 'returned';
      const isReturned = newStatusCategory === 'returned' || newStatusName === 'returned';
      const isDispatch = newStatusCategory === 'shipped' || newStatusCategory === 'dispatch' || newStatusName === 'dispatch';
      
      // Special case: If status is Delivered, only allow Returned
      if (isDeliveredStatus) {
        if (!isReturned) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `When an order is Delivered, only Returned status is allowed.`
          }));
          setUpdatingStatus(false);
          return;
        }
        // Allow Returned from Delivered, skip other validations
      } else {
        // Returned is only available from Delivered status
        if (isReturned) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `Returned status is only available when the order is Delivered.`
          }));
          setUpdatingStatus(false);
          return;
        }
        
        // Special case: If status is Cancelled, no further status changes allowed
        if (isCancelledStatus) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `Cancelled is a final status. No further status changes are allowed.`
          }));
          setUpdatingStatus(false);
          return;
        }
        
        // Dispatch is only available when current status is Processing
        // This check ensures the order is in Processing before allowing Dispatch
        // The sequential flow check below will also validate the order progression
        if (isDispatch) {
          // Get dispatch order to verify it's the next step after Processing
          const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
          
          // Check if current status is actually Processing
          // Verify by name, category, and order sequence
          const isActuallyProcessing = (currentStatusName === 'processing' || 
                                      currentStatusCategory === 'processing') &&
                                      currentOrder === processingOrder &&
                                      processingOrder > 0;
          
          if (!isActuallyProcessing) {
            // Use original status for display, fallback to current if original not available
            const currentStatusDisplay = statusUpdateModal.originalStatus?.name || 
                                       statusUpdateModal.order?.status_name || 
                                       statusUpdateModal.order?.status?.name || 
                                       currentStatusCategory ||
                                       'Unknown';
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: `Dispatch status is only available when the order is in Processing status. Current status: ${currentStatusDisplay}.`
            }));
            setUpdatingStatus(false);
            return;
          }
        }
        
        // Cancelled is allowed before Delivered (but not after, which is handled by isDeliveredStatus check above)
        // The sequential flow validation below will handle if Cancelled can be selected
      }
      
      // For non-cancelled/returned statuses, enforce sequential flow
      if (!isCancelledOrReturned) {
        // Check if trying to skip sequential steps
        if (newOrder > currentOrder + 1) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `Status must be updated sequentially. You can only move to the next status in the sequence. Current: ${statusUpdateModal.order?.status_name || currentStatusCategory}, Next allowed: ${statusOptions.find(opt => {
              const optOrder = statusOrder[(opt.category || '').toLowerCase()] || 0;
              return optOrder === currentOrder + 1;
            })?.name || 'N/A'}.`
          }));
          setUpdatingStatus(false);
          return;
        }
        
        // Check if trying to go backwards
        if (newOrder < currentOrder) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: `Cannot move to a previous status. Current status: ${statusUpdateModal.order?.status_name || currentStatusCategory}.`
          }));
          setUpdatingStatus(false);
          return;
        }
      }
      
      // If status category is 'shipped' (Dispatch), create delivery challan first
      const selectedOptionForShipping = statusOptions.find(opt => opt.name === statusName);
      const statusCategoryForShipping = (selectedOptionForShipping?.category || '').toLowerCase();
      if (statusCategoryForShipping === 'shipped') {
        const { driverName, vehicleNo } = statusUpdateModal;
        const isTruckOrderBy = String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck';
        const isProcessingTab = (statusFilter || '').toLowerCase() === 'processing';
        const meterNotWorking = isTruthyFlag(statusUpdateModal.meter_not_working);
        const dispatchPersonName = statusUpdateModal.useCustomDispatchPerson 
          ? statusUpdateModal.customDispatchPerson 
          : statusUpdateModal.dispatchPersonName;
        const isDispatchFormIncomplete =
          !statusUpdateModal.orderBy?.trim() ||
          !statusUpdateModal.entityName?.trim() ||
          !driverName.trim() ||
          (isTruckOrderBy && !statusUpdateModal.vehicleType?.trim()) ||
          !vehicleNo.trim() ||
          !statusUpdateModal.shippedDate ||
          !dispatchPersonName?.trim() ||
          !!validateDispatchLogistics(statusUpdateModal) ||
          (!meterNotWorking &&
            isTruckOrderBy &&
            isProcessingTab &&
            !String(statusUpdateModal.start_km || '').trim()) ||
          (!meterNotWorking &&
            isTruckOrderBy &&
            String(statusUpdateModal.start_km || '').trim() !== '' &&
            Number(statusUpdateModal.start_km) < 1);

        if (isDispatchFormIncomplete) {
          let challanError = 'Please fill all required delivery details before shipping.';
          if (validateDispatchLogistics(statusUpdateModal)) {
            challanError = validateDispatchLogistics(statusUpdateModal);
          } else if (
            !meterNotWorking &&
            isTruckOrderBy &&
            isProcessingTab &&
            !String(statusUpdateModal.start_km || '').trim()
          ) {
            challanError = 'Start KM is required for Truck orders.';
          } else if (
            !meterNotWorking &&
            isTruckOrderBy &&
            String(statusUpdateModal.start_km || '').trim() !== '' &&
            Number(statusUpdateModal.start_km) < 1
          ) {
            challanError = 'Start KM must be 1 or greater';
          } else if (!dispatchPersonName?.trim()) {
            challanError = 'Dispatch Person Name is required. Please select a dispatch person or add a custom one.';
          }
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError
          }));
          setUpdatingStatus(false);
          return;
        }

        setStatusUpdateModal(prev => ({
          ...prev,
          isCreatingChallan: true,
          challanError: ''
        }));

        try {
          // Create delivery challan first
          const challanResponse = await createDeliveryChallan(orderId, driverName, vehicleNo);
          
          if (!challanResponse?.success) {
            throw new Error(challanResponse?.message || 'Failed to create delivery challan');
          }

          console.log('Delivery challan created successfully');
        } catch (challanError) {
          console.error('Error creating delivery challan:', challanError);
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: challanError.message || 'Failed to create delivery challan',
            isCreatingChallan: false
          }));
          setUpdatingStatus(false);
          return;
        }

        setStatusUpdateModal(prev => ({
          ...prev,
          isCreatingChallan: false
        }));
      }

      if (statusName === 'Delivered') {
        const isDispatchTab = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
        const isTruckOrder = isDispatchTab
          ? isOrderByTruck(statusUpdateModal.order)
          : String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck';
        const challan = getOrderChallan(statusUpdateModal.order);
        const meterNotWorking = isTruthyFlag(
          statusUpdateModal.meter_not_working ?? challan?.meter_not_working
        );
        const deliveredLogisticsError = validateDeliveredLogistics(statusUpdateModal, challan);
        if (deliveredLogisticsError) {
          setStatusUpdateModal(prev => ({
            ...prev,
            challanError: deliveredLogisticsError
          }));
          setUpdatingStatus(false);
          return;
        }
        if ((!isDispatchTab || isTruckOrder) && !meterNotWorking) {
          const startKmFromModal = statusUpdateModal.start_km;
          const startKmFromOrder = challan?.start_km;
          const startKm = parseFloat(startKmFromModal || startKmFromOrder);
          const endKm = parseFloat(statusUpdateModal.end_km);
          const hasStartKm = !isNaN(startKm);

          if (hasStartKm && !String(statusUpdateModal.end_km || '').trim()) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: 'End KM is required'
            }));
            setUpdatingStatus(false);
            return;
          }
          if (hasStartKm && !isNaN(endKm) && endKm <= startKm) {
            setStatusUpdateModal(prev => ({
              ...prev,
              challanError: 'End KM must be greater than Start KM'
            }));
            setUpdatingStatus(false);
            return;
          }
        }
      }

      // Now update the status
      const userId = localStorage.getItem("user_id");
      const dispatchPersonName = statusUpdateModal.useCustomDispatchPerson 
        ? statusUpdateModal.customDispatchPerson 
        : statusUpdateModal.dispatchPersonName;
      const resolvedVehicleType = resolveVehicleTypeForPayload(
        statusUpdateModal.orderBy,
        statusUpdateModal.vehicleType
      );
      const challanForPayload = getOrderChallan(statusUpdateModal.order);
      const meterNotWorkingForPayload = isTruthyFlag(
        statusUpdateModal.meter_not_working ?? challanForPayload?.meter_not_working
      );
      const payload = {
        user_id: userId,
        status: statusName, // send Name as requested
        order_id: orderId,
        emails: statusUpdateModal.emails,
        generateInvoice: statusUpdateModal.generateInvoice,
        driverName: statusUpdateModal.driverName,
        vehicleNo: statusUpdateModal.vehicleNo,
        delivery_date: statusName === 'Processing' ? null : statusUpdateModal.shippedDate,
        dispatch_person_name: dispatchPersonName,
        start_km: isTruthyFlag(statusUpdateModal.meter_not_working) ? '' : statusUpdateModal.start_km,
        ...(statusUpdateModal.orderBy && { order_by: statusUpdateModal.orderBy }),
        ...(resolvedVehicleType && { vehicle_type: resolvedVehicleType }),
        ...(statusUpdateModal.entityName && { order_by_entity: statusUpdateModal.entityName }),
        ...(statusCategoryForShipping === 'shipped'
          ? buildDispatchLogisticsPayload(statusUpdateModal)
          : {}),
        ...(statusName === 'Delivered' && !meterNotWorkingForPayload
          ? { end_km: statusUpdateModal.end_km }
          : {}),
        ...(statusName === 'Delivered'
          ? buildDeliveredLogisticsPayload(statusUpdateModal, challanForPayload)
          : {}),
        ...(statusName === 'Cancelled' && { reason: statusUpdateModal.cancellationReason || '' }),
        ...(statusName === 'Returned' && { reason: statusUpdateModal.returnReason || '' })
      };

      const response = await (statusCategoryForShipping === 'shipped' &&
      statusUpdateModal.diesel_receipt_upload instanceof File
        ? (() => {
            const formData = new FormData();
            Object.entries(payload).forEach(([key, value]) => {
              if (value === undefined || value === null) return;
              if (Array.isArray(value)) {
                formData.append(key, value.join(','));
                return;
              }
              formData.append(
                key,
                typeof value === 'boolean' || typeof value === 'number' ? String(value) : value
              );
            });
            formData.append(
              'diesel_receipt_photo',
              statusUpdateModal.diesel_receipt_upload,
              statusUpdateModal.diesel_receipt_upload.name
            );
            return apiService.putMultipart(API_ENDPOINTS.UPDATE_product_status, formData);
          })()
        : apiService.put(
            API_ENDPOINTS.UPDATE_product_status,
            payload,
            "application/json"
          ));

      if (response?.success) {
        setStatusUpdateModal({ 
          open: false, 
          order: null,
          originalStatus: null,
          driverName: '',
          vehicleNo: '',
          vehicleType: '',
          isCreatingChallan: false,
          challanError: '',
          emails: [],
          emailInput: '',
          dispatchPersonName: '',
          customDispatchPerson: '',
          useCustomDispatchPerson: false,
          start_km: '',
          end_km: '',
          diesel_receipt_upload: null,
          diesel_receipt_upload_error: '',
          ...DEFAULT_DISPATCH_LOGISTICS,
          ...DEFAULT_DELIVERED_LOGISTICS,
          cancellationReason: '',
          returnReason: ''
        });
        // Clear selections and refresh data
        if (onSelectAll) {
          onSelectAll(false);
        }
        setTimeout(() => {
          onRefresh?.();
        }, 100);
      } else {
        console.error('Status update failed:', response?.message);
        setStatusUpdateModal(prev => ({
          ...prev,
          challanError: response?.message || 'Failed to update status'
        }));
      }
    } catch (response) {
      console.error('Error updating status:', response);
      setStatusUpdateModal(prev => ({
        ...prev,
        challanError: response?.errors?.join(', ') || 'Failed to update status'
      }));
      throw new Error(response?.errors?.join(', ') || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handlePaymentStatusUpdate = async (
    orderId,
    newStatus,
    order_id,
    refundAmount,
    orderTotalAmount,
    paidPaymentExtras = null
  ) => {
    setUpdatingPayment(true)
    try {
      const response = await submitPaymentStatusRequest(order_id, newStatus, {
        refundAmount,
        orderTotalAmount,
        installmentAmount: paidPaymentExtras?.installmentAmount,
        creditNote: paidPaymentExtras?.creditNote,
        modalState: paymentUpdateModal,
      })
      if (response?.success) {
        toast.showSuccess(response?.message || 'Payment status updated successfully')
        await onRefresh?.()
        setPaymentUpdateModal(closePaymentUpdateModalState())
        setPaymentStatusChoices([])
      } else {
        const message = response?.message || 'Failed to update payment status'
        toast.showError(message)
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }))
      }
    } catch (error) {
      console.error('Error updating payment status:', error)
      const message = extractPaymentApiError(error)
      toast.showError(message)
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }))
    } finally {
      setUpdatingPayment(false)
    }
  }

  const handleConfirmCreditPaymentDetails = async () => {
    const methodValidationError = validatePaymentMethodMandatoryFields(paymentUpdateModal)
    if (methodValidationError) {
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: methodValidationError }))
      return
    }

    if (!canSubmitCreditPayment(paymentUpdateModal)) {
      return
    }

    const remainingAmount = Number(
      paymentUpdateModal.orderPaymentDetails?.remainingAmount ?? 0
    )
    const orderId = paymentUpdateModal.order?.order_id
    const filledInstallment = paymentUpdateModal.installmentAmount
    const installmentAmount =
      remainingAmount === 0
        ? 0
        : filledInstallment !== '' && !Number.isNaN(Number(filledInstallment))
          ? Number(filledInstallment)
          : 0
    const creditNote = paymentUpdateModal.creditNote || ''

    setUpdatingPayment(true)
    setPaymentUpdateModal((prev) => ({ ...prev, paymentError: '' }))

    try {
      const formData = buildNewCreditPaymentFormData(
        paymentUpdateModal,
        orderId,
        installmentAmount,
        creditNote
      )

      const response = await apiService.putMultipart(
        API_ENDPOINTS.UPDATE_new_payment_status,
        formData
      )

      if (response?.success) {
        toast.showSuccess(response?.message || 'Payment status updated successfully')
        await onRefresh?.()
        setPaymentUpdateModal(closePaymentUpdateModalState())
        setPaymentStatusChoices([])
      } else {
        const message = response?.message || 'Failed to update payment status'
        toast.showError(message)
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }))
      }
    } catch (error) {
      console.error('Error submitting credit payment:', error)
      const message = extractPaymentApiError(error)
      toast.showError(message)
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }))
    } finally {
      setUpdatingPayment(false)
    }
  }

  const handleConfirmPaidPaymentDetails = async () => {
    const validationError = validatePaidPaymentDetails(paymentUpdateModal)
    if (validationError) {
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: validationError }))
      return
    }

    const orderId = paymentUpdateModal.order?.order_id
    setUpdatingPayment(true)
    setPaymentUpdateModal((prev) => ({ ...prev, paymentError: '' }))

    try {
      const formData = buildNewPaidPaymentFormData(paymentUpdateModal, orderId)
      formData.append('note', paymentUpdateModal.creditNote.trim())
      const response = await apiService.putMultipart(
        API_ENDPOINTS.UPDATE_new_payment_status,
        formData
      )
      if (response?.success) {
        toast.showSuccess(response?.message || 'Payment status updated successfully')
        await onRefresh?.()
        setPaymentUpdateModal(closePaymentUpdateModalState())
        setPaymentStatusChoices([])
      } else {
        const message = response?.message || 'Failed to update payment status'
        toast.showError(message)
        setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }))
      }
    } catch (error) {
      console.error('Error submitting paid payment:', error)
      const message = extractPaymentApiError(error)
      toast.showError(message)
      setPaymentUpdateModal((prev) => ({ ...prev, paymentError: message }))
    } finally {
      setUpdatingPayment(false)
    }
  }

  const getSelectedOrdersForBulkPayment = () =>
    filteredData.filter((product) => selectedProducts.includes(product.id))

  const getUnpaidOrdersFromList = (orders = []) =>
    orders.filter((product) => {
      const paymentStatus = getOrderPaymentStatusValue(normalizeProductForPayment(product))
      return paymentStatus !== 'paid' && paymentStatus !== 'outstanding'
    })

  const handleConfirmBulkCreditPaymentDetails = async () => {
    const bulkCreditState = {
      ...bulkPaymentStatusModal,
      order: bulkPaymentStatusModal.summaryOrder,
    }

    const bulkMethodValidationError = validatePaymentMethodMandatoryFields(bulkCreditState)
    if (bulkMethodValidationError) {
      setBulkPaymentStatusModal((prev) => ({
        ...prev,
        paymentError: bulkMethodValidationError,
      }))
      return
    }

    if (!canSubmitCreditPayment(bulkCreditState)) {
      return
    }

    const unpaidOrders = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment())
    if (unpaidOrders.length === 0) {
      setBulkPaymentStatusModal((prev) => ({
        ...prev,
        paymentError: 'No unpaid orders in the current selection.',
      }))
      return
    }

    const remainingAmount = Number(
      bulkPaymentStatusModal.orderPaymentDetails?.remainingAmount ?? 0
    )
    const filledInstallment = bulkPaymentStatusModal.installmentAmount
    const installmentAmount =
      remainingAmount === 0
        ? 0
        : filledInstallment !== '' && !Number.isNaN(Number(filledInstallment))
          ? Number(filledInstallment)
          : 0
    const creditNote = bulkPaymentStatusModal.creditNote || ''

    setBulkPaymentStatusModal((prev) => ({ ...prev, isUpdating: true, error: '', paymentError: '' }))

    try {
      const responses = await Promise.all(
        unpaidOrders.map((product) => {
          const order = normalizeProductForPayment(product)
          const orderDetails = getOrderPaymentDetailsFromOrder(order)
          const orderRemaining = Number(orderDetails.remainingAmount ?? 0)
          const orderInstallment = orderRemaining === 0 ? 0 : installmentAmount
          const orderModalState = {
            ...bulkCreditState,
            order,
            orderPaymentDetails: orderDetails,
          }
          const formData = buildNewCreditPaymentFormData(
            orderModalState,
            order.order_id,
            orderInstallment,
            creditNote
          )
          return apiService.putMultipart(API_ENDPOINTS.UPDATE_new_payment_status, formData)
        })
      )
      const allSuccess = responses.every((response) => response?.success)
      if (allSuccess) {
        toast.showSuccess(responses[0]?.message || 'Payment status updated successfully')
        setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL })
        await onRefresh?.()
      } else {
        const failed = responses.find((response) => !response?.success)
        const message = failed?.message || 'Some payment status updates failed. Please try again.'
        toast.showError(message)
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          isUpdating: false,
          error: message,
        }))
      }
    } catch (error) {
      console.error('Error submitting bulk credit payment:', error)
      const message = extractPaymentApiError(error)
      toast.showError(message)
      setBulkPaymentStatusModal((prev) => ({
        ...prev,
        isUpdating: false,
        error: message,
      }))
    }
  }

  const handleConfirmBulkPaidPaymentDetails = async () => {
    const bulkPaidState = {
      ...bulkPaymentStatusModal,
      order: bulkPaymentStatusModal.summaryOrder,
    }
    const unpaidOrders = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment())
    if (unpaidOrders.length === 0) {
      setBulkPaymentStatusModal((prev) => ({
        ...prev,
        paymentError: 'No unpaid orders in the current selection.',
      }))
      return
    }

    for (const product of unpaidOrders) {
      const order = normalizeProductForPayment(product)
      const orderDetails = getOrderPaymentDetailsFromOrder(order)
      const orderModalState = {
        ...bulkPaidState,
        order,
        orderPaymentDetails: orderDetails,
        advanceAmount: String(orderDetails.remainingAmount),
        paymentMethodConfig:
          paymentMethodsById[bulkPaidState.paymentMethod] || bulkPaidState.paymentMethodConfig,
      }
      const validationError = validatePaidPaymentDetails(orderModalState)
      if (validationError) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          paymentError: `${order.order_id}: ${validationError}`,
        }))
        return
      }
    }

    setBulkPaymentStatusModal((prev) => ({ ...prev, isUpdating: true, error: '', paymentError: '' }))

    try {
      const responses = await Promise.all(
        unpaidOrders.map((product) => {
          const order = normalizeProductForPayment(product)
          const orderDetails = getOrderPaymentDetailsFromOrder(order)
          const orderModalState = {
            ...bulkPaidState,
            order,
            orderPaymentDetails: orderDetails,
            advanceAmount: String(orderDetails.remainingAmount),
          }
          const formData = buildNewPaidPaymentFormData(orderModalState, order.order_id)
          formData.append('note', bulkPaidState.creditNote.trim())
          return apiService.putMultipart(API_ENDPOINTS.UPDATE_new_payment_status, formData)
        })
      )
      const allSuccess = responses.every((response) => response?.success)
      if (allSuccess) {
        toast.showSuccess(responses[0]?.message || 'Payment status updated successfully')
        setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL })
        await onRefresh?.()
      } else {
        const failed = responses.find((response) => !response?.success)
        const message = failed?.message || 'Some payment status updates failed. Please try again.'
        toast.showError(message)
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          isUpdating: false,
          error: message,
        }))
      }
    } catch (error) {
      console.error('Error updating bulk paid payment:', error)
      const message = extractPaymentApiError(error)
      toast.showError(message)
      setBulkPaymentStatusModal((prev) => ({
        ...prev,
        isUpdating: false,
        error: message,
      }))
    }
  }

  // Handle bulk payment status update from separate modal - update all selected orders
  const handleBulkPaymentStatusUpdateFromModal = async () => {
    if (!bulkPaymentStatusModal.selectedPaymentStatus) {
      setBulkPaymentStatusModal(prev => ({
        ...prev,
        error: 'Please select a payment status'
      }))
      return
    }

    if (bulkPaymentStatusModal.selectedPaymentStatus === 'paid') {
      handleConfirmBulkPaidPaymentDetails()
      return
    }

    if (
      ['failed', 'pending', 'outstanding'].includes(
        bulkPaymentStatusModal.selectedPaymentStatus
      ) &&
      !String(bulkPaymentStatusModal.creditNote || '').trim()
    ) {
      setBulkPaymentStatusModal((prev) => ({
        ...prev,
        error: 'Please enter a note',
      }))
      return
    }

    if (bulkPaymentStatusModal.selectedPaymentStatus === 'refunded') {
      if (!String(bulkPaymentStatusModal.creditNote || '').trim()) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          error: 'Please enter a note',
        }))
        return
      }
      const refundNum = Number(bulkPaymentStatusModal.refundAmount)
      const apiDetails = bulkPaymentStatusModal.orderPaymentDetails
      const advancePaidTotal = getAdvancePaymentEntries(
        bulkPaymentStatusModal.summaryOrder || bulkPaymentStatusModal
      ).reduce((sum, entry) => sum + Number(entry.advance_amount || 0), 0)
      const paidFallback =
        advancePaidTotal > 0
          ? advancePaidTotal
          : (bulkPaymentStatusModal.summaryOrder?.amount_paid ??
              bulkPaymentStatusModal.summaryOrder?.paid ??
              0)
      const maxRefund = Number(apiDetails?.paidAmount ?? paidFallback)
      if (!Number.isFinite(refundNum) || refundNum <= 0 || refundNum > maxRefund) {
        setBulkPaymentStatusModal((prev) => ({
          ...prev,
          error: 'Please enter a valid refund amount',
        }))
        return
      }
    }

    setBulkPaymentStatusModal(prev => ({ ...prev, isUpdating: true, error: '' }))
    try {
      const userId = localStorage.getItem("user_id");
      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
      const selectedOrderIds = selectedOrders.map(order => order.order_id).filter(Boolean);

      if (selectedOrderIds.length === 0) {
        setBulkPaymentStatusModal(prev => ({
          ...prev,
          error: 'No orders selected',
          isUpdating: false
        }))
        return
      }

      const isRefunded = bulkPaymentStatusModal.selectedPaymentStatus === 'refunded'
      const refundVal =
        bulkPaymentStatusModal.refundAmount != null &&
        bulkPaymentStatusModal.refundAmount !== ''
          ? bulkPaymentStatusModal.refundAmount
          : bulkPaymentStatusModal.summaryOrder?.total_amount ?? 0

      // Update payment status for all selected orders
      const updatePromises = selectedOrderIds.map(orderId => {
        const payload = {
          order_id: orderId,
          payment_status: bulkPaymentStatusModal.selectedPaymentStatus,
          user_id: userId,
          ...(isRefunded && { refund_amount: refundVal }),
          ...(bulkPaymentStatusModal.creditNote?.trim()
            ? { note: bulkPaymentStatusModal.creditNote.trim() }
            : {}),
        };
        return apiService.put(
          `${API_ENDPOINTS.UPDATE_payment_status}`,
          payload
        );
      });

      const responses = await Promise.all(updatePromises);
      const allSuccess = responses.every(response => response?.success);

      if (allSuccess) {
        toast.showSuccess('Payment status updated successfully')
        setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL })
        setPaymentStatusChoices([])
        // Clear all selections
        if (onSelectAll) {
          onSelectAll(false)
        }
        setTimeout(() => {
          onRefresh?.()
        }, 100)
        // Toast removed as per user request
      } else {
        setBulkPaymentStatusModal(prev => ({
          ...prev,
          error: 'Some payment status updates failed. Please try again.',
          isUpdating: false
        }))
      }
    } catch (error) {
      console.error("Error updating bulk payment status:", error)
      const message = extractPaymentApiError(error)
      toast.showError(message)
      setBulkPaymentStatusModal(prev => ({
        ...prev,
        error: message,
        isUpdating: false
      }))
    }
  }

  // Handle bulk payment status update from bulk challan modal - only update unpaid orders
  const handleBulkPaymentStatusUpdate = async () => {
    if (!bulkChallanModal.bulkPaymentStatus) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Please select a payment status'
      }))
      return
    }

    setIsUpdatingBulkPayment(true)
    try {
      const userId = localStorage.getItem("user_id");
      const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
      
      // Filter to only get unpaid orders (not paid or outstanding)
      const unpaidOrders = selectedOrders.filter(order => {
        const paymentStatus = (order.payment_status?.name || order.payment_status_name || order.payment_status || '').toLowerCase();
        const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding';
        return !isPaid;
      });

      if (unpaidOrders.length === 0) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'All selected orders are already paid or outstanding. No payment status update needed.'
        }))
        setIsUpdatingBulkPayment(false)
        return
      }

      const unpaidOrderIds = unpaidOrders.map(order => order.order_id).filter(Boolean);

      // Update payment status only for unpaid orders
      const updatePromises = unpaidOrderIds.map(orderId => {
        const payload = {
          order_id: orderId,
          payment_status: bulkChallanModal.bulkPaymentStatus,
          user_id: userId,
        };
        return apiService.put(
          `${API_ENDPOINTS.UPDATE_payment_status}`,
          payload
        );
      });

      const responses = await Promise.all(updatePromises);
      const allSuccess = responses.every(response => response?.success);

      if (allSuccess) {
        // Clear the payment status selection and refresh data
        setBulkChallanModal(prev => ({
          ...prev,
          bulkPaymentStatus: '',
          error: ''
        }))
        await onRefresh()
      } else {
        setBulkChallanModal(prev => ({
          ...prev,
          error: 'Some payment status updates failed. Please try again.'
        }))
      }
    } catch (error) {
      console.error("Error updating bulk payment status:", error)
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Error updating payment status. Please try again.'
      }))
    } finally {
      setIsUpdatingBulkPayment(false)
    }
  }

  // Open bulk payment status update modal
  const handleOpenBulkPaymentStatusModal = () => {
    if (isBulkPaymentUpdateDisabled()) return
    const selected = getSelectedOrdersForBulkPayment()
    const summaryOrder = normalizeProductForPayment(selected[0] || null)
    const paymentStatus = summaryOrder ? getOrderPaymentStatusValue(summaryOrder) : ''
    const orderDetails = summaryOrder ? getOrderPaymentDetailsFromOrder(summaryOrder) : null
    setBulkPaymentStatusModal({
      ...DEFAULT_BULK_PAYMENT_STATUS_MODAL,
      open: true,
      summaryOrder,
      initialPaymentStatus: paymentStatus,
      refundAmount:
        summaryOrder?.refunded_amount ??
        summaryOrder?.refund_amount ??
        summaryOrder?.total_amount ??
        '',
      orderPaymentDetails: orderDetails,
      ...(paymentStatus === 'credit' ? getCreditPaymentStepState(orderDetails) : {}),
    })
    fetchPaymentStatusChoicesForOrder(summaryOrder?.order_id)
    loadAdvancePaymentMethods()
    closeActionsMenu()
  }

  const handleCloseBulkPaymentStatusModal = () => {
    if (bulkPaymentStatusModal.isUpdating) return
    setBulkPaymentStatusModal({ ...DEFAULT_BULK_PAYMENT_STATUS_MODAL })
    setPaymentStatusChoices([])
  }
  
  // Handle individual product selection
  const handleProductSelection = (productId, isSelected) => {
    if (onProductSelect) {
      onProductSelect(productId, isSelected)
    }
  }

  // Handle select all toggle
  const handleSelectAllToggle = () => {
    if (onSelectAll) {
      onSelectAll(!isAllSelected)
    }
  }

  // Check if a product is selected
  const isProductSelected = (productId) => {
    return selectedProducts.includes(productId)
  }

  // Handle invoice view
  const handleViewInvoice = (product) => {
    if (product.invoice?.path) {
      window.open(product.invoice.path, '_blank')
    }
  }

  // Handle invoice download
  const handleDownloadInvoice = async (invoice, productName) => {
    if (!invoice || !invoice.path) {
      alert('Invoice not available for download')
      return
    }

    setDownloadingInvoice(true)
    try {
      // Create a temporary link to download the PDF
      const link = document.createElement('a')
      link.href = invoice.path
      link.download = `Invoice_${invoice.id}_${productName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading invoice:', error)
      alert('Error downloading invoice. Please try again.')
    } finally {
      setDownloadingInvoice(false)
    }
  }

  // Close invoice modal
  const handleCloseInvoiceModal = () => {
    setInvoiceModal({ open: false, invoice: null, product: null })
  }

  // Handle delivery challan view
  const handleViewDeliveryChallan = (product) => {
    if (product.delivery_challan?.path) {
      window.open(product.delivery_challan.path, '_blank')
    }
  }

  // Handle delivery challan download
  const handleDownloadDeliveryChallan = async (challan, productName) => {
    if (!challan || !challan.path) {
      alert('Delivery challan not available for download')
      return
    }

    setDownloadingChallan(true)
    try {
      // Create a temporary link to download the PDF
      const link = document.createElement('a')
      link.href = challan.path
      link.download = `Delivery_Challan_${challan.id}_${productName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading delivery challan:', error)
      alert('Error downloading delivery challan. Please try again.')
    } finally {
      setDownloadingChallan(false)
    }
  }

  // Close delivery challan modal
  const handleCloseDeliveryChallanModal = () => {
    setDeliveryChallanModal({ open: false, challan: null, product: null })
  }

  // Handle status modal opening
  const handleOpenStatusModal = (product) => {
    // Store the original status before any changes
    const originalStatus = {
      name: product?.status_name || product?.status?.name || '',
      category: product?.status_category || product?.status?.category || ''
    };
    
    // Get the next sequential status
    const nextStatus = getNextSequentialStatus(originalStatus.name, originalStatus.category);
    
    // Create updated order object with next status pre-selected
    const updatedOrder = nextStatus ? {
      ...product,
      status_name: nextStatus.name,
      status_category: nextStatus.category,
      status: {
        name: nextStatus.name,
        category: nextStatus.category
      }
    } : product;

    const dispatchChallan = getOrderChallan(product);
    const isTruckOrder = isOrderByTruck(product);
    
    setStatusUpdateModal({
      open: true,
      order: updatedOrder, // Order with next status pre-selected
      originalStatus: originalStatus, // Store original status for validation
      entityName: '',
      orderBy: '',
      driverName: isTruckOrder ? (dispatchChallan?.driver_name || '') : '',
      vehicleNo: isTruckOrder
        ? (
            dispatchChallan?.vehicle_number ||
            dispatchChallan?.vehicle_no ||
            dispatchChallan?.vehicle_name ||
            ''
          )
        : '',
      vehicleType: '',
      shippedDate: new Date().toISOString().slice(0,10),
      generateInvoice: false,
      isCreatingChallan: false,
      challanError: '',
      emails: [],
      emailInput: '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km:
        isTruckOrder &&
        dispatchChallan?.start_km !== null &&
        dispatchChallan?.start_km !== undefined
          ? String(dispatchChallan.start_km)
          : '',
      end_km: '',
      ...DEFAULT_DISPATCH_LOGISTICS,
      ...DEFAULT_DELIVERED_LOGISTICS,
      ...getChallanLogisticsDefaults(dispatchChallan),
      diesel_receipt_upload: null,
      diesel_receipt_upload_error: '',
      cancellationReason: '',
      returnReason: '',
    });
  };

  // Handle status modal closing
  const handleCloseStatusModal = () => {
    setStatusUpdateModal({
      open: false,
      order: null,
      originalStatus: null,
      entityName: '',
      orderBy: '',
      driverName: '',
      vehicleNo: '',
      vehicleType: '',
      shippedDate: new Date().toISOString().slice(0,10),
      generateInvoice: false,
      isCreatingChallan: false,
      challanError: '',
      emails: [],
      emailInput: '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km: '',
      end_km: '',
      diesel_receipt_upload: null,
      diesel_receipt_upload_error: '',
      ...DEFAULT_DISPATCH_LOGISTICS,
      ...DEFAULT_DELIVERED_LOGISTICS,
      cancellationReason: '',
      returnReason: ''
    });
  };

  // Handle input changes in status modal
  const handleStatusModalChange = (field, value) => {
    setStatusUpdateModal(prev => {
      const next = {
        ...prev,
        [field]: value,
        challanError: '' // Clear error when user types
      };
      if (field === 'orderBy') {
        next.entityName = '';
        if (String(value || '').trim().toLowerCase() !== 'truck') {
          next.vehicleType = '';
          next.start_km = '';
          next.meter_not_working = false;
          next.google_maps_km = '';
          next.diesel_receipt_number = '';
          next.diesel_amount = '';
          next.diesel_receipt_upload = null;
          next.diesel_receipt_upload_error = '';
        }
      }
      return next;
    });
  };

  // Handle bulk delivery challan creation
  const handleBulkDeliveryChallan = () => {
    // Find minimum status among selected orders to determine next sequential status
    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
    const statusOrder = getStatusOrder();
    
    // Get the minimum status order (lowest sequence) among selected orders
    let minStatusOrder = 999;
    let minStatusCategory = '';
    
    selectedOrders.forEach(order => {
      let orderStatusCategory = (order.status_category || '').toLowerCase();
      const orderStatusName = order.status_name || '';
      
      // Try to find category from statusOptions if not directly available
      if (!orderStatusCategory && orderStatusName) {
        const orderStatusOption = statusOptions.find(opt => opt.name === orderStatusName);
        orderStatusCategory = orderStatusOption?.category?.toLowerCase() || '';
      }
      
      const orderSequence = statusOrder[orderStatusCategory] || 0;
      if (orderSequence > 0 && orderSequence < minStatusOrder) {
        minStatusOrder = orderSequence;
        minStatusCategory = orderStatusCategory;
      }
    });
    
    let defaultStatus = null;
    
    if (statusFilter === 'Dispatch') {
      // When Dispatch tab is active, default to Delivered (next sequential status)
      defaultStatus = statusOptions.find(status => status.name === 'Delivered') || 
                      statusOptions.find(status => status.name === 'Returned') ||
                      statusOptions.find(status => status.name === 'Cancelled') ||
                      (statusOptions.length > 0 ? statusOptions[0] : null);
    } else if (statusFilter === 'Delivered') {
      // Keep bulk behavior aligned with single status update: Delivered -> Returned only.
      defaultStatus = statusOptions.find(status => status.name === 'Returned') || null;
    } else if (minStatusOrder !== 999 && minStatusOrder > 0) {
      // Find the next sequential status based on minimum current status
      const nextSequence = minStatusOrder + 1;
      defaultStatus = statusOptions.find(status => {
        const statusCategory = (status.category || '').toLowerCase();
        return statusOrder[statusCategory] === nextSequence;
      });
      
      // If next sequential not found, try to find any available next status
      if (!defaultStatus) {
        defaultStatus = statusOptions.find(status => {
          const statusCategory = (status.category || '').toLowerCase();
          const statusSeq = statusOrder[statusCategory] || 0;
          return statusSeq > minStatusOrder && statusSeq <= minStatusOrder + 1;
        });
      }
      
      // Fallback to first available status if no next sequential found
      if (!defaultStatus) {
        defaultStatus = statusOptions.length > 0 ? statusOptions[0] : null;
      }
    } else {
      // If no valid status found, default to first status (safety fallback)
      defaultStatus = statusOptions.length > 0 ? statusOptions[0] : null;
    }
    
    const truckOrderWithStartKm = getTruckOrderWithStartKm(selectedOrders);
    const selectedDeliveryChallan =
      getOrderChallan(truckOrderWithStartKm) ||
      getOrderChallan(selectedOrders.find((order) => getOrderChallan(order))) ||
      getOrderChallan(selectedOrders[0]);
    const isTruckOrder = Boolean(truckOrderWithStartKm) || isOrderByTruck(selectedOrders[0]);

    setBulkChallanModal({
      open: true,
      orderBy: '',
      entityName: '',
      driverName: isTruckOrder ? (selectedDeliveryChallan?.driver_name || '') : '',
      vehicleNo: isTruckOrder
        ? (
            selectedDeliveryChallan?.vehicle_number ||
            selectedDeliveryChallan?.vehicle_no ||
            selectedDeliveryChallan?.vehicle_name ||
            ''
          )
        : '',
      vehicleType: '',
      deliveryDate: new Date().toISOString().slice(0,10),
      generateInvoice: false,
      isCreating: false,
      error: '',
      emails: [],
      emailInput: '',
      selectedStatus: defaultStatus?.name || '',
      selectedStatusCategory: defaultStatus?.category || '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km:
        isTruckOrder &&
        selectedDeliveryChallan?.start_km !== null &&
        selectedDeliveryChallan?.start_km !== undefined
          ? String(selectedDeliveryChallan.start_km)
          : '',
      end_km: '',
      ...DEFAULT_DISPATCH_LOGISTICS,
      ...DEFAULT_DELIVERED_LOGISTICS,
      ...getChallanLogisticsDefaults(selectedDeliveryChallan),
      cancellationReason: '',
      returnReason: '',
      bulkPaymentStatus: ''
    })
  }

  // Create bulk delivery challan
  const createBulkDeliveryChallan = async (orderIds, driverName, vehicleNo) => {
    try {
      const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson 
        ? bulkChallanModal.customDispatchPerson 
        : bulkChallanModal.dispatchPersonName;
      const meterNotWorking = isTruthyFlag(bulkChallanModal.meter_not_working);

      const payload = {
        order_id: orderIds.join(','),
        driver_name: driverName,
        vehicle_no: vehicleNo || '',
        vehicle_type: resolveVehicleTypeForPayload(
          bulkChallanModal.orderBy,
          bulkChallanModal.vehicleType
        ),
        invoice: bulkChallanModal.generateInvoice ? true : false,
        ...(bulkChallanModal.deliveryDate ? { delivery_date: bulkChallanModal.deliveryDate } : {}),
        ...(bulkChallanModal.emails?.length > 0 ? { emails: bulkChallanModal.emails.join(',') } : {}),
        ...(!meterNotWorking && bulkChallanModal.start_km
          ? { start_km: bulkChallanModal.start_km }
          : {}),
        ...buildDispatchLogisticsPayload(bulkChallanModal),
        ...(dispatchPersonName ? { dispatch_person_name: dispatchPersonName } : {}),
        ...(bulkChallanModal.selectedStatus ? { status: bulkChallanModal.selectedStatus } : {}),
        ...(bulkChallanModal.orderBy ? { order_by: bulkChallanModal.orderBy } : {}),
        ...(bulkChallanModal.entityName ? { order_by_entity: bulkChallanModal.entityName } : {}),
      };

      const response = await apiService.post(
        API_ENDPOINTS.POST_create_delivery_challan,
        payload,
        'application/json'
      )

      return response
    } catch (error) {
      console.error('Error creating bulk delivery challan:', error)
      throw error
    }
  }

  // Handle bulk challan submission
  const handleBulkChallanSubmit = async () => {
    const { driverName, vehicleNo, selectedStatus } = bulkChallanModal

    // Validation
    if (!selectedStatus.trim()) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Please select a status to update orders to'
      }))
      return
    }

    if (selectedProducts.length === 0) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'No products selected'
      }))
      return
    }

    // Validate sequential status update
    const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
    const statusOrder = getStatusOrder();
    const selectedOption = statusOptions.find(opt => opt.name === selectedStatus);
    const newStatusCategory = (selectedOption?.category || '').toLowerCase();
    const newStatusName = (selectedStatus || '').toLowerCase();
    
    const processingOrder = statusOrder['processing'] || 0;
    const deliveredOrder = statusOrder['delivered'] || 999;
    const dispatchOrder = statusOrder['shipped'] || statusOrder['dispatch'] || 999;
    
    const isReturned = newStatusCategory === 'returned' || newStatusName === 'returned';
    const isCancelledOrReturned = newStatusCategory === 'cancelled' || newStatusCategory === 'returned' ||
                                   newStatusName === 'cancelled' || newStatusName === 'returned';
    const isDispatch = newStatusCategory === 'shipped' || newStatusCategory === 'dispatch' || newStatusName === 'dispatch';
    
    // Find minimum status among selected orders
    let minStatusOrder = 999;
    let minStatusCategory = '';
    let minStatusName = '';
    let hasDelivered = false;
    let hasCancelled = false;
    
    selectedOrders.forEach(order => {
      const orderStatusCategory = (order.status_category || order.status?.category || '').toLowerCase();
      const orderStatusName = (order.status_name || order.status?.name || '').toLowerCase();
      
      // Check if any order is Delivered
      if (orderStatusName === 'delivered' || orderStatusCategory === 'delivered' || 
          statusOrder[orderStatusCategory] === deliveredOrder) {
        hasDelivered = true;
      }
      
      // Check if any order is Cancelled
      if (orderStatusName === 'cancelled' || orderStatusCategory === 'cancelled') {
        hasCancelled = true;
      }
      
      // Find order sequence
      let orderCategory = orderStatusCategory;
      if (!orderCategory && orderStatusName) {
        const orderStatusOption = statusOptions.find(opt => opt.name === orderStatusName);
        orderCategory = (orderStatusOption?.category || '').toLowerCase();
      }
      
      const orderSequence = statusOrder[orderCategory] || 0;
      if (orderSequence > 0 && orderSequence < minStatusOrder) {
        minStatusOrder = orderSequence;
        minStatusCategory = orderCategory;
        minStatusName = orderStatusName;
      }
    });
    
    // Special case: If any order is Cancelled, no further status changes are allowed
    if (hasCancelled) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Cancelled is a final status. No further status changes are allowed for cancelled orders.'
      }));
      return;
    }
    
    // Special case: If any order is Delivered, only allow Returned
    if (hasDelivered && !isReturned) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'When an order is Delivered, only Returned status is allowed.'
      }));
      return;
    }
    
    // Check if current minimum status is Processing for Dispatch
    const isProcessingStatus = minStatusName === 'processing' || 
                               minStatusCategory === 'processing' ||
                               minStatusOrder === processingOrder;
    
    // Dispatch is only available when current status is Processing
    if (isDispatch && !isProcessingStatus) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: `Dispatch status is only available after orders have been successfully updated to Processing. Current minimum status: ${minStatusName || minStatusCategory || 'Unknown'}.`
      }));
      return;
    }
    
    // Returned is only available from Delivered status
    if (isReturned && !hasDelivered) {
      setBulkChallanModal(prev => ({
        ...prev,
        error: 'Returned status is only available when orders are Delivered.'
      }));
      return;
    }
    
    // For non-cancelled/returned statuses, enforce sequential flow
    if (!isCancelledOrReturned) {
      if (minStatusOrder === 999) {
        minStatusOrder = 0;
      }
      
      const newStatusOrder = statusOrder[newStatusCategory] || 0;
      
      // Check if trying to skip sequential steps
      if (newStatusOrder > minStatusOrder + 1) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: `Status must be updated sequentially. You can only move to the next status in the sequence. Minimum current status: ${minStatusName || minStatusCategory || 'Unknown'}, Next allowed: ${statusOptions.find(opt => {
            const optOrder = statusOrder[(opt.category || '').toLowerCase()] || 0;
            return optOrder === minStatusOrder + 1;
          })?.name || 'N/A'}.`
        }))
        return
      }
      
      // Check if trying to go backwards
      if (newStatusOrder < minStatusOrder) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: `Cannot move to a previous status. Minimum current status: ${minStatusName || minStatusCategory || 'Unknown'}.`
        }))
        return
      }
    }

    // Only validate delivery fields for Dispatch status
    if (selectedStatus === 'Dispatch') {
      const isTruckOrderBy = String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck';
      const isProcessingTab = (statusFilter || '').toLowerCase() === 'processing';
      const meterNotWorking = isTruthyFlag(bulkChallanModal.meter_not_working);
      const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson 
        ? bulkChallanModal.customDispatchPerson 
        : bulkChallanModal.dispatchPersonName;
      const isDispatchFormIncomplete =
        !bulkChallanModal.orderBy?.trim() ||
        !bulkChallanModal.entityName?.trim() ||
        !driverName.trim() ||
        (isTruckOrderBy && !bulkChallanModal.vehicleType?.trim()) ||
        !vehicleNo.trim() ||
        !bulkChallanModal.deliveryDate ||
        !dispatchPersonName?.trim() ||
        !!validateDispatchLogistics(bulkChallanModal) ||
        (!meterNotWorking &&
          isTruckOrderBy &&
          isProcessingTab &&
          !String(bulkChallanModal.start_km || '').trim()) ||
        (!meterNotWorking &&
          isTruckOrderBy &&
          String(bulkChallanModal.start_km || '').trim() !== '' &&
          Number(bulkChallanModal.start_km) < 1);

      if (isDispatchFormIncomplete) {
        let error = 'Please fill all required delivery details for Dispatch status';
        if (validateDispatchLogistics(bulkChallanModal)) {
          error = validateDispatchLogistics(bulkChallanModal);
        } else if (!dispatchPersonName?.trim()) {
          error = 'Dispatch Person Name is required. Please select a dispatch person or add a custom one.';
        }
        setBulkChallanModal(prev => ({
          ...prev,
          error
        }))
        return
      }
    }

    if (selectedStatus === 'Delivered') {
      const ordersForDelivered = filteredData.filter(order => selectedProducts.includes(order.id));
      const referenceOrder =
        getTruckOrderWithStartKm(ordersForDelivered) ||
        ordersForDelivered.find((order) => getOrderChallan(order)) ||
        ordersForDelivered[0];
      const referenceChallan = getOrderChallan(referenceOrder);
      const deliveredLogisticsError = validateDeliveredLogistics(bulkChallanModal, referenceChallan);
      if (deliveredLogisticsError) {
        setBulkChallanModal(prev => ({
          ...prev,
          error: deliveredLogisticsError
        }))
        return
      }
      const meterNotWorking = isTruthyFlag(
        bulkChallanModal.meter_not_working ?? referenceChallan?.meter_not_working
      );
      const truckOrderWithStartKm = getTruckOrderWithStartKm(ordersForDelivered);
      if (truckOrderWithStartKm && !meterNotWorking) {
        const startKm = parseFloat(
          bulkChallanModal.start_km || truckOrderWithStartKm.delivery_challan?.start_km
        );
        const endKm = parseFloat(bulkChallanModal.end_km);
        if (!String(bulkChallanModal.end_km || '').trim()) {
          setBulkChallanModal(prev => ({
            ...prev,
            error: 'End KM is required'
          }))
          return
        }
        if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
          setBulkChallanModal(prev => ({
            ...prev,
            error: 'End KM must be greater than Start KM'
          }))
          return
        }
      }
    }

    setBulkChallanModal(prev => ({
      ...prev,
      isCreating: true,
      error: ''
    }))

    try {
      // Get order IDs from selected products
      const selectedOrderIds = filteredData
        .filter(product => selectedProducts.includes(product.id))
        .map(product => product.order_id)
        .filter(Boolean) // Remove any null/undefined order IDs

      if (selectedOrderIds.length === 0) {
        throw new Error('No valid orders found for selected products')
      }

      let response = { success: true }; // Default success for non-dispatch statuses
      
      // Only create delivery challans for Dispatch status
      if (selectedStatus === 'Dispatch') {
        console.log('Creating bulk delivery challan for orders:', selectedOrderIds)
        response = await createBulkDeliveryChallan(selectedOrderIds, driverName, vehicleNo)
      }

      if (response?.success) {
        // Update order statuses - BULK API CALL with comma-separated order IDs
        console.log('Updating order statuses to:', selectedStatus);
        console.log('Order IDs to update:', selectedOrderIds);
        
          try {
          const userId = localStorage.getItem("user_id");
          const dispatchPersonName = bulkChallanModal.useCustomDispatchPerson 
            ? bulkChallanModal.customDispatchPerson 
            : bulkChallanModal.dispatchPersonName;
          const resolvedBulkVehicleType = resolveVehicleTypeForPayload(
            bulkChallanModal.orderBy,
            bulkChallanModal.vehicleType
          );
          const ordersForPayload = filteredData.filter(order => selectedProducts.includes(order.id));
          const referenceChallan =
            getOrderChallan(getTruckOrderWithStartKm(ordersForPayload)) ||
            getOrderChallan(ordersForPayload.find((order) => getOrderChallan(order))) ||
            getOrderChallan(ordersForPayload[0]);
          const meterNotWorkingForPayload = isTruthyFlag(
            bulkChallanModal.meter_not_working ?? referenceChallan?.meter_not_working
          );
          const payload = {
            user_id: userId,
            status: selectedStatus,
            order_id: selectedOrderIds.join(','), // Comma-separated order IDs
            emails: bulkChallanModal.emails?.length > 0 ? bulkChallanModal.emails.join(',') : '',
            generateInvoice: bulkChallanModal.generateInvoice,
            driverName: selectedStatus === 'Dispatch' ? bulkChallanModal.driverName : '',
            vehicleNo: selectedStatus === 'Dispatch' ? bulkChallanModal.vehicleNo : '',
            delivery_date: bulkChallanModal.deliveryDate || new Date().toISOString().slice(0,10),
            dispatch_person_name: dispatchPersonName,
            start_km: isTruthyFlag(bulkChallanModal.meter_not_working) ? '' : (bulkChallanModal.start_km || ''),
            ...(selectedStatus === 'Dispatch' && bulkChallanModal.orderBy && { order_by: bulkChallanModal.orderBy }),
            ...(selectedStatus === 'Dispatch' &&
              resolvedBulkVehicleType && { vehicle_type: resolvedBulkVehicleType }),
            ...(selectedStatus === 'Dispatch' && bulkChallanModal.entityName && { order_by_entity: bulkChallanModal.entityName }),
            ...(selectedStatus === 'Dispatch' ? buildDispatchLogisticsPayload(bulkChallanModal) : {}),
            ...(selectedStatus === 'Delivered' && !meterNotWorkingForPayload
              ? { end_km: bulkChallanModal.end_km || '' }
              : {}),
            ...(selectedStatus === 'Delivered'
              ? buildDeliveredLogisticsPayload(bulkChallanModal, referenceChallan)
              : {}),
            ...(selectedStatus === 'Cancelled' && { reason: bulkChallanModal.cancellationReason || '' }),
            ...(selectedStatus === 'Returned' && { reason: bulkChallanModal.returnReason || '' })
          };

          console.log('Bulk status update payload:', payload);

          const receiptFile = bulkChallanModal.diesel_receipt_upload;
          const statusResponse =
            selectedStatus === 'Dispatch' && receiptFile instanceof File
              ? await (() => {
                  const formData = new FormData();
                  Object.entries(payload).forEach(([key, value]) => {
                    if (value === undefined || value === null) return;
                    if (Array.isArray(value)) {
                      formData.append(key, value.join(','));
                      return;
                    }
                    formData.append(
                      key,
                      typeof value === 'boolean' || typeof value === 'number' ? String(value) : value
                    );
                  });
                  formData.append('diesel_receipt_photo', receiptFile, receiptFile.name);
                  return apiService.putMultipart(API_ENDPOINTS.UPDATE_product_status, formData);
                })()
              : await apiService.put(
                  API_ENDPOINTS.UPDATE_product_status,
                  payload,
                  "application/json"
                );
            
          console.log('Bulk status update response:', statusResponse);

          if (statusResponse?.success) {
        setBulkChallanModal({
          open: false,
          driverName: '',
          vehicleNo: '',
          vehicleType: '',
          deliveryDate: new Date().toISOString().slice(0,10),
          generateInvoice: false,
          isCreating: false,
          error: '',
          emails: [],
          emailInput: '',
          selectedStatus: '',
          selectedStatusCategory: '',
          dispatchPersonName: '',
          customDispatchPerson: '',
          useCustomDispatchPerson: false,
          start_km: '',
          end_km: '',
          ...DEFAULT_DISPATCH_LOGISTICS,
          ...DEFAULT_DELIVERED_LOGISTICS,
          cancellationReason: '',
          returnReason: '',
          bulkPaymentStatus: ''
        })
        
        // Clear selections and refresh data
        if (onSelectAll) {
          onSelectAll(false)
        }
        setTimeout(() => {
          onRefresh?.()
        }, 100)
        
        // Toast removed as per user request
          } else {
            throw new Error(statusResponse?.message || 'Failed to update order statuses')
          }
        } catch (statusError) {
          console.error('Error updating order statuses:', statusError);
          throw new Error(statusError?.message || 'Failed to update order statuses');
        }
      } else {
        throw new Error(response?.message || 'Failed to create bulk delivery challan')
      }
    } catch (error) {
      console.error('Error in bulk operation:', error)
      setBulkChallanModal(prev => ({
        ...prev,
        error: error.message || 'Failed to process bulk operation',
        isCreating: false
      }))
    }
  }

  // Close bulk challan modal
  const handleCloseBulkChallanModal = () => {
    setBulkChallanModal({
      open: false,
       orderBy: '',
      entityName: '',
      driverName: '',
      vehicleNo: '',
      vehicleType: '',
      deliveryDate: new Date().toISOString().slice(0,10),
      generateInvoice: false,
      isCreating: false,
      error: '',
      emails: [],
      emailInput: '',
      selectedStatus: '',
      selectedStatusCategory: '',
      dispatchPersonName: '',
      customDispatchPerson: '',
      useCustomDispatchPerson: false,
      start_km: '',
      end_km: '',
      diesel_receipt_upload: null,
      diesel_receipt_upload_error: '',
      ...DEFAULT_DISPATCH_LOGISTICS,
      ...DEFAULT_DELIVERED_LOGISTICS,
      cancellationReason: '',
      returnReason: '',
      bulkPaymentStatus: ''
    })
  }

  // Handle bulk challan input changes
  const handleBulkChallanInputChange = (field, value) => {
    setBulkChallanModal(prev => ({
      ...prev,
      [field]: value,
      error: '' // Clear error when user types
    }))

    // When orderBy changes, fetch entities and clear entityName
    if (field === 'orderBy') {
      setBulkChallanModal(prev => ({
        ...prev,
        orderBy: value,
        entityName: '', // Clear entity name when order by changes
        ...(String(value || '').trim().toLowerCase() !== 'truck'
          ? {
              vehicleType: '',
              start_km: '',
              meter_not_working: false,
              google_maps_km: '',
              diesel_receipt_number: '',
              diesel_amount: '',
              diesel_receipt_upload: null,
              diesel_receipt_upload_error: '',
            }
          : {}),
        error: ''
      }));
      fetchBulkEntitiesByOrderBy(value);
    }
  }

  // Fetch entities for bulk orders based on order_by selection
  const fetchBulkEntitiesByOrderBy = async (orderBy) => {
    if (!orderBy) {
      setBulkEntityOptions([]);
      return;
    }
    
    setLoadingBulkEntities(true);
    try {
      // TODO: Replace with actual API endpoint when available
      // For now, using a mapping based on order_by value
      // Example: const response = await apiService.get(`${API_ENDPOINTS.GET_entities_by_order_by}?order_by=${orderBy}`);
      
      // Mock data - replace with actual API call
      const entityMapping = {
        'indian post': ['Mumbai Branch', 'Delhi Branch', 'Bangalore Branch', 'Chennai Branch'],
        'courier services': ['BlueDart', 'FedEx', 'DTDC', 'First Flight', 'Professional Courier'],
        'name': ['Mumbai Branch', 'Delhi Branch', 'Bangalore Branch', 'Chennai Branch']
      };
      
      const entities = entityMapping[orderBy.toLowerCase()] || [];
      setBulkEntityOptions(entities);
    } catch (error) {
      console.error('Error fetching bulk entities:', error);
      setBulkEntityOptions([]);
    } finally {
      setLoadingBulkEntities(false);
    }
  } 

  // Add email to bulk challan modal
  const addEmailToBulkModal = () => {
    const email = bulkChallanModal.emailInput?.trim() || ''
    if (email && !bulkChallanModal.emails?.includes(email)) {
      setBulkChallanModal(prev => ({
        ...prev,
        emails: [...(prev.emails || []), email],
        emailInput: ''
      }))
    }
  }

  // Remove email from bulk challan modal
  const removeEmailFromBulkModal = (emailToRemove) => {
    setBulkChallanModal(prev => ({
      ...prev,
      emails: (prev.emails || []).filter(email => email !== emailToRemove)
    }))
  }

  // Add email to status update modal
  const addEmailToStatusModal = () => {
    const email = statusUpdateModal.emailInput?.trim() || ''
    if (email && !statusUpdateModal.emails?.includes(email)) {
      setStatusUpdateModal(prev => ({
        ...prev,
        emails: [...(prev.emails || []), email],
        emailInput: ''
      }))
    }
  }

  // Remove email from status update modal
  const removeEmailFromStatusModal = (emailToRemove) => {
    setStatusUpdateModal(prev => ({
      ...prev,
      emails: (prev.emails || []).filter(email => email !== emailToRemove)
    }))
  }

  // Get eligible products for bulk challan (those without existing delivery challan)
  const getEligibleProductsCount = () => {
    return filteredData
      .filter(product => selectedProducts.includes(product.id))
      .filter(product => !product.delivery_challan || !product.delivery_challan.path)
      .length
  }

  const getSelectedProductsCount = () => {
    return filteredData
      .filter(product => selectedProducts.includes(product.id))
      .length
  }

  if (isLoading) {
    const skeletonRowCount = Math.min(Math.max(Number(pageSize) || 10, 5), 10)
    return (
      <Box sx={{ width: "100%", p: 2 }} aria-busy="true" aria-live="polite">
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 600,
            overflowX: "auto",
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#555 #1e1e1e"
                : "#c1c1c1 #f1f1f1",
            "&::-webkit-scrollbar": {
              height: 8,
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              background:
                theme.palette.mode === "dark"
                  ? "#1e1e1e"
                  : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "dark"
                  ? "#555"
                  : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background:
                theme.palette.mode === "dark"
                  ? "#777"
                  : "#a8a8a8",
            },
          }}
        >
          <Table
            stickyHeader
            sx={{
              width: "100%",
              tableLayout: "fixed",
              "& .MuiTableCell-root": {
                verticalAlign: "middle",
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}>
                <StyledTableCell sx={{ width: 50, minWidth: 50, backgroundColor: (theme) => theme.palette.grey[200] }} />
                <StyledTableCell align="center" sx={{ width: 80, minWidth: 80, backgroundColor: (theme) => theme.palette.grey[200] }}>Sr. No.</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 155, minWidth: 155, backgroundColor: (theme) => theme.palette.grey[200] }}>Order ID</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: { xs: 110, sm: 130, md: 150 }, minWidth: 0, maxWidth: { xs: 110, sm: 130, md: 150 }, overflow: "hidden", backgroundColor: (theme) => theme.palette.grey[200] }}>Customer Name</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 200, minWidth: 140, whiteSpace: 'normal', backgroundColor: (theme) => theme.palette.grey[200] }}>Company Name</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 110, minWidth: 100, backgroundColor: (theme) => theme.palette.grey[200] }}>Status</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 90, minWidth: 88, backgroundColor: (theme) => theme.palette.grey[200] }}>Total Amount</StyledTableCell>
                <StyledTableCell align="center" sx={{
                  width: 180,
                  minWidth: 180,
                  position: "sticky",
                  right: 0,
                  zIndex: 5,
                  backgroundColor: (theme) => theme.palette.grey[200],
                }}>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: skeletonRowCount }).map((_, index) => (
                <TableRow key={`bulk-order-skeleton-${index}`} sx={{ pointerEvents: "none" }}>
                  <StyledTableCell sx={{ width: 50, minWidth: 50 }}>
                    <Skeleton animation="wave" variant="rounded" width={18} height={18} />
                  </StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: 80, minWidth: 80 }}>
                    <Skeleton animation="wave" variant="text" width={24} height={24} sx={{ mx: "auto" }} />
                  </StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: 155, minWidth: 155 }}>
                    <Skeleton animation="wave" variant="text" width="80%" height={24} sx={{ mx: "auto" }} />
                  </StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: { xs: 110, sm: 130, md: 150 }, minWidth: 0, maxWidth: { xs: 110, sm: 130, md: 150 } }}>
                    <Skeleton animation="wave" variant="text" width="70%" height={24} sx={{ mx: "auto" }} />
                  </StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: 200, minWidth: 140 }}>
                    <Skeleton animation="wave" variant="text" width="85%" height={24} sx={{ mx: "auto" }} />
                  </StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: 110, minWidth: 100 }}>
                    <Skeleton animation="wave" variant="rounded" width={72} height={24} sx={{ mx: "auto", borderRadius: 4 }} />
                  </StyledTableCell>
                  <StyledTableCell align="center" sx={{ width: 90, minWidth: 88 }}>
                    <Skeleton animation="wave" variant="text" width={56} height={24} sx={{ mx: "auto" }} />
                  </StyledTableCell>
                  <StyledTableCell align="center" sx={{
                    width: 180,
                    minWidth: 180,
                    position: "sticky",
                    right: 0,
                    backgroundColor: "background.paper",
                  }}>
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                      <Skeleton animation="wave" variant="circular" width={28} height={28} />
                      <Skeleton animation="wave" variant="circular" width={28} height={28} />
                      <Skeleton animation="wave" variant="circular" width={28} height={28} />
                    </Box>
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    )
  }

  if (bulkProducts.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="h6" color="textSecondary">
          No bulk products found
        </Typography>
        <Button 
          variant="outlined" 
          color="primary" 
          onClick={onRefresh}
          startIcon={<UpdateIcon />}
          sx={{ mt: 2 }}
        >
          Refresh
        </Button>
      </Box>
    )
  }


  return (
    <Box sx={{ width: "100%", p: 2 }}>
      {/* Export Controls */}
      {selectedProducts.length > 0 && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {selectedProducts.length} product{selectedProducts.length > 1 ? 's' : ''} selected
            </Typography>
            {getEligibleProductsCount() > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: 'block' }}>
                {getEligibleProductsCount()} item Selected
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Bulk Payment Status Update Button - Show prominently in Confirm tab */}
           
            <Button
              variant="contained"
              size="small"
              startIcon={<FileDownload />}
              onClick={openActionsMenu}
              sx={{ textTransform: 'none' }}
            >
              Actions
            </Button>
            <Menu
              anchorEl={actionsAnchorEl}
              open={isActionsMenuOpen}
              onClose={closeActionsMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <ListSubheader disableSticky>Export</ListSubheader>
              <MenuItem onClick={() => { closeActionsMenu(); onExport && onExport('excel'); }}>
                <ListItemIcon>
                  <FileDownload fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Export to Excel" />
              </MenuItem>
              <MenuItem onClick={() => { closeActionsMenu(); onExport && onExport('csv'); }}>
                <ListItemIcon>
                  <FileDownload fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Export to CSV" />
              </MenuItem>
              <Divider />
              <ListSubheader disableSticky>Documents ZIP</ListSubheader>
              <MenuItem 
                onClick={() => { closeActionsMenu(); onZipDownload && onZipDownload('invoice'); }}
                disabled={shouldDisableDocumentsAndEmail() || (statusFilter || '').toLowerCase() === 'dispatch'}
              >
                <ListItemIcon>
                  <ReceiptIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Invoices ZIP" />
              </MenuItem>
              <MenuItem 
                onClick={() => { closeActionsMenu(); onZipDownload && onZipDownload('challan'); }}
                disabled={shouldDisableDocumentsAndEmail()}
              >
                <ListItemIcon>
                  <ChallanIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Challans ZIP" />
              </MenuItem>
              <MenuItem 
                onClick={() => { closeActionsMenu(); onZipDownload && onZipDownload('both'); }}
                disabled={shouldDisableDocumentsAndEmail() || (statusFilter || '').toLowerCase() === 'dispatch'}
              >
                <ListItemIcon>
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Both ZIP" />
              </MenuItem>
              <Divider />
              <ListSubheader disableSticky>Email</ListSubheader>
              <MenuItem 
                onClick={handleOpenEmailDialog}
                disabled={shouldDisableDocumentsAndEmail()}
              >
                <ListItemIcon>
                  <EmailIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Send via Email" />
              </MenuItem>
              <Divider />
              <ListSubheader disableSticky>Operations</ListSubheader>
              <MenuItem
                onClick={handleOpenBulkPaymentStatusModal}
                disabled={selectedProducts.length === 0 || isBulkPaymentUpdateDisabled()}
              >
                <ListItemIcon>
                  <Payment fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Bulk Payment Status Update" />
              </MenuItem>
              <MenuItem
                onClick={() => { closeActionsMenu(); handleBulkDeliveryChallan(); }}
                disabled={
                  selectedProducts.length === 0 || 
                  ['Returned', 'Cancelled'].includes(statusFilter)
                }
              >
                <ListItemIcon>
                  <DeliveryIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={statusFilter === 'Dispatch' ? 'Status Update' : 'Bulk Delivery Status'} />
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ maxHeight: 600, overflowX: "auto",
          /* Firefox */
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#555 #1e1e1e"
                : "#c1c1c1 #f1f1f1",

            /* Chrome, Edge, Safari */
            "&::-webkit-scrollbar": {
              height: 8,
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              background:
                theme.palette.mode === "dark"
                  ? "#1e1e1e"
                  : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "dark"
                  ? "#555"
                  : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background:
                theme.palette.mode === "dark"
                  ? "#777"
                  : "#a8a8a8",
            },
       }}>
        <Table
          stickyHeader
          sx={{
            width: "100%",
            tableLayout: "fixed",
            "& .MuiTableCell-root": {
              verticalAlign: "middle",
            },
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}>
              <StyledTableCell sx={{ width: 50, minWidth: 50, backgroundColor: (theme) => theme.palette.grey[200] }}>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={selectedProducts.length > 0 && !isAllSelected}
                  onChange={handleSelectAllToggle}
                  color="primary"
                />
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ width: 80, minWidth: 80, backgroundColor: (theme) => theme.palette.grey[200] }}>Sr. No.</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: 155, minWidth: 155, backgroundColor: (theme) => theme.palette.grey[200] }}>Order ID</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: { xs: 110, sm: 130, md: 150 }, minWidth: 0, maxWidth: { xs: 110, sm: 130, md: 150 }, overflow: "hidden", backgroundColor: (theme) => theme.palette.grey[200] }}>Customer Name</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: 200, minWidth: 140, whiteSpace: 'normal', backgroundColor: (theme) => theme.palette.grey[200] }}>Company Name</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: 110, minWidth: 100, backgroundColor: (theme) => theme.palette.grey[200] }}>Status</StyledTableCell>
              <StyledTableCell align="center" sx={{ width: 90, minWidth: 88, backgroundColor: (theme) => theme.palette.grey[200] }}>Total Amount</StyledTableCell>
              <StyledTableCell align="center" sx={{
              width: 180,
              minWidth: 180,
              position: "sticky",
              right: 0,
              zIndex: 5,
              backgroundColor: (theme) => theme.palette.grey[200], // 🔑 ensures no overlap
            }}>Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ border: 'none' }}>
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                    <Typography color="text.secondary">
                      No Products found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((product, index) => {
                const isSelected = isProductSelected(product.id)
                const lastOpenedId = getLastOpenedBulkOrderId()
                const isLastOpened =
                  !!lastOpenedId &&
                  (String(product.order_id) === String(lastOpenedId) ||
                    String(product.id) === String(lastOpenedId))
                
                return (
                  <React.Fragment key={product.id}>
                    <StyledTableRow
                      hover
                      selected={isSelected}
                      highlighted={isLastOpened ? 1 : 0}
                      onClick={() => openOrderDetails(product)}
                    >
                      <StyledTableCell
                        onClick={(e) => e.stopPropagation()}
                        sx={{ width: 50, minWidth: 50 }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => handleProductSelection(product.id, e.target.checked)}
                          color="primary"
                          size="small"
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center" sx={{ width: 80, minWidth: 80 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {(currentPage - 1) * pageSize + index + 1}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center" sx={{ width: 155, minWidth: 155 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openOrderDetails(product);
                            }}
                            sx={{
                              p: 0.25,
                            }}
                          >
                            <KeyboardArrowRightIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="body2" style={{ fontSize: "14px" }} fontWeight={500}>
                            {product.order_id}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          {product.order_date_formatted || (product.created_at ? new Date(product.created_at).toLocaleDateString() : "Date N/A")}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell
                        align="center"
                        sx={{
                          width: { xs: 110, sm: 130, md: 150 },
                          minWidth: 0,
                          maxWidth: { xs: 110, sm: 130, md: 150 },
                          overflow: "hidden",
                        }}
                      >
                        <Tooltip
                          title={String(product.customer_name || "").length > 37 ? product.customer_name : ""}
                          arrow
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              display: "block",
                              width: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: String(product.customer_name || "").length > 37 ? "pointer" : "default",
                            }}
                          >
                            {String(product.customer_name || "").length > 37
                              ? `${String(product.customer_name).slice(0, 37)}...`
                              : product.customer_name}
                          </Typography>
                        </Tooltip>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          noWrap
                          sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {product.mobile_number}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center" sx={{ width: 200, minWidth: 140, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        <Typography variant="body2">
                          {product.company_name}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell align="center" sx={{ width: 110, minWidth: 100 }}>
                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <StatusChip 
                            status={product.status_category}
                            component="div"
                          >
                            {product.status_name || (product.status ? (product.status.charAt(0).toUpperCase() + product.status.slice(1)) : '')}
                          </StatusChip>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell align="center" sx={{ width: 90, minWidth: 88 }}>
                        {formatBulkDetailCurrency(product.order?.total_amount)}
                      </StyledTableCell>
                      <StyledTableCell
                        align="center"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                        width: 180,
                        minWidth: 180,
                        position: "sticky",
                        right: 0,
                        zIndex: 4,
                        backgroundColor: theme.palette.background.paper,
                      }}>
                        <Box display="flex" gap={1} sx={{ justifyContent: "center", alignItems: "center", width: "100%" }}>
                         
                          <Tooltip
                            title={
                              ['cancelled', 'returned'].includes((statusFilter || '').toLowerCase())
                                ? 'Status update is not available on Cancelled or Returned tabs'
                                : !shouldEnableButton?.('update_status')
                                  ? "You don't have permission to update status"
                                  : "Update Status"
                            }
                          >
  <span>
    <IconButton 
      size="small"
      onClick={(e) => {
        e.stopPropagation()
        handleOpenStatusModal(product)
      }}
      disabled={
        !shouldEnableButton?.('update_status') ||
        ['cancelled', 'returned'].includes((statusFilter || '').toLowerCase())
      }
      sx={{
        opacity:
          !shouldEnableButton?.('update_status') ||
          ['cancelled', 'returned'].includes((statusFilter || '').toLowerCase())
            ? 0.5
            : 1
      }}
    >
      <UpdateIcon fontSize="small" />
    </IconButton>
  </span>
</Tooltip>

<Tooltip title={getPaymentUpdateTooltip(product)}>
  <span>
    <IconButton 
      size="small"
      onClick={(e) => {
        e.stopPropagation()
        openPaymentModalForProduct(product)
      }}
      disabled={
        !shouldEnableButton?.('update_payment') ||
        isPaymentUpdateDisabled(product)
      }
      sx={{
        opacity:
          !shouldEnableButton?.('update_payment') ||
          isPaymentUpdateDisabled(product)
            ? 0.5
            : 1,
      }}
    >
      <Payment fontSize="small" />
    </IconButton>
  </span>
</Tooltip>

{/* Send Email — always takes up space, hidden on placed/confirmed/processing */}
<span style={{
  visibility: ['placed', 'confirmed', 'processing'].includes((statusFilter || '').toLowerCase())
    ? 'hidden' : 'visible'
}}>
  <Tooltip title={!shouldEnableButton?.('send_email') ? "You don't have permission to send email" : "Send Email"}>
    <span>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          openSingleOrderEmail(product)
        }}
        disabled={!shouldEnableButton?.('send_email')}
        sx={{ opacity: !shouldEnableButton?.('send_email') ? 0.5 : 1 }}
      >
        <EmailIcon fontSize="small" />
      </IconButton>
    </span>
  </Tooltip>
</span>

{!['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase()) &&
  product.invoice &&
  product.invoice.path && (
  <Tooltip title={!shouldEnableButton?.('view_invoice') ? "You don't have permission to view invoice" : "View Invoice"}>
    <span>
      <IconButton 
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          handleViewInvoice(product)
        }}
        disabled={!shouldEnableButton?.('view_invoice')}
        color="primary"
        sx={{ opacity: !shouldEnableButton?.('view_invoice') ? 0.5 : 1 }}
      >
        <ReceiptIcon fontSize="small" />
      </IconButton>
    </span>
  </Tooltip>
)}

{product.delivery_challan && product.delivery_challan.path && (
  <Tooltip title={!shouldEnableButton?.('view_delivery_challan') ? "You don't have permission to view delivery challan" : "View Delivery Challan"}>
    <span>
      <IconButton 
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          handleViewDeliveryChallan(product)
        }}
        disabled={!shouldEnableButton?.('view_delivery_challan')}
        color="secondary"
        sx={{ opacity: !shouldEnableButton?.('view_delivery_challan') ? 0.5 : 1 }}
      >
        <ChallanIcon fontSize="small" />
      </IconButton>
    </span>
  </Tooltip>
)}
                        </Box>
                      </StyledTableCell>
                      </StyledTableRow>
                    </React.Fragment>
                )
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Enhanced Status Update Modal */}
      <Dialog 
        open={statusUpdateModal.open} 
        onClose={handleCloseStatusModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <UpdateIcon color="primary" />
            <Typography variant="h6">Update Order Status</Typography>
          </Box>
          {statusUpdateModal.order && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Order ID: {statusUpdateModal.order.order_id}
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent
        sx={{ /* Firefox */
          scrollbarWidth: "thin",
          scrollbarColor:
            theme.palette.mode === "dark"
              ? "#555 #1e1e1e"
              : "#c1c1c1 #f1f1f1",
          /* Chrome, Edge, Safari */
          "&::-webkit-scrollbar": {
            width: 8,
          },
          "&::-webkit-scrollbar-track": {
            background:
              theme.palette.mode === "dark" ? "#1e1e1e" : "#f1f1f1",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            background:
              theme.palette.mode === "dark" ? "#555" : "#c1c1c1",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background:
              theme.palette.mode === "dark" ? "#777" : "#a8a8a8",
          },}}>
          {statusUpdateModal.challanError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {statusUpdateModal.challanError}
            </Alert>
          )}

          {statusUpdateModal.isCreatingChallan && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Creating delivery challan...
              </Box>
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Order Status</InputLabel>
          <Select
            value={statusUpdateModal.order?.status_name || ''}
            label="Order Status"
            onChange={(e) => {
              const selectedOption = statusOptions.find(opt => opt.name === e.target.value);
              handleStatusModalChange('order', { 
                ...statusUpdateModal.order, 
                status_name: e.target.value,
                status: (e.target.value || '').toString().toLowerCase(),
                status_category: selectedOption?.category || ''
              });
            }}
            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
          >
            {statusOptions.map((option) => {
              // Use original status for validation, not the auto-selected next status
              // This ensures we only show the next sequential status, not skip ahead
              const originalStatusCategory = (statusUpdateModal.originalStatus?.category || '').toLowerCase();
              const originalStatusName = statusUpdateModal.originalStatus?.name || '';
              
              // Get current status category and name - try multiple sources (for display)
              let currentStatusCategory = (statusUpdateModal.order?.status_category || '').toLowerCase();
              const currentStatusName = statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name || '';
              
              // If category not available, find it from statusOptions by name
              if (!currentStatusCategory && currentStatusName) {
                const currentStatusOption = statusOptions.find(opt => opt.name === currentStatusName);
                currentStatusCategory = currentStatusOption?.category?.toLowerCase() || '';
              }
              
              // Get statuses to disable based on ORIGINAL status (not the auto-selected one)
              const statusesToDisable = getStatusesToDisable(originalStatusCategory, originalStatusName);
              
              // Check payment status - Outstanding/Credit payment allows orders to proceed
              const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
              const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding' || paymentStatus === 'credit';
              
              // If not paid/outstanding/credit, only allow "Orders" status (name: "Orders")
              const isOrdersStatus = option.name === 'Orders';
              const shouldDisableByPayment = !isPaid && !isOrdersStatus;
              
              // Check if this option should be disabled
              const optionCategory = (option.category || '').toLowerCase();
              // Only disable if category is valid and in the disabled list
              const shouldDisableBySequence = optionCategory && statusesToDisable.includes(optionCategory);
              const shouldDisable = shouldDisableByPayment || shouldDisableBySequence;

              // Dispatch tab: hide Cancelled option
              const isCancelledOption = (option.name || '').toLowerCase() === 'cancelled';
              const isDispatchTab = (statusFilter || '').toLowerCase() === 'dispatch';
              if (isDispatchTab && isCancelledOption) return null;

              if (shouldDisable) return null;
              
              return (
                <MenuItem 
                  key={option.id || option.name} 
                  value={option.name}
                  disabled={shouldDisable || updatingStatus || statusUpdateModal.isCreatingChallan}
                  sx={shouldDisable ? { 
                    opacity: 0.5,
                    backgroundColor: theme.palette.grey[100],
                    '&:hover': {
                      backgroundColor: theme.palette.grey[100],
                      cursor: 'not-allowed'
                    }
                  } : {}}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {getCategoryIcon(option.category)}
                    <StatusChip 
                      status={option.category}
                      component="span"
                    >
                      {option.name}
                    </StatusChip>
                    {statusUpdateModal.order?.status_name === option.name && (
                      <CheckCircle 
                        color="primary" 
                        sx={{ 
                          ml: 'auto', 
                          fontSize: '16px' 
                        }} 
                      />
                    )}
                    {shouldDisableByPayment && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                        Payment required
                      </Typography>
                    )}
                    {shouldDisableBySequence && !shouldDisableByPayment && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                        Sequential only
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

          {/* Payment Status Warning - Outstanding payment is allowed */}
          {statusUpdateModal.order && (() => {
            const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
            const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding' || paymentStatus === 'credit';
            const selectedStatus = statusUpdateModal.order?.status_name || '';
            const isOrdersStatus = selectedStatus === 'Orders';
            
            // Only show warning if payment is not paid/outstanding/credit AND status is not "Orders"
            if (!isPaid && !isOrdersStatus) {
              return (
                <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Payment Required:</strong> This order must be paid before it can be moved to any status other than "Orders". Please update the payment status first.
                  </Typography>
                </Alert>
              );
            }
            return null;
          })()}

          {/* Conditional fields for shipping */}
          {(() => {
            const selectedStatusName = statusUpdateModal.order?.status_name || '';
            const selectedOption = statusOptions.find(opt => opt.name === selectedStatusName);
            const statusCategoryForShipping = (selectedOption?.category || '').toLowerCase();
            return statusCategoryForShipping === 'shipped';
          })() && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ 
                p: 2, 
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                borderRadius: 2,
                mb: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <DeliveryIcon color="info" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Delivery Information Required
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Please provide delivery details to create the delivery challan
                </Typography>
              </Box>

                              <Grid container spacing={2}>
                

               <Grid item xs={12}>
                                <FormControl fullWidth required>
                                  <InputLabel>Order By</InputLabel>
                                  <Select
                                    value={statusUpdateModal.orderBy}
                                    onChange={(e) => handleStatusModalChange('orderBy', e.target.value)}
                                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                                    label="Order By"
                                  >
                                    <MenuItem value="">
                                      <em>Select order by</em>
                                    </MenuItem>
                                    {orderByChoices.map((choice) => (
                                      <MenuItem key={choice.key} value={choice.key}>
                                        {choice.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
              
                               {/* Entity Name */}
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Delivery Partner Name"
                                value={statusUpdateModal.entityName}
                                onChange={(e) => {
                                  const lettersOnly = e.target.value.replace(/[^A-Za-z ]/g, '');
                                  handleStatusModalChange('entityName', lettersOnly);
                                }}
                                disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                                required
                                placeholder="Enter delivery partner name"
                              />
                            </Grid>
                                                      

                            <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Driver/Authorized Name"
                          value={statusUpdateModal.driverName}
                          onChange={(e) => {
                            const lettersOnly = e.target.value.replace(/[^A-Za-z ]/g, '');
                            handleStatusModalChange('driverName', lettersOnly);
                          }}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          required
                          placeholder="Driver/Authorized Name"
                          error={statusUpdateModal.challanError.includes('Driver name')}
                        />
                      </Grid>
                {String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Vehicle Type</InputLabel>
                      <Select
                        value={statusUpdateModal.vehicleType || ''}
                        label="Vehicle Type"
                        onChange={(e) => handleStatusModalChange('vehicleType', e.target.value)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      >
                        <MenuItem value="">
                          <em>Select vehicle type</em>
                        </MenuItem>
                        {vehicleTypes.map((type) => (
                          <MenuItem key={type.id} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Vehicle No / Reference No"
                    value={statusUpdateModal.vehicleNo}
                    onChange={(e) => {
                      handleStatusModalChange(
                        'vehicleNo',
                        e.target.value.replace(/[^a-zA-Z0-9]/g, '')
                      );
                    }}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    required
                    placeholder="Vehicle No / Reference No"
                    error={
                      !!(statusUpdateModal.challanError && statusUpdateModal.challanError.includes('vehicle number'))
                    }
                    helperText=""
                  />
                </Grid>
                  
                  {['truck', 'by truck'].includes(String(statusUpdateModal.orderBy || '').trim().toLowerCase()) && (
                    <>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isTruthyFlag(statusUpdateModal.meter_not_working)}
                              onChange={(e) => {
                                handleStatusModalChange('meter_not_working', e.target.checked);
                                if (e.target.checked) {
                                  handleStatusModalChange('start_km', '');
                                } else {
                                  handleStatusModalChange('google_maps_km', '');
                                }
                              }}
                              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                            />
                          }
                          label="Meter not working"
                        />
                      </Grid>

                      {!isTruthyFlag(statusUpdateModal.meter_not_working) && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Start KM"
                            type="number"
                            value={statusUpdateModal.start_km}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^[1-9]\d*$/.test(value)) {
                                handleStatusModalChange('start_km', value);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                            required={(statusFilter || '').toLowerCase() === 'processing'}
                            placeholder="Enter starting kilometer reading"
                            inputProps={{
                              min: 1,
                              step: 1,
                              onWheel: (e) => e.currentTarget.blur(),
                            }}
                          />
                        </Grid>
                      )}

                      {isTruthyFlag(statusUpdateModal.meter_not_working) && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            required
                            label="Google Maps KM (round trip)"
                            type="number"
                            value={statusUpdateModal.google_maps_km || ''}
                            onChange={(e) => handleStatusModalChange('google_maps_km', e.target.value)}
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                            placeholder="Enter Google Maps KM"
                            inputProps={{
                              min: 0.01,
                              step: 'any',
                              onWheel: (e) => e.currentTarget.blur(),
                            }}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Diesel Receipt Number"
                          value={statusUpdateModal.diesel_receipt_number || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^[a-zA-Z0-9]+$/.test(value)) {
                              handleStatusModalChange('diesel_receipt_number', value);
                            }
                          }}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          placeholder="Enter diesel receipt number"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.75 }}>
                          Receipt Upload
                          <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
                            *
                          </Box>
                        </Typography>
                        <input
                          id="bulk-single-diesel-receipt-camera-input"
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                          capture="environment"
                          style={{ display: 'none' }}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            e.target.value = '';
                            if (!file) return;
                            if (!file.type.startsWith('image/') && !/\.(jpe?g|png|gif|webp|bmp)$/i.test(file.name || '')) {
                              setStatusUpdateModal((prev) => ({
                                ...prev,
                                diesel_receipt_upload: null,
                                diesel_receipt_upload_error: 'Only image files are allowed for camera capture.',
                                challanError: '',
                              }));
                              return;
                            }
                            if (file.size > 5 * 1024 * 1024) {
                              setStatusUpdateModal((prev) => ({
                                ...prev,
                                diesel_receipt_upload: null,
                                diesel_receipt_upload_error: 'File size must not exceed 5 MB.',
                                challanError: '',
                              }));
                              return;
                            }
                            setStatusUpdateModal((prev) => ({
                              ...prev,
                              diesel_receipt_upload: file,
                              diesel_receipt_upload_error: '',
                              challanError: '',
                            }));
                          }}
                        />
                        <input
                          id="bulk-single-diesel-receipt-file-input"
                          type="file"
                          accept="image/*,.pdf,application/pdf"
                          style={{ display: 'none' }}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            e.target.value = '';
                            if (!file) return;
                            const isImageOrPdf =
                              file.type.startsWith('image/') ||
                              file.type === 'application/pdf' ||
                              /\.(pdf|jpe?g|png|gif|webp|bmp)$/i.test(file.name || '');
                            if (!isImageOrPdf) {
                              setStatusUpdateModal((prev) => ({
                                ...prev,
                                diesel_receipt_upload: null,
                                diesel_receipt_upload_error: 'Only image or PDF files are allowed.',
                                challanError: '',
                              }));
                              return;
                            }
                            if (file.size > 5 * 1024 * 1024) {
                              setStatusUpdateModal((prev) => ({
                                ...prev,
                                diesel_receipt_upload: null,
                                diesel_receipt_upload_error: 'File size must not exceed 5 MB.',
                                challanError: '',
                              }));
                              return;
                            }
                            setStatusUpdateModal((prev) => ({
                              ...prev,
                              diesel_receipt_upload: file,
                              diesel_receipt_upload_error: '',
                              challanError: '',
                            }));
                          }}
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                          <Button
                            variant="outlined"
                            component="label"
                            htmlFor="bulk-single-diesel-receipt-camera-input"
                            startIcon={<CameraAltIcon />}
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                            sx={{ textTransform: 'none' }}
                          >
                            Take Photo
                          </Button>
                          <Button
                            variant="outlined"
                            component="label"
                            htmlFor="bulk-single-diesel-receipt-file-input"
                            startIcon={<UploadFileIcon />}
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                            sx={{ textTransform: 'none' }}
                          >
                            Upload from Device
                          </Button>
                          {statusUpdateModal.diesel_receipt_upload && (
                            <Button
                              variant="text"
                              color="inherit"
                              startIcon={<CloseIcon />}
                              disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                              onClick={() => {
                                setStatusUpdateModal((prev) => ({
                                  ...prev,
                                  diesel_receipt_upload: null,
                                  diesel_receipt_upload_error: '',
                                  challanError: '',
                                }));
                              }}
                              sx={{ textTransform: 'none' }}
                            >
                              Clear
                            </Button>
                          )}
                        </Stack>
                        {statusUpdateModal.diesel_receipt_upload_error && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            {statusUpdateModal.diesel_receipt_upload_error}
                          </Typography>
                        )}
                        {statusUpdateModal.diesel_receipt_upload && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Selected: {statusUpdateModal.diesel_receipt_upload.name}
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Diesel Amount"
                          type="number"
                          value={statusUpdateModal.diesel_amount || ''}
                          onChange={(e) => handleStatusModalChange('diesel_amount', e.target.value)}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          placeholder="Enter diesel amount"
                          inputProps={{
                            min: 0,
                            step: 'any',
                            onWheel: (e) => e.currentTarget.blur(),
                          }}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Crates Dispatched"
                      type="number"
                      value={statusUpdateModal.crates_dispatched ?? '0'}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          handleStatusModalChange('crates_dispatched', value);
                        }
                      }}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      placeholder="0"
                      inputProps={{
                        min: 0,
                        step: 1,
                        onWheel: (e) => e.currentTarget.blur(),
                      }}
                    />
                  </Grid>

                  {(statusFilter || '').toLowerCase() === 'processing' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Extra Plants"
                        type="number"
                        value={statusUpdateModal.extra_plants ?? '0'}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            handleStatusModalChange('extra_plants', value);
                          }
                        }}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        placeholder="0"
                        inputProps={{
                          min: 0,
                          step: 1,
                          onWheel: (e) => e.currentTarget.blur(),
                        }}
                      />
                    </Grid>
                  )}

                <Grid item xs={12}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={statusUpdateModal.useCustomDispatchPerson}
                          onChange={(e) => handleStatusModalChange('useCustomDispatchPerson', e.target.checked)}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        />
                      }
                      label="Add custom dispatch person"
                    />
                    {statusUpdateModal.useCustomDispatchPerson ? (
                      <TextField
                        fullWidth
                        label="Custom Dispatch Person Name"
                        value={statusUpdateModal.customDispatchPerson}
                        onChange={(e) => handleStatusModalChange('customDispatchPerson', e.target.value)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        required
                        placeholder="Enter custom dispatch person name"
                        sx={{ mt: 1 }}
                      />
                    ) : (
                      <FormControl fullWidth required sx={{ mt: 1 }}>
                        <InputLabel>Dispatch Person Name</InputLabel>
                        <Select
                          value={statusUpdateModal.dispatchPersonName}
                          label="Dispatch Person Name"
                          onChange={(e) => handleStatusModalChange('dispatchPersonName', e.target.value)}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        >
                          <MenuItem value="">
                            <em>Select dispatch person</em>
                          </MenuItem>
                          {dispatchPersons.map((person) => (
                            <MenuItem key={person.id} value={person.name}>
                              {person.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Delivery Date"
                    type="date"
                    value={statusUpdateModal.shippedDate}
                    onChange={(e) => handleStatusModalChange('shippedDate', e.target.value)}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    required
                    InputLabelProps={{ shrink: true }}
                      inputProps={{
                    min: new Date().toISOString().split("T")[0], // 🔑 disables all past dates
                  }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Email Addresses (Optional)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={statusUpdateModal.emailInput}
                        onChange={(e) => handleStatusModalChange('emailInput', e.target.value)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        placeholder="Enter email address"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addEmailToStatusModal();
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={addEmailToStatusModal}
                        disabled={!statusUpdateModal.emailInput?.trim() || updatingStatus || statusUpdateModal.isCreatingChallan}
                      >
                        Add
                      </Button>
                    </Box>
                    {statusUpdateModal.emails?.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {statusUpdateModal.emails?.map((email, index) => (
                          <Chip
                            key={index}
                            label={email}
                            onDelete={() => removeEmailFromStatusModal(email)}
                            size="small"
                            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          />
                        ))}
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Delivery challan will be sent to these email addresses
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={statusUpdateModal.generateInvoice}
                        onChange={(e) => handleStatusModalChange('generateInvoice', e.target.checked)}
                        disabled={
                          updatingStatus ||
                          statusUpdateModal.isCreatingChallan ||
                          (['processing'].includes((statusFilter || '').toLowerCase()) &&
                            !shouldEnableButton?.('generate_invoice_along_with_delivery_challan'))
                        }
                      />
                    }
                    label="Generate Invoice along with Delivery Challan"
                  />
                </Grid>
              </Grid>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                * A delivery challan will be created automatically with these details
                {statusUpdateModal.generateInvoice && (
                  <><br />* An invoice will also be generated for this order.</>
                )}
              </Typography>
            </Box>
          )}

          {(() => {
            const order = statusUpdateModal.order;
            const orderChallan = getOrderChallan(order);
            const isDeliveredSelected =
              (
                order?.status?.name ||
                order?.status_name ||
                ''
              ).toLowerCase() === 'delivered';
            const isDispatchTab = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());

            if (!isDeliveredSelected) return null;

            const meterNotWorking = isTruthyFlag(
              statusUpdateModal.meter_not_working ?? orderChallan?.meter_not_working
            );
            const googleMapsKm =
              statusUpdateModal.google_maps_km ||
              (orderChallan?.google_maps_km !== null && orderChallan?.google_maps_km !== undefined
                ? String(orderChallan.google_maps_km)
                : '');
            const cratesDispatched = Number(
              statusUpdateModal.crates_dispatched !== undefined &&
                String(statusUpdateModal.crates_dispatched).trim() !== ''
                ? statusUpdateModal.crates_dispatched
                : (orderChallan?.crates_dispatched ?? 0)
            );
            const cratesReturned = Number(statusUpdateModal.crates_returned);
            const plantsReturnedRaw = String(statusUpdateModal.plants_returned ?? '').trim();
            const plantsReturned = plantsReturnedRaw === '' ? 0 : Number(plantsReturnedRaw);
            const finalKm = Number(statusUpdateModal.final_km);
            const mapsKmNumber = Number(googleMapsKm);
            const showKmExcessReason =
              meterNotWorking &&
              String(statusUpdateModal.final_km || '').trim() !== '' &&
              !Number.isNaN(finalKm) &&
              !Number.isNaN(mapsKmNumber) &&
              finalKm !== mapsKmNumber;
            const showCratesShortReason =
              String(statusUpdateModal.crates_returned || '').trim() !== '';
            const showPlantsReason = plantsReturned > 0;

            const startKmValue =
              statusUpdateModal.start_km ||
              (
                orderChallan?.start_km !== null && orderChallan?.start_km !== undefined
                  ? String(orderChallan.start_km)
                  : ''
              );
            const hasStartKm = String(startKmValue).trim() !== '';
            const isTruckOrder = isOrderByTruck(order);
            const showEndKmSection =
              !meterNotWorking &&
              hasStartKm &&
              ((isDispatchTab && isTruckOrder) || (!isDispatchTab && isTruckOrder) || (!isDispatchTab && hasStartKm));

            const driverName =
              statusUpdateModal.driverName ||
              orderChallan?.driver_name ||
              '';
            const vehicleNo =
              statusUpdateModal.vehicleNo ||
              orderChallan?.vehicle_number ||
              orderChallan?.vehicle_no ||
              orderChallan?.vehicle_name ||
              '';

            return (
              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  {showEndKmSection && (
                    <>
                      {(isTruckOrder || isDispatchTab) && (
                        <>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Driver Name"
                              value={driverName}
                              disabled
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Vehicle No / Reference No"
                              value={vehicleNo}
                              disabled
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Start KM"
                              value={startKmValue}
                              disabled
                              InputProps={{ readOnly: true }}
                            />
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12} md={(isTruckOrder || isDispatchTab) ? 6 : 12}>
                        <TextField
                          fullWidth
                          required
                          label="End KM"
                          type="number"
                          value={statusUpdateModal.end_km}
                          onChange={(e) => {
                            handleStatusModalChange('end_km', e.target.value);
                          }}
                          disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                          placeholder="Enter ending kilometer reading"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            onWheel: (e) => e.currentTarget.blur(),
                          }}
                          sx={{
                            '& .MuiFormLabel-asterisk': { color: 'error.main' },
                          }}
                          error={(() => {
                            const startKm = parseFloat(startKmValue);
                            const endKm = parseFloat(statusUpdateModal.end_km);
                            const hasEndKm = String(statusUpdateModal.end_km || '').trim() !== '';
                            return !hasEndKm || (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm);
                          })()}
                          helperText={(() => {
                            const startKm = parseFloat(startKmValue);
                            const endKm = parseFloat(statusUpdateModal.end_km);
                            const hasEndKm = String(statusUpdateModal.end_km || '').trim() !== '';
                            if (!hasEndKm) return 'End KM is required';
                            if (!isNaN(startKm) && !isNaN(endKm) && endKm <= startKm) {
                              return 'End KM must be greater than Start KM';
                            }
                            return '';
                          })()}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Delivery Logistics
                    </Typography>
                  </Grid>

                  {isTruckOrder && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Meter not Working"
                          value={meterNotWorking ? 'Yes' : 'No'}
                          disabled
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Google Maps KM"
                          value={googleMapsKm || '—'}
                          disabled
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Crates Dispatched"
                      value={String(cratesDispatched)}
                      disabled
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  {isTruckOrder && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Diesel Receipt Number"
                          value={
                            statusUpdateModal.diesel_receipt_number ||
                            orderChallan?.diesel_receipt_number ||
                            '—'
                          }
                          disabled
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Diesel Amount"
                          value={
                            statusUpdateModal.diesel_amount ||
                            (orderChallan?.diesel_amount !== null &&
                            orderChallan?.diesel_amount !== undefined
                              ? String(orderChallan.diesel_amount)
                              : '—')
                          }
                          disabled
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </>
                  )}

                  {meterNotWorking && (
                   <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Final KM"
                      type="number"
                      value={statusUpdateModal.final_km}
                      onChange={(e) => handleStatusModalChange('final_km', e.target.value)}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      placeholder="Enter actual trip KM"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: 0,
                        step: 'any',
                        onWheel: (e) => {
                          e.preventDefault();
                          e.currentTarget.blur();
                        },
                      }}
                      sx={{
                        '& .MuiFormLabel-asterisk': { color: 'error.main' },
                      }}
                    />
                 </Grid>
                  )}

                  {showKmExcessReason && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label="KM Excess Reason"
                        value={statusUpdateModal.km_excess_reason}
                        onChange={(e) => handleStatusModalChange('km_excess_reason', e.target.value)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        placeholder="Why KM exceeded Maps estimate"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiFormLabel-asterisk': { color: 'error.main' },
                        }}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Crates Returned"
                      type="number"
                      value={statusUpdateModal.crates_returned}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          handleStatusModalChange('crates_returned', value);
                        }
                      }}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      placeholder="Enter crates returned"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: 0,
                        step: 1,
                        onWheel: (e) => e.currentTarget.blur(),
                      }}
                      sx={{
                        '& .MuiFormLabel-asterisk': { color: 'error.main' },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Crates Return Person"
                      value={statusUpdateModal.crates_returned_person || ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                        handleStatusModalChange('crates_returned_person', value);
                      }}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      placeholder="Enter crates return person name"
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiFormLabel-asterisk': { color: 'error.main' },
                      }}
                    />
                  </Grid>

                  {showCratesShortReason && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label="Crates Short Reason"
                        value={statusUpdateModal.crates_short_reason}
                        onChange={(e) => handleStatusModalChange('crates_short_reason', e.target.value)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        placeholder="Why returned crates are less than dispatched"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiFormLabel-asterisk': { color: 'error.main' },
                        }}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Plants Returned"
                      type="number"
                      value={statusUpdateModal.plants_returned}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          handleStatusModalChange('plants_returned', value);
                        }
                      }}
                      disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                      placeholder="0"
                      inputProps={{
                        min: 0,
                        step: 1,
                        onWheel: (e) => e.currentTarget.blur(),
                      }}
                    />
                  </Grid>

                  {showPlantsReason && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label="Plants Returned Reason"
                        value={statusUpdateModal.plants_returned_reason}
                        onChange={(e) => handleStatusModalChange('plants_returned_reason', e.target.value)}
                        disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                        placeholder="Why plants were returned"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiFormLabel-asterisk': { color: 'error.main' },
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            );
          })()}

          

          {/* Conditional field for Cancelled status - Cancellation Reason */}
          {(statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name) === 'Cancelled' && (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cancellation Reason"
                    required
                    multiline
                    rows={3}
                    value={statusUpdateModal.cancellationReason || ''}
                    onChange={(e) => handleStatusModalChange('cancellationReason', e.target.value)}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    placeholder="Enter reason for cancellation"
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Conditional field for Returned status - Return Reason */}
          {(statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name) === 'Returned' && (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Return Reason"
                    required
                    multiline
                    rows={3}
                    value={statusUpdateModal.returnReason || ''}
                    onChange={(e) => handleStatusModalChange('returnReason', e.target.value)}
                    disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
                    placeholder="Enter reason for return"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleCloseStatusModal}
            disabled={updatingStatus || statusUpdateModal.isCreatingChallan}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleStatusUpdate(
              statusUpdateModal.order?.id,
              statusUpdateModal.order?.status_name,
              statusUpdateModal.order?.order_id,
              statusUpdateModal.emails,
              statusUpdateModal.generateInvoice,
              statusUpdateModal.driverName,
              statusUpdateModal.vehicleNo,
              statusUpdateModal.delivery_date
            )}
            variant="contained"
            disabled={
              updatingStatus || 
              statusUpdateModal.isCreatingChallan ||
              !statusUpdateModal.order?.status_name ||
              (() => {
                const selectedStatusName = statusUpdateModal.order?.status_name || '';
                const selectedOption = statusOptions.find(opt => opt.name === selectedStatusName);
                const statusCategoryForShipping = (selectedOption?.category || '').toLowerCase();
                if (statusCategoryForShipping !== 'shipped') return false;
                const isTruckOrder = String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck';
                const isProcessingTab = (statusFilter || '').toLowerCase() === 'processing';
                const meterNotWorking = isTruthyFlag(statusUpdateModal.meter_not_working);
                const missingDispatchPerson = statusUpdateModal.useCustomDispatchPerson 
                  ? !statusUpdateModal.customDispatchPerson?.trim()
                  : !statusUpdateModal.dispatchPersonName?.trim();
                return (
                  !statusUpdateModal.orderBy?.trim() ||
                  !statusUpdateModal.entityName?.trim() ||
                  !statusUpdateModal.driverName.trim() ||
                  (isTruckOrder && !statusUpdateModal.vehicleType?.trim()) ||
                  !statusUpdateModal.vehicleNo.trim() ||
                  !statusUpdateModal.shippedDate?.trim() ||
                  missingDispatchPerson ||
                  !!validateDispatchLogistics(statusUpdateModal) ||
                  (!meterNotWorking &&
                    isTruckOrder &&
                    isProcessingTab &&
                    !String(statusUpdateModal.start_km || '').trim()) ||
                  (!meterNotWorking &&
                    isTruckOrder &&
                    String(statusUpdateModal.start_km || '').trim() !== '' &&
                    Number(statusUpdateModal.start_km) < 1)
                );
              })() ||
              (() => {
                const selectedStatus = statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name || '';
                if (selectedStatus !== 'Delivered') return false;
                const isDispatchTab = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
                const isTruckOrder = isDispatchTab
                  ? isOrderByTruck(statusUpdateModal.order)
                  : String(statusUpdateModal.orderBy || '').trim().toLowerCase() === 'truck';
                const challan = getOrderChallan(statusUpdateModal.order);
                if (validateDeliveredLogistics(statusUpdateModal, challan)) return true;

                const meterNotWorking = isTruthyFlag(
                  statusUpdateModal.meter_not_working ?? challan?.meter_not_working
                );
                if (isDispatchTab && !isTruckOrder && !meterNotWorking) return false;
                if (meterNotWorking) return false;

                const startKmFromModal = statusUpdateModal.start_km;
                const startKmFromOrder = challan?.start_km;
                const startKm = parseFloat(startKmFromModal || startKmFromOrder);
                const endKm = parseFloat(statusUpdateModal.end_km);
                const hasStartKm = !isNaN(startKm);
                if (hasStartKm && !String(statusUpdateModal.end_km || '').trim()) return true;
                if (hasStartKm && !isNaN(endKm) && endKm <= startKm) return true;
                return false;
              })() ||
              ((statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name) === 'Cancelled' && 
               !statusUpdateModal.cancellationReason?.trim()) ||
              ((statusUpdateModal.order?.status_name || statusUpdateModal.order?.status?.name) === 'Returned' && 
               !statusUpdateModal.returnReason?.trim()) ||
              (() => {
                // Disable if payment is not paid/outstanding/credit and selected status is not "Orders"
                const paymentStatus = (statusUpdateModal.order?.payment_status?.name || statusUpdateModal.order?.payment_status || '').toLowerCase();
                const isPaid = paymentStatus === 'paid' || paymentStatus === 'outstanding' || paymentStatus === 'credit';
                const selectedStatus = statusUpdateModal.order?.status_name || '';
                const isOrdersStatus = selectedStatus === 'Orders';
                return !isPaid && !isOrdersStatus;
              })()
            }
            startIcon={
              updatingStatus || statusUpdateModal.isCreatingChallan ? 
              <CircularProgress size={16} /> : 
              (() => {
                const selectedStatusName = statusUpdateModal.order?.status_name || '';
                const selectedOption = statusOptions.find(opt => opt.name === selectedStatusName);
                const statusCategoryForShipping = (selectedOption?.category || '').toLowerCase();
                return statusCategoryForShipping === 'shipped' ? <DeliveryIcon /> : <UpdateIcon />;
              })()
            }
          >
            {updatingStatus ? 'Updating Status...' : 
             statusUpdateModal.isCreatingChallan ? 'Creating Challan...' :
             (() => {
               const selectedStatusName = statusUpdateModal.order?.status_name || '';
               const selectedOption = statusOptions.find(opt => opt.name === selectedStatusName);
               const statusCategoryForShipping = (selectedOption?.category || '').toLowerCase();
               return statusCategoryForShipping === 'shipped' ? 'Ship Order' : 'Update Status';
             })()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Status Update Modal */}
      <Dialog
        open={paymentUpdateModal.open}
        onClose={() => {
          if (updatingPayment) return
          setPaymentUpdateModal(closePaymentUpdateModalState())
          setPaymentStatusChoices([])
        }}
        maxWidth={paymentUpdateModal.step === 'payment_details' ? false : 'sm'}
        fullWidth={paymentUpdateModal.step !== 'payment_details'}
        scroll={paymentUpdateModal.step === 'payment_details' ? 'paper' : 'body'}
        PaperProps={
          paymentUpdateModal.step === 'payment_details'
            ? PAYMENT_DETAILS_DIALOG_PAPER_PROPS
            : undefined
        }
      >
        <DialogTitle>
          {paymentUpdateModal.step === 'payment_details'
            ? 'Payment Details'
            : 'Update Payment Status'}
          {paymentUpdateModal.order && (
            <Typography variant="body2" color="text.secondary">
              Order ID: {paymentUpdateModal.order.order_id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent
          sx={
            paymentUpdateModal.step === 'payment_details'
              ? PAYMENT_DETAILS_DIALOG_CONTENT_SX
              : undefined
          }
        >
          {paymentUpdateModal.paymentError && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
              {paymentUpdateModal.paymentError}
            </Alert>
          )}

          {paymentUpdateModal.step === 'status' && (
            <>
              {isPaymentUpdateDisabled(paymentUpdateModal.order) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {getPaymentUpdateDisabledReason(paymentUpdateModal.order, statusFilter)}
                </Alert>
              )}
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel id="single-payment-status-label">Payment Status</InputLabel>
                <Select
                  labelId="single-payment-status-label"
                  value={getOrderPaymentStatusValue(paymentUpdateModal.order)}
                  label="Payment Status"
                  disabled={
                    loadingPaymentStatusChoices ||
                    updatingPayment ||
                    isPaymentUpdateDisabled({
                      ...paymentUpdateModal.order,
                      payment_status: {
                        name: paymentUpdateModal.initialPaymentStatus || '',
                      },
                    })
                  }
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      sx: { maxHeight: 300 },
                    },
                    sx: {
                      zIndex: (theme) => theme.zIndex.modal + 1,
                    },
                  }}
                  onChange={(e) => {
                    const newStatus = e.target.value
                    if (newStatus === 'paid' || newStatus === 'credit') {
                      loadAdvancePaymentMethods()
                    }
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      order: {
                        ...prev.order,
                        payment_status: {
                          ...(typeof prev.order?.payment_status === 'object'
                            ? prev.order.payment_status
                            : {}),
                          name: newStatus,
                        },
                      },
                      refundAmount:
                        newStatus === 'refunded'
                          ? String(prev.order?.total_amount ?? prev.refundAmount ?? '')
                          : prev.refundAmount,
                      paymentError: '',
                      ...(newStatus === 'paid'
                        ? getInlinePaidPaymentStepState(prev.orderPaymentDetails)
                        : newStatus === 'credit'
                          ? getCreditPaymentStepState(prev.orderPaymentDetails)
                          : {
                              step: 'status',
                              paymentOption: '',
                              advanceAmount: '',
                              installmentAmount: '',
                              creditNote: '',
                              paymentMethod: '',
                              paymentMethodConfig: null,
                              paymentFields: {},
                              paymentFiles: {},
                            }),
                    }))
                  }}
                >
                  {renderPaymentStatusMenuItems()}
                </Select>
              </FormControl>
              {(paymentUpdateModal.paymentOption === 'credit' ||
                paymentUpdateModal.paymentOption === 'remaining') && (
                <Box
                  sx={{
                    mt: 2,
                    '& .MuiPaper-outlined + .MuiBox-root, & .MuiPaper-outlined + .MuiTextField-root': {
                      mt: 2,
                    },
                  }}
                >
                  <PaymentDetailsStepContent
                    idPrefix="bulk-single-payment-inline"
                    order={paymentUpdateModal.order}
                    orderPaymentDetails={paymentUpdateModal.orderPaymentDetails}
                    paymentOption={paymentUpdateModal.paymentOption}
                    advanceAmount={paymentUpdateModal.advanceAmount}
                    onAdvanceAmountChange={(e) =>
                      setPaymentUpdateModal((prev) => ({
                        ...prev,
                        advanceAmount: e.target.value,
                        paymentError: '',
                      }))
                    }
                    installmentAmount={paymentUpdateModal.installmentAmount || ''}
                    onInstallmentAmountChange={(e) => {
                      const value = e.target.value
                      const remaining = Number(
                        paymentUpdateModal.orderPaymentDetails?.remainingAmount ?? 0
                      )
                      if (value !== '' && Number(value) > remaining) {
                        setPaymentUpdateModal((prev) => ({
                          ...prev,
                          paymentError: 'Installment amount cannot exceed the remaining amount',
                        }))
                        return
                      }
                      setPaymentUpdateModal((prev) => ({
                        ...prev,
                        installmentAmount: value,
                        paymentError: '',
                        ...(prev.paymentOption === 'credit'
                          ? {
                              paymentFields: getCreditChequePaymentFields(
                                prev.paymentFields,
                                value,
                                prev.paymentMethodConfig
                              ),
                            }
                          : {}),
                      }))
                    }}
                    creditNote={paymentUpdateModal.creditNote || ''}
                    onCreditNoteChange={(e) =>
                      setPaymentUpdateModal((prev) => ({
                        ...prev,
                        creditNote: e.target.value,
                        paymentError: '',
                      }))
                    }
                    paymentMethod={paymentUpdateModal.paymentMethod}
                    paymentMethodConfig={paymentUpdateModal.paymentMethodConfig}
                    advancePaymentMethods={advancePaymentMethods}
                    loadingAdvancePaymentMethods={loadingAdvancePaymentMethods}
                    onPaymentMethodChange={(e) => {
                      const methodId = e.target.value
                      setPaymentUpdateModal((prev) => {
                        const payload = buildPaymentMethodChangePayload(
                          methodId,
                          prev.orderPaymentDetails
                        )
                        if (
                          prev.paymentOption === 'credit' &&
                          payload.paymentMethodConfig?.type === 'cheque'
                        ) {
                          payload.paymentFields = getCreditChequePaymentFields(
                            payload.paymentFields,
                            prev.installmentAmount ?? '',
                            payload.paymentMethodConfig
                          )
                        }
                        return { ...prev, ...payload }
                      })
                    }}
                    paymentFields={paymentUpdateModal.paymentFields}
                    paymentFiles={paymentUpdateModal.paymentFiles}
                    onFieldChange={(key, value) => {
                      if (key === 'cheque_amount' && paymentUpdateModal.orderPaymentDetails) return
                      setPaymentUpdateModal((prev) => ({
                        ...prev,
                        paymentFields: { ...prev.paymentFields, [key]: value },
                        paymentError: '',
                      }))
                    }}
                    onFileChange={(key, file) =>
                      setPaymentUpdateModal((prev) => ({
                        ...prev,
                        paymentFiles: { ...prev.paymentFiles, [key]: file },
                        paymentError: '',
                      }))
                    }
                    totalLabel="Total"
                  />
                </Box>
              )}
              {['failed', 'pending', 'outstanding'].includes(
                getOrderPaymentStatusValue(paymentUpdateModal.order)
              ) && (
                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Note"
                    value={paymentUpdateModal.creditNote || ''}
                    onChange={(e) =>
                      setPaymentUpdateModal((prev) => ({
                        ...prev,
                        creditNote: e.target.value,
                        paymentError: '',
                      }))
                    }
                    placeholder="Enter note"
                    multiline
                    minRows={3}
                    required
                  />
                </Box>
              )}
              {getOrderPaymentStatusValue(paymentUpdateModal.order) === 'refunded' && (() => {
            const totalAmount = Number(
              paymentUpdateModal.order?.order?.total_amount ??
              paymentUpdateModal.order?.total_amount ??
              0
            );
            const paidAmountFromOrder = Number(
              paymentUpdateModal.order?.paid_amount ??
              paymentUpdateModal.order?.amount_paid ??
              paymentUpdateModal.order?.advance_payment?.advance_amount ??
              
              0
            );
            const paidAmount = paidAmountFromOrder > 0 ? paidAmountFromOrder : totalAmount;
            const pendingAmount = Math.max(0, totalAmount - paidAmount);
            const formatAmount = (value) => {
              const amount = Number(value || 0);
              return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
            };
            const refundInput = Number(paymentUpdateModal.refundAmount);
            const isRefundInvalid =
              !Number.isFinite(refundInput) || refundInput <= 0 || refundInput > totalAmount;

            return (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Refund Amount"
                  type="number"
                  value={paymentUpdateModal.refundAmount ?? ''}
                  placeholder="Enter refund amount"
                  inputProps={{
                    min: 0,
                    max: totalAmount || undefined,
                    step: 0.01,
                  }}
                  error={isRefundInvalid && paymentUpdateModal.refundAmount !== ''}
                  helperText={
                    isRefundInvalid && paymentUpdateModal.refundAmount !== ''
                      ? 'Enter a valid amount greater than 0 and not more than the order total.'
                      : ''
                  }
                  onChange={(e) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      refundAmount: e.target.value,
                    }))
                  }
                />
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <TextField
                    label="Total"
                    value={formatAmount(totalAmount)}
                    size="small"
                    fullWidth
                    disabled
                  />
                  <TextField
                    label="Paid"
                    value={formatAmount(paidAmount)}
                    size="small"
                    fullWidth
                    disabled
                  />
                  <TextField
                    label="Pending"
                    value={formatAmount(pendingAmount)}
                    size="small"
                    fullWidth
                    disabled
                  />
                </Box>
                <TextField
                  fullWidth
                  size="small"
                  label="Note"
                  value={paymentUpdateModal.creditNote || ''}
                  onChange={(e) =>
                    setPaymentUpdateModal((prev) => ({
                      ...prev,
                      creditNote: e.target.value,
                      paymentError: '',
                    }))
                  }
                  placeholder="Enter note"
                  multiline
                  minRows={3}
                  required
                  sx={{ mt: 3 }}
                />
              </Box>
            );
          })()}
            </>
          )}
        </DialogContent>
        <DialogActions
          sx={
            paymentUpdateModal.step === 'payment_details'
              ? PAYMENT_DETAILS_DIALOG_ACTIONS_SX
              : { px: 3, pb: 2 }
          }
        >
          <Button
            onClick={() => {
              if (paymentUpdateModal.step === 'payment_details') {
                const previousStatus = (
                  paymentUpdateModal.orderPaymentDetails?.current_payment_status ||
                  getOrderPaymentStatusValue(paymentUpdateModal.order) ||
                  'pending'
                )
                  .toString()
                  .toLowerCase()
                setPaymentUpdateModal((prev) => ({
                  ...prev,
                  step: 'status',
                  paymentOption: '',
                  advanceAmount: '',
                  installmentAmount: '',
                  creditNote: '',
                  paymentMethod: '',
                  paymentFields: {},
                  paymentFiles: {},
                  paymentError: '',
                  order: {
                    ...prev.order,
                    payment_status: {
                      ...(typeof prev.order?.payment_status === 'object'
                        ? prev.order.payment_status
                        : {}),
                      name: previousStatus,
                    },
                  },
                }))
                return
              }
              setPaymentUpdateModal(closePaymentUpdateModalState())
              setPaymentStatusChoices([])
            }}
            disabled={updatingPayment}
            sx={{ textTransform: 'none' }}
          >
            {paymentUpdateModal.step === 'status' ? 'Cancel' : 'Back'}
          </Button>

          {paymentUpdateModal.paymentOption === 'credit' &&
            shouldShowCreditSubmitButton(paymentUpdateModal) && (
            <Button
              variant="contained"
              disabled={updatingPayment || !canSubmitCreditPayment(paymentUpdateModal)}
              onClick={handleConfirmCreditPaymentDetails}
              startIcon={updatingPayment ? <CircularProgress size={16} /> : <Payment />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {updatingPayment ? 'Updating...' : 'Submit Payment'}
            </Button>
          )}

          {paymentUpdateModal.paymentOption === 'remaining' && (
            <Button
              variant="contained"
              disabled={updatingPayment || !canSubmitPaidPayment(paymentUpdateModal)}
              onClick={handleConfirmPaidPaymentDetails}
              startIcon={updatingPayment ? <CircularProgress size={16} /> : <Payment />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {updatingPayment ? 'Updating...' : 'Submit Payment'}
            </Button>
          )}

          {paymentUpdateModal.step === 'status' &&
            getOrderPaymentStatusValue(paymentUpdateModal.order) !== 'paid' &&
            getOrderPaymentStatusValue(paymentUpdateModal.order) !== 'credit' && (
            <Button
              onClick={() =>
                handlePaymentStatusUpdate(
                  paymentUpdateModal.order?.id,
                  getOrderPaymentStatusValue(paymentUpdateModal.order),
                  paymentUpdateModal.order?.order_id,
                  paymentUpdateModal.refundAmount,
                  paymentUpdateModal.order?.total_amount,
                  { creditNote: paymentUpdateModal.creditNote }
                )
              }
              variant="contained"
              disabled={
                updatingPayment ||
                isPaymentUpdateDisabled(paymentUpdateModal.order) ||
                (() => {
                  const ps = getOrderPaymentStatusValue(paymentUpdateModal.order)
                  if (
                    ['failed', 'pending', 'outstanding'].includes(ps) &&
                    !String(paymentUpdateModal.creditNote || '').trim()
                  ) {
                    return true
                  }
                  const refundNum = Number(paymentUpdateModal.refundAmount)
                  const maxTotal = Number(paymentUpdateModal.order?.total_amount ?? 0)
                  if (ps === 'refunded') {
                    if (!String(paymentUpdateModal.creditNote || '').trim()) return true
                    return (
                      !Number.isFinite(refundNum) || refundNum <= 0 || refundNum > maxTotal
                    )
                  }
                  return false
                })()
              }
              startIcon={updatingPayment ? <CircularProgress size={16} /> : <Payment />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {updatingPayment ? 'Updating...' : 'Update Payment Status'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Invoice View Modal */}
      <InvoiceModal
        open={invoiceModal.open}
        onClose={handleCloseInvoiceModal}
        maxWidth={false}
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PdfIcon color="error" />
              <Typography variant="h6" fontWeight={600}>
                Invoice - {invoiceModal.product?.product_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handleCloseInvoiceModal}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          {invoiceModal.product && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Order ID: {invoiceModal.product.order_id} • Customer: {invoiceModal.product.customer_name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {invoiceModal.invoice && invoiceModal.invoice.path && (
            <InvoiceFrame
              src={invoiceModal.invoice.path}
              title="Invoice PDF"
              onError={(e) => {
                console.error('Error loading PDF:', e)
                alert('Error loading invoice PDF. Please try downloading instead.')
              }}
            />
          )}
        </DialogContent>
      </InvoiceModal>

      {/* Delivery Challan View Modal */}
      <InvoiceModal
        open={deliveryChallanModal.open}
        onClose={handleCloseDeliveryChallanModal}
        maxWidth={false}
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ChallanIcon color="secondary" />
              <Typography variant="h6" fontWeight={600}>
                Delivery Challan - {deliveryChallanModal.product?.product_name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handleCloseDeliveryChallanModal}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          {deliveryChallanModal.product && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Order ID: {deliveryChallanModal.product.order_id} • Customer: {deliveryChallanModal.product.customer_name}
              {deliveryChallanModal.challan?.vehicle_number && (
                <> • Vehicle: {deliveryChallanModal.challan.vehicle_number}</>
              )}
              {deliveryChallanModal.challan?.delivery_date && (
                <> • Date: {new Date(deliveryChallanModal.challan.delivery_date).toLocaleDateString()}</>
              )}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {deliveryChallanModal.challan && deliveryChallanModal.challan.path && (
            <InvoiceFrame
              src={deliveryChallanModal.challan.path}
              title="Delivery Challan PDF"
              onError={(e) => {
                console.error('Error loading PDF:', e)
                alert('Error loading delivery challan PDF. Please try downloading instead.')
              }}
            />
          )}
        </DialogContent>
      </InvoiceModal>

      {/* Bulk Delivery Challan Modal */}
      <Dialog
        open={bulkChallanModal.open}
        onClose={handleCloseBulkChallanModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeliveryIcon color="secondary" />
            <Typography variant="h6" fontWeight={600}>
              Bulk Status Update & Delivery Management
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Update status for {getSelectedProductsCount()} selected orders. For "Dispatch" status, delivery challans will be created.
          </Typography>
        </DialogTitle>

        <DialogContent>
          {bulkChallanModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bulkChallanModal.error}
            </Alert>
          )}

          {bulkChallanModal.isCreating && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Creating bulk delivery challan...
              </Box>
            </Alert>
          )}

          {(() => {
            // Check payment status for all selected orders
            const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
            const unpaidOrders = selectedOrders.filter(order => {
              const paymentStatus = (order.payment_status?.name || order.payment_status_name || order.payment_status || '').toLowerCase();
              const isPaid = isProcessingDispatchSelection(statusFilter, bulkChallanModal.selectedStatus)
                ? isDispatchPaymentSatisfied(paymentStatus)
                : (paymentStatus === 'paid' || paymentStatus === 'outstanding');
              return !isPaid;
            });
            
            if (unpaidOrders.length > 0) {
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Payment Required
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, mb: 2 }}>
                    {unpaidOrders.length} of {selectedOrders.length} selected order{selectedOrders.length > 1 ? 's' : ''} must be paid before status can be updated. 
                    Please update the payment status first. Only unpaid orders will be updated.
                  </Typography>
                </Alert>
              );
            }
            return null;
          })()}

          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Update Status To</InputLabel>
                  <Select
                    value={
                      // If Dispatch tab is active and Dispatch is selected, clear it
                      (statusFilter === 'Dispatch' && bulkChallanModal.selectedStatus === 'Dispatch') 
                        ? '' 
                        : (bulkChallanModal.selectedStatus || '')
                    }
                    label="Update Status To"
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      const selectedOption = statusOptions.find(opt => opt.name === selectedValue);
                      
                      // Prevent selecting Dispatch when Dispatch tab is active
                      if (statusFilter === 'Dispatch' && selectedValue === 'Dispatch') {
                        return; // Don't allow selection
                      }
                      
                      handleBulkChallanInputChange('selectedStatus', selectedValue);
                      handleBulkChallanInputChange('selectedStatusCategory', selectedOption?.category || '');
                    }}
                    disabled={false}
                  >
                    {statusOptions.map((option) => {
                      const optionName = (option.name || '').toLowerCase();
                      const optionCategory = (option.category || '').toLowerCase();
                      const isCancelled = optionName === 'cancelled' || optionCategory === 'cancelled';
                      const isProcessing = optionName === 'processing' || optionCategory === 'processing';
                      const isDispatch = optionName === 'dispatch' || optionCategory === 'dispatch' || optionCategory === 'shipped';
                      const isDelivered = optionName === 'delivered' || optionCategory === 'delivered';
                      const isReturned = optionName === 'returned' || optionCategory === 'returned';
                      const currentTab = (statusFilter || '').toLowerCase();

                      let shouldEnable = false;
                      if (currentTab === 'confirmed') {
                        shouldEnable = isProcessing || isCancelled;
                      } else if (currentTab === 'processing') {
                        shouldEnable = isDispatch || isCancelled;
                      } else if (currentTab === 'dispatch') {
                        shouldEnable = isDelivered;
                      } else if (currentTab === 'delivered') {
                        shouldEnable = isReturned;
                      } else {
                        // Fallback for mixed/default tabs while preserving our Delivered->Returned rule.
                        shouldEnable = isProcessing || isCancelled || isReturned;
                      }

                      const shouldDisable = !shouldEnable;

                      if (shouldDisable) return null;
                      
                      return (
                        <MenuItem 
                          key={option.id || option.name} 
                          value={option.name}
                          disabled={shouldDisable}
                          sx={shouldDisable ? { 
                            opacity: 0.5,
                            backgroundColor: theme.palette.grey[100],
                            '&:hover': {
                              backgroundColor: theme.palette.grey[100],
                              cursor: 'not-allowed'
                            }
                          } : {}}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            {getCategoryIcon(option.category)}
                            <StatusChip 
                              status={option.category}
                              component="span"
                            >
                              {option.name}
                            </StatusChip>
                            {shouldDisable && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.7rem' }}>
                                Not available
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Show delivery fields only for Dispatch status */}
              {bulkChallanModal.selectedStatus === 'Dispatch' && (
                <>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                      borderRadius: 2,
                      mb: 2
                    }}>
                      <Typography variant="subtitle2" fontWeight={600} color="warning.main">
                        Delivery Information Required for Dispatch Status
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Please provide delivery details to create delivery challans
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                                <FormControl fullWidth required>
                                  <InputLabel>Order By</InputLabel>
                                  <Select
                                    value={bulkChallanModal.orderBy}
                                    onChange={(e) => handleBulkChallanInputChange('orderBy', e.target.value)}
                                    disabled={bulkChallanModal.isCreating}
                                    label="Order By"
                                  >
                                    <MenuItem value="">
                                      <em>Select order by</em>
                                    </MenuItem>
                                    {orderByChoices.map((choice) => (
                                      <MenuItem key={choice.key} value={choice.key}>
                                        {choice.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              {/* Entity Name */}
                              <Grid item xs={12}>
                                <FormControl fullWidth required>
                                  {/* <InputLabel>Delivery Partner Name</InputLabel> */}
                                  <TextField
                                    value={bulkChallanModal.entityName}
                                    onChange={(e) => handleBulkChallanInputChange('entityName', e.target.value)}
                                    disabled={loadingBulkEntities || bulkChallanModal.isCreating}
                                    label="Delivery Partner Name"
                                  />  
                                  {/* <Select
                                    value={bulkChallanModal.entityName}
                                    label="Delivery Partner Name"
                                    onChange={(e) => handleBulkChallanInputChange('entityName', e.target.value)}
                                    disabled={loadingBulkEntities || bulkChallanModal.isCreating}
                                  >
                                    <MenuItem value="">
                                      <em>Select delivery partner</em>
                                    </MenuItem>
                                    {bulkEntityOptions.map((entity) => (
                                      <MenuItem key={entity} value={entity}>{entity}</MenuItem>
                                    ))}
                                  </Select> */}
                                </FormControl>
                              </Grid>
                              <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Driver/Authorized Name"
                              value={bulkChallanModal.driverName}
                              onChange={(e) => {
                                const lettersOnly = e.target.value.replace(/[^A-Za-z ]/g, '');
                                handleBulkChallanInputChange('driverName', lettersOnly);
                              }}
                              disabled={bulkChallanModal.isCreating}
                              required
                              placeholder="Driver/Authorized Name"
                              error={bulkChallanModal.error.includes('Driver name')}
                            />
                          </Grid>
                  {String(bulkChallanModal.orderBy || '').trim().toLowerCase() === 'truck' && (
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Vehicle Type</InputLabel>
                        <Select
                          value={bulkChallanModal.vehicleType || ''}
                          label="Vehicle Type"
                          onChange={(e) => handleBulkChallanInputChange('vehicleType', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                        >
                          <MenuItem value="">
                            <em>Select vehicle type</em>
                          </MenuItem>
                          {vehicleTypes.map((type) => (
                            <MenuItem key={type.id} value={type.value}>
                              {type.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Vehicle No / Reference No"
                      value={bulkChallanModal.vehicleNo}
                      onChange={(e) => {
                        handleBulkChallanInputChange(
                          'vehicleNo',
                          e.target.value.replace(/[^a-zA-Z0-9 ]/g, '')
                        );
                      }}
                      disabled={bulkChallanModal.isCreating}
                      required
                      placeholder="Vehicle No / Reference No"
                      error={
                        !!(bulkChallanModal.error && bulkChallanModal.error.includes('vehicle number'))
                      }
                      helperText=""
                    />
                  </Grid>
                  
                  {['truck', 'by truck'].includes(String(bulkChallanModal.orderBy || '').trim().toLowerCase()) && (
                    <>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isTruthyFlag(bulkChallanModal.meter_not_working)}
                              onChange={(e) => {
                                handleBulkChallanInputChange('meter_not_working', e.target.checked);
                                if (e.target.checked) {
                                  handleBulkChallanInputChange('start_km', '');
                                } else {
                                  handleBulkChallanInputChange('google_maps_km', '');
                                }
                              }}
                              disabled={bulkChallanModal.isCreating}
                            />
                          }
                          label="Meter not working"
                        />
                      </Grid>

                      {!isTruthyFlag(bulkChallanModal.meter_not_working) && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Start KM"
                            type="number"
                            value={bulkChallanModal.start_km || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '' || /^[1-9]\d*$/.test(value)) {
                                handleBulkChallanInputChange('start_km', value);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            disabled={bulkChallanModal.isCreating}
                            required={(statusFilter || '').toLowerCase() === 'processing'}
                            placeholder="Enter starting kilometer reading"
                            inputProps={{
                              min: 1,
                              step: 1,
                              onWheel: (e) => e.currentTarget.blur(),
                            }}
                          />
                        </Grid>
                      )}

                      {isTruthyFlag(bulkChallanModal.meter_not_working) && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            required
                            label="Google Maps KM (round trip)"
                            type="number"
                            value={bulkChallanModal.google_maps_km || ''}
                            onChange={(e) => handleBulkChallanInputChange('google_maps_km', e.target.value)}
                            disabled={bulkChallanModal.isCreating}
                            placeholder="Enter Google Maps KM"
                            inputProps={{
                              min: 0.01,
                              step: 'any',
                              onWheel: (e) => e.currentTarget.blur(),
                            }}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Diesel Receipt Number"
                          value={bulkChallanModal.diesel_receipt_number || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^[a-zA-Z0-9]+$/.test(value)) {
                              handleBulkChallanInputChange('diesel_receipt_number', value);
                            }
                          }}
                          disabled={bulkChallanModal.isCreating}
                          placeholder="Enter diesel receipt number"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body2" fontWeight={500} sx={{ mb: 0.75 }}>
                          Receipt Upload
                          <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
                            *
                          </Box>
                        </Typography>
                        <input
                          id="bulk-multi-diesel-receipt-camera-input"
                          type="file"
                          accept="image/*"
                          capture="environment"
                          style={{ display: 'none' }}
                          disabled={bulkChallanModal.isCreating}
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            e.target.value = '';
                            if (!file) return;
                            const isSupportedCameraImage =
                              ['image/jpeg', 'image/png', 'image/webp'].includes(String(file.type || '').toLowerCase()) ||
                              /\.(jpe?g|png|webp)$/i.test(file.name || '');
                            if (!isSupportedCameraImage) {
                              setBulkChallanModal((prev) => ({
                                ...prev,
                                diesel_receipt_upload: null,
                                diesel_receipt_upload_error: 'Only JPG, PNG, or WEBP images are allowed.',
                                error: '',
                              }));
                              return;
                            }
                            if (file.size > 5 * 1024 * 1024) {
                              setBulkChallanModal((prev) => ({
                                ...prev,
                                diesel_receipt_upload: null,
                                diesel_receipt_upload_error: 'File size must not exceed 5 MB.',
                                error: '',
                              }));
                              return;
                            }
                            setBulkChallanModal((prev) => ({
                              ...prev,
                              diesel_receipt_upload: file,
                              diesel_receipt_upload_error: '',
                              error: '',
                            }));
                          }}
                        />
                        <input
                          id="bulk-multi-diesel-receipt-file-input"
                          type="file"
                          accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
                          style={{ display: 'none' }}
                          disabled={bulkChallanModal.isCreating}
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            e.target.value = '';
                            if (!file) return;
                            const normalizedType = String(file.type || '').toLowerCase();
                            const isImageOrPdf =
                              ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(normalizedType) ||
                              /\.(pdf|jpe?g|png|webp)$/i.test(file.name || '');
                            if (!isImageOrPdf) {
                              setBulkChallanModal((prev) => ({
                                ...prev,
                                diesel_receipt_upload: null,
                                diesel_receipt_upload_error: 'Only JPG, PNG, WEBP, or PDF files are allowed.',
                                error: '',
                              }));
                              return;
                            }
                            if (file.size > 5 * 1024 * 1024) {
                              setBulkChallanModal((prev) => ({
                                ...prev,
                                diesel_receipt_upload: null,
                                diesel_receipt_upload_error: 'File size must not exceed 5 MB.',
                                error: '',
                              }));
                              return;
                            }
                            setBulkChallanModal((prev) => ({
                              ...prev,
                              diesel_receipt_upload: file,
                              diesel_receipt_upload_error: '',
                              error: '',
                            }));
                          }}
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                          <Button
                            variant="outlined"
                            component="label"
                            htmlFor="bulk-multi-diesel-receipt-camera-input"
                            startIcon={<CameraAltIcon />}
                            disabled={bulkChallanModal.isCreating}
                            sx={{ textTransform: 'none' }}
                          >
                            Take Photo
                          </Button>
                          <Button
                            variant="outlined"
                            component="label"
                            htmlFor="bulk-multi-diesel-receipt-file-input"
                            startIcon={<UploadFileIcon />}
                            disabled={bulkChallanModal.isCreating}
                            sx={{ textTransform: 'none' }}
                          >
                            Upload from Device
                          </Button>
                          {bulkChallanModal.diesel_receipt_upload && (
                            <Button
                              variant="text"
                              color="inherit"
                              startIcon={<CloseIcon />}
                              disabled={bulkChallanModal.isCreating}
                              onClick={() => {
                                setBulkChallanModal((prev) => ({
                                  ...prev,
                                  diesel_receipt_upload: null,
                                  diesel_receipt_upload_error: '',
                                  error: '',
                                }));
                              }}
                              sx={{ textTransform: 'none' }}
                            >
                              Clear
                            </Button>
                          )}
                        </Stack>
                        {bulkChallanModal.diesel_receipt_upload_error && (
                          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            {bulkChallanModal.diesel_receipt_upload_error}
                          </Typography>
                        )}
                        {bulkChallanModal.diesel_receipt_upload && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Selected: {bulkChallanModal.diesel_receipt_upload.name}
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Diesel Amount"
                          type="number"
                          value={bulkChallanModal.diesel_amount || ''}
                          onChange={(e) => handleBulkChallanInputChange('diesel_amount', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                          placeholder="Enter diesel amount"
                          inputProps={{
                            min: 0,
                            step: 'any',
                            onWheel: (e) => e.currentTarget.blur(),
                          }}
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Crates Dispatched"
                      type="number"
                      value={bulkChallanModal.crates_dispatched ?? '0'}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          handleBulkChallanInputChange('crates_dispatched', value);
                        }
                      }}
                      disabled={bulkChallanModal.isCreating}
                      placeholder="0"
                      inputProps={{
                        min: 0,
                        step: 1,
                        onWheel: (e) => e.currentTarget.blur(),
                      }}
                    />
                  </Grid>

                  {(statusFilter || '').toLowerCase() === 'processing' && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Extra Plants"
                        type="number"
                        value={bulkChallanModal.extra_plants ?? '0'}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            handleBulkChallanInputChange('extra_plants', value);
                          }
                        }}
                        disabled={bulkChallanModal.isCreating}
                        placeholder="0"
                        inputProps={{
                          min: 0,
                          step: 1,
                          onWheel: (e) => e.currentTarget.blur(),
                        }}
                      />
                    </Grid>
                  )}
                  
                  <Grid item xs={12}>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={bulkChallanModal.useCustomDispatchPerson}
                            onChange={(e) => handleBulkChallanInputChange('useCustomDispatchPerson', e.target.checked)}
                            disabled={bulkChallanModal.isCreating}
                          />
                        }
                        label="Add custom dispatch person"
                      />
                      {bulkChallanModal.useCustomDispatchPerson ? (
                        <TextField
                          fullWidth
                          label="Custom Dispatch Person Name"
                          value={bulkChallanModal.customDispatchPerson}
                          onChange={(e) => handleBulkChallanInputChange('customDispatchPerson', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                          required={bulkChallanModal.selectedStatus === 'Dispatch'}
                          placeholder="Enter custom dispatch person name"
                          sx={{ mt: 1 }}
                        />
                      ) : (
                        <FormControl fullWidth required={bulkChallanModal.selectedStatus === 'Dispatch'} sx={{ mt: 1 }}>
                          <InputLabel>Dispatch Person Name</InputLabel>
                          <Select
                            value={bulkChallanModal.dispatchPersonName}
                            label="Dispatch Person Name"
                            onChange={(e) => handleBulkChallanInputChange('dispatchPersonName', e.target.value)}
                            disabled={bulkChallanModal.isCreating}
                          >
                            <MenuItem value="">
                              <em>Select dispatch person</em>
                            </MenuItem>
                            {dispatchPersons.map((person) => (
                              <MenuItem key={person.id} value={person.name}>
                                {person.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Delivery Date"
                      type="date"
                      value={bulkChallanModal.deliveryDate}
                      onChange={(e) => handleBulkChallanInputChange('deliveryDate', e.target.value)}
                      disabled={bulkChallanModal.isCreating}
                      required={bulkChallanModal.selectedStatus === 'Dispatch'}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Email Addresses (Optional)
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={bulkChallanModal.emailInput}
                          onChange={(e) => handleBulkChallanInputChange('emailInput', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                          placeholder="Enter email address"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addEmailToBulkModal();
                            }
                          }}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={addEmailToBulkModal}
                          disabled={!bulkChallanModal.emailInput?.trim() || bulkChallanModal.isCreating}
                        >
                          Add
                        </Button>
                      </Box>
                      {bulkChallanModal.emails.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {bulkChallanModal.emails.map((email, index) => (
                            <Chip
                              key={index}
                              label={email}
                              onDelete={() => removeEmailFromBulkModal(email)}
                              size="small"
                              disabled={bulkChallanModal.isCreating}
                            />
                          ))}
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Delivery challans will be sent to these email addresses
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={bulkChallanModal.generateInvoice}
                          onChange={(e) => handleBulkChallanInputChange('generateInvoice', e.target.checked)}
                          disabled={bulkChallanModal.isCreating}
                        />
                      }
                      label="Generate Invoice along with Delivery Challan"
                    />
                  </Grid>
                </>
              )}

              {/* Delivered logistics + End KM when applicable */}
              {bulkChallanModal.selectedStatus === 'Delivered' && (() => {
                const selectedOrders = filteredData.filter(order => selectedProducts.includes(order.id));
                const isDispatchTabForBulk = ['dispatch', 'shipped'].includes((statusFilter || '').toLowerCase());
                const selectedOrderWithStartKm = selectedOrders.find(order => {
                  const startKm = order.delivery_challan?.start_km;
                  const hasStartKm = startKm !== null && startKm !== undefined && String(startKm).trim() !== '';
                  if (isDispatchTabForBulk && !isOrderByTruck(order)) return false;
                  return hasStartKm;
                });
                const deliveryChallan =
                  selectedOrderWithStartKm?.delivery_challan ||
                  getOrderChallan(selectedOrders.find((order) => getOrderChallan(order))) ||
                  getOrderChallan(selectedOrders[0]);

                const meterNotWorking = isTruthyFlag(
                  bulkChallanModal.meter_not_working ?? deliveryChallan?.meter_not_working
                );
                const googleMapsKm =
                  bulkChallanModal.google_maps_km ||
                  (deliveryChallan?.google_maps_km !== null &&
                  deliveryChallan?.google_maps_km !== undefined
                    ? String(deliveryChallan.google_maps_km)
                    : '');
                const cratesDispatched = Number(
                  bulkChallanModal.crates_dispatched !== undefined &&
                    String(bulkChallanModal.crates_dispatched).trim() !== ''
                    ? bulkChallanModal.crates_dispatched
                    : (deliveryChallan?.crates_dispatched ?? 0)
                );
                const cratesReturned = Number(bulkChallanModal.crates_returned);
                const plantsReturnedRaw = String(bulkChallanModal.plants_returned ?? '').trim();
                const plantsReturned = plantsReturnedRaw === '' ? 0 : Number(plantsReturnedRaw);
                const finalKm = Number(bulkChallanModal.final_km);
                const mapsKmNumber = Number(googleMapsKm);
                const showKmExcessReason =
                  meterNotWorking &&
                  String(bulkChallanModal.final_km || '').trim() !== '' &&
                  !Number.isNaN(finalKm) &&
                  !Number.isNaN(mapsKmNumber) &&
                  finalKm !== mapsKmNumber;
                const showCratesShortReason =
                  String(bulkChallanModal.crates_returned || '').trim() !== '';
                const showPlantsReason = plantsReturned > 0;

                const driverName = bulkChallanModal.driverName || deliveryChallan?.driver_name || '';
                const vehicleNo =
                  bulkChallanModal.vehicleNo ||
                  deliveryChallan?.vehicle_number ||
                  deliveryChallan?.vehicle_no ||
                  deliveryChallan?.vehicle_name ||
                  '';
                const startKm =
                  bulkChallanModal.start_km ||
                  (
                    deliveryChallan?.start_km !== null && deliveryChallan?.start_km !== undefined
                      ? String(deliveryChallan.start_km)
                      : ''
                  );
                const showEndKmSection = !meterNotWorking && !!deliveryChallan && String(startKm).trim() !== '';
                const isTruckOrder =
                  Boolean(selectedOrderWithStartKm) ||
                  selectedOrders.some((order) => isOrderByTruck(order)) ||
                  ['truck', 'by truck'].includes(
                    String(bulkChallanModal.orderBy || '').trim().toLowerCase()
                  );

                return (
                  <>
                    {showEndKmSection && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Driver Name"
                            value={driverName}
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Vehicle No / Reference No"
                            value={vehicleNo}
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Start KM"
                            value={startKm}
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="End KM"
                            required
                            type="number"
                            value={bulkChallanModal.end_km || ''}
                            onChange={(e) => {
                              handleBulkChallanInputChange('end_km', e.target.value);
                            }}
                            disabled={bulkChallanModal.isCreating}
                            placeholder="Enter ending kilometer reading"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              onWheel: (e) => e.currentTarget.blur(),
                            }}
                            sx={{
                              '& .MuiFormLabel-asterisk': { color: 'error.main' },
                            }}
                            error={(() => {
                              const parsedStartKm = parseFloat(startKm);
                              const endKm = parseFloat(bulkChallanModal.end_km);
                              const hasEndKm = String(bulkChallanModal.end_km || '').trim() !== '';
                              return !hasEndKm || (!isNaN(parsedStartKm) && !isNaN(endKm) && endKm <= parsedStartKm);
                            })()}
                            helperText={(() => {
                              const parsedStartKm = parseFloat(startKm);
                              const endKm = parseFloat(bulkChallanModal.end_km);
                              const hasEndKm = String(bulkChallanModal.end_km || '').trim() !== '';
                              if (!hasEndKm) {
                                return 'End KM is required';
                              }
                              if (!isNaN(parsedStartKm) && !isNaN(endKm) && endKm <= parsedStartKm) {
                                return 'End KM must be greater than Start KM';
                              }
                              return '';
                            })()}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Delivery Logistics
                      </Typography>
                    </Grid>
                    {isTruckOrder && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Meter not Working"
                            value={meterNotWorking ? 'Yes' : 'No'}
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Google Maps KM"
                          value={googleMapsKm || '—'}
                          disabled
                          InputProps={{ readOnly: true }}
                          inputProps={{
                            onWheel: (e) => {
                              e.preventDefault();
                              e.currentTarget.blur();
                            },
                          }}
                        />
                      </Grid>
                      </>
                    )}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Crates Dispatched"
                        value={String(cratesDispatched)}
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    {isTruckOrder && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Diesel Receipt Number"
                            value={
                              bulkChallanModal.diesel_receipt_number ||
                              deliveryChallan?.diesel_receipt_number ||
                              '—'
                            }
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Diesel Amount"
                            value={
                              bulkChallanModal.diesel_amount ||
                              (deliveryChallan?.diesel_amount !== null &&
                              deliveryChallan?.diesel_amount !== undefined
                                ? String(deliveryChallan.diesel_amount)
                                : '—')
                            }
                            disabled
                            InputProps={{ readOnly: true }}
                          />
                        </Grid>
                      </>
                    )}

                    {meterNotWorking && (
                      <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label="Final KM"
                        type="number"
                        value={bulkChallanModal.final_km || ''}
                        onChange={(e) => handleBulkChallanInputChange('final_km', e.target.value)}
                        disabled={bulkChallanModal.isCreating}
                        placeholder="Enter actual trip KM"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: 0,
                          step: 'any',
                          onWheel: (e) => {
                            e.preventDefault();
                            e.currentTarget.blur();
                          },
                        }}
                        sx={{
                          '& .MuiFormLabel-asterisk': { color: 'error.main' },
                        }}
                      />
                    </Grid>
                    )}

                    {showKmExcessReason && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          required
                          label="KM Excess Reason"
                          value={bulkChallanModal.km_excess_reason || ''}
                          onChange={(e) => handleBulkChallanInputChange('km_excess_reason', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                          placeholder="Why KM exceeded Maps estimate"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiFormLabel-asterisk': { color: 'error.main' },
                          }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label="Crates Returned"
                        type="number"
                        value={bulkChallanModal.crates_returned || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            handleBulkChallanInputChange('crates_returned', value);
                          }
                        }}
                        disabled={bulkChallanModal.isCreating}
                        placeholder="Enter crates returned"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{
                          min: 0,
                          step: 1,
                          onWheel: (e) => e.currentTarget.blur(),
                        }}
                        sx={{
                          '& .MuiFormLabel-asterisk': { color: 'error.main' },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label="Crates Return Person"
                        value={bulkChallanModal.crates_returned_person || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                          handleBulkChallanInputChange('crates_returned_person', value);
                        }}
                        disabled={bulkChallanModal.isCreating}
                        placeholder="Enter crates return person name"
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          '& .MuiFormLabel-asterisk': { color: 'error.main' },
                        }}
                      />
                    </Grid>

                    {showCratesShortReason && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          required
                          label="Crates Short Reason"
                          value={bulkChallanModal.crates_short_reason || ''}
                          onChange={(e) => handleBulkChallanInputChange('crates_short_reason', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                          placeholder="Why returned crates are less than dispatched"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiFormLabel-asterisk': { color: 'error.main' },
                          }}
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Plants Returned"
                        type="number"
                        value={bulkChallanModal.plants_returned ?? '0'}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d+$/.test(value)) {
                            handleBulkChallanInputChange('plants_returned', value);
                          }
                        }}
                        disabled={bulkChallanModal.isCreating}
                        placeholder="0"
                        inputProps={{
                          min: 0,
                          step: 1,
                          onWheel: (e) => e.currentTarget.blur(),
                        }}
                      />
                    </Grid>

                    {showPlantsReason && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          required
                          label="Plants Returned Reason"
                          value={bulkChallanModal.plants_returned_reason || ''}
                          onChange={(e) => handleBulkChallanInputChange('plants_returned_reason', e.target.value)}
                          disabled={bulkChallanModal.isCreating}
                          placeholder="Why plants were returned"
                          InputLabelProps={{ shrink: true }}
                          sx={{
                            '& .MuiFormLabel-asterisk': { color: 'error.main' },
                          }}
                        />
                      </Grid>
                    )}
                  </>
                );
              })()}

              {/* Conditional field for Cancelled status - Cancellation Reason */}
              {bulkChallanModal.selectedStatus === 'Cancelled' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Cancellation Reason"
                    required
                    multiline
                    rows={3}
                    value={bulkChallanModal.cancellationReason || ''}
                    onChange={(e) => handleBulkChallanInputChange('cancellationReason', e.target.value)}
                    disabled={bulkChallanModal.isCreating}
                    placeholder="Enter reason for cancellation"
                  />
                </Grid>
              )}

              {/* Conditional field for Returned status - Return Reason */}
              {bulkChallanModal.selectedStatus === 'Returned' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Return Reason"
                    required
                    multiline
                    rows={3}
                    value={bulkChallanModal.returnReason || ''}
                    onChange={(e) => handleBulkChallanInputChange('returnReason', e.target.value)}
                    disabled={bulkChallanModal.isCreating}
                    placeholder="Enter reason for return"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button 
            onClick={handleCloseBulkChallanModal}
            disabled={bulkChallanModal.isCreating}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBulkChallanSubmit}
            variant="contained"
            color="secondary"
            disabled={isBulkSubmitDisabled()}
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.success.main,
              },
              transition: 'all 0.2s ease-in-out'
            }}
            startIcon={
              bulkChallanModal.isCreating ? 
              <CircularProgress size={16} /> : 
              <DeliveryIcon />
            }
          >
            {bulkChallanModal.isCreating ? 'Processing...' : 
              bulkChallanModal.selectedStatus === 'Dispatch' ? 
                `Create ${getEligibleProductsCount()} Challans` : 
                `Update ${getSelectedProductsCount()} Orders`
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Documents via Email Modal */}
      <Dialog
        open={emailDialog.open}
        onClose={handleCloseEmailDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>Send Documents via Email</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Selected orders: {selectedProducts.length}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {emailDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>{emailDialog.error}</Alert>
          )}
          {emailDialog.success && (
            <Alert severity="success" sx={{ mb: 2 }}>{emailDialog.success}</Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add Email"
                  value={emailDialog.emailInput}
                  onChange={(e) =>
                    setEmailDialog((prev) => ({
                      ...prev,
                      emailInput: e.target.value,
                      error: '',
                      success: '',
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addEmailToBulkDialog()
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={addEmailToBulkDialog}
                  sx={{ minWidth: 92, textTransform: 'none' }}
                  startIcon={<AddIcon />}
                >
                  Add
                </Button>
              </Box>
              {emailDialog.emails.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {emailDialog.emails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => removeEmailFromBulkDialog(email)}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {(statusFilter || '').toLowerCase() !== 'dispatch' && (
                  <FormControlLabel
                    control={<Checkbox checked={emailDialog.sendInvoice} onChange={(e) => handleEmailDialogChange('sendInvoice', e.target.checked)} />}
                    label={`Include Invoices (${getAvailableCounts().invoices} available)`}
                  />
                )}
                <FormControlLabel
                  control={<Checkbox checked={emailDialog.sendDelivery} onChange={(e) => handleEmailDialogChange('sendDelivery', e.target.checked)} />}
                  label={`Include Delivery Challans (${getAvailableCounts().challans} available)`}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} disabled={emailDialog.isSending}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={emailDialog.isSending ? <CircularProgress size={16} /> : <SendIcon />}
            disabled={
              emailDialog.isSending ||
              (emailDialog.emails.length === 0 && !isValidBulkEmailInput(emailDialog.emailInput))
            }
            onClick={handleEmailSendSubmit}
            sx={{ textTransform: 'none' }}
          >
            {emailDialog.isSending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual Order Email Modal */}
  <Dialog
  open={singleEmailDialog.open}
  onClose={closeSingleOrderEmail}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <EmailIcon color="primary" />
      <Typography variant="h6" fontWeight={600}>Send Documents via Email</Typography>
    </Box>
    {singleEmailDialog.order && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Order ID: {singleEmailDialog.order.order_id} • {singleEmailDialog.order.product_name}
      </Typography>
    )}
  </DialogTitle>

  <DialogContent>
    {singleEmailDialog.error && (
      <Alert severity="error" sx={{ mb: 2 }}>{singleEmailDialog.error}</Alert>
    )}
    {singleEmailDialog.success && (
      <Alert severity="success" sx={{ mb: 2 }}>{singleEmailDialog.success}</Alert>
    )}

    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box display="flex" gap={1}>
          <TextField
            fullWidth
            label="Add Email"
            placeholder="Enter email and click Add"
            value={singleEmailDialog.emailInput}
            onChange={(e) => setSingleEmailDialog(prev => ({ ...prev, emailInput: e.target.value, error: '', success: '' }))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmailToSingle(); } }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addEmailToSingle}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add
          </Button>
        </Box>

        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {singleEmailDialog.emails.map(email => (
            <Chip key={email} label={email} onDelete={() => removeEmailFromSingle(email)} />
          ))}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {(statusFilter || '').toLowerCase() !== 'dispatch' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={singleEmailDialog.sendInvoice}
                  onChange={(e) =>
                    setSingleEmailDialog(prev => ({ ...prev, sendInvoice: e.target.checked }))
                  }
                  disabled={!singleEmailDialog.order?.invoice?.path}
                />
              }
              label={`Include Invoice ${singleEmailDialog.order?.invoice?.path ? '' : '(not available)'}`}
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={singleEmailDialog.sendDelivery}
                onChange={(e) =>
                  setSingleEmailDialog(prev => ({ ...prev, sendDelivery: e.target.checked }))
                }
                disabled={!singleEmailDialog.order?.delivery_challan?.path}
              />
            }
            label={`Include Delivery Challan ${singleEmailDialog.order?.delivery_challan?.path ? '' : '(not available)'}`}
          />
        </Box>
      </Grid>
    </Grid>
  </DialogContent>

  <DialogActions>
    <Button onClick={closeSingleOrderEmail} disabled={singleEmailDialog.isSending}>
      Cancel
    </Button>
    <Button
      variant="contained"
      startIcon={
        singleEmailDialog.isSending ? <CircularProgress size={16} /> : <SendIcon />
      }
      disabled={
        singleEmailDialog.isSending ||
        (!singleEmailDialog.order?.invoice?.path && !singleEmailDialog.order?.delivery_challan?.path) ||
        (singleEmailDialog.emails.length === 0 && !isValidSingleEmailInput(singleEmailDialog.emailInput))
      }
      onClick={submitSingleOrderEmail}
      sx={{ textTransform: 'none' }}
    >
      {singleEmailDialog.isSending ? 'Sending...' : 'Send Email'}
    </Button>
  </DialogActions>
</Dialog>

      {/* Bulk Payment Status Update Modal */}
      <Dialog
        open={bulkPaymentStatusModal.open}
        onClose={handleCloseBulkPaymentStatusModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Update Payment Status
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment()).length} unpaid of{' '}
            {getSelectedProductsCount()} selected order{getSelectedProductsCount() > 1 ? 's' : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {bulkPaymentStatusModal.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bulkPaymentStatusModal.error}
            </Alert>
          )}

          {bulkPaymentStatusModal.paymentError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bulkPaymentStatusModal.paymentError}
            </Alert>
          )}

          {bulkPaymentStatusModal.isUpdating && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Updating payment status...
              </Box>
            </Alert>
          )}

          {bulkPaymentStatusModal.summaryOrder &&
            isPaymentUpdateDisabled({
              ...bulkPaymentStatusModal.summaryOrder,
              payment_status: {
                name: bulkPaymentStatusModal.initialPaymentStatus || '',
              },
            }) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {getPaymentUpdateDisabledReason(bulkPaymentStatusModal.summaryOrder, statusFilter)}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="payment-status-label">Payment Status</InputLabel>
            <Select
              labelId="payment-status-label"
              value={bulkPaymentStatusModal.selectedPaymentStatus || ''}
              label="Payment Status"
              onChange={(e) => {
                const newStatus = e.target.value
                if (newStatus === 'paid') {
                  loadAdvancePaymentMethods()
                }
                setBulkPaymentStatusModal((prev) => ({
                  ...prev,
                  selectedPaymentStatus: newStatus,
                  error: '',
                  paymentError: '',
                  refundAmount:
                    newStatus === 'refunded'
                      ? (prev.summaryOrder?.total_amount ?? prev.refundAmount ?? '')
                      : prev.refundAmount,
                  ...(newStatus === 'paid'
                    ? getInlinePaidPaymentStepState(prev.orderPaymentDetails)
                    : newStatus === 'credit'
                      ? getCreditPaymentStepState(prev.orderPaymentDetails)
                      : {
                          step: 'status',
                          paymentOption: '',
                          advanceAmount: '',
                          installmentAmount: '',
                          creditNote: '',
                          paymentMethod: '',
                          paymentMethodConfig: null,
                          paymentFields: {},
                          paymentFiles: {},
                        }),
                }))
              }}
              disabled={
                bulkPaymentStatusModal.isUpdating ||
                loadingPaymentStatusChoices ||
                isPaymentUpdateDisabled({
                  ...bulkPaymentStatusModal.summaryOrder,
                  payment_status: {
                    name: bulkPaymentStatusModal.initialPaymentStatus || '',
                  },
                })
              }
              MenuProps={{
                disableScrollLock: true,
                PaperProps: {
                  sx: { maxHeight: 300 },
                },
                sx: {
                  zIndex: (theme) => theme.zIndex.modal + 1,
                },
              }}
            >
              {renderPaymentStatusMenuItems()}
            </Select>
          </FormControl>

          {(bulkPaymentStatusModal.paymentOption === 'credit' ||
            bulkPaymentStatusModal.paymentOption === 'remaining') && (() => {
            const unpaidCount = getUnpaidOrdersFromList(getSelectedOrdersForBulkPayment()).length
            return (
              <Box
                sx={{
                  mt: 2,
                  '& .MuiPaper-outlined + .MuiBox-root, & .MuiPaper-outlined + .MuiTextField-root': {
                    mt: 2,
                  },
                }}
              >
                <PaymentDetailsStepContent
                  idPrefix="bulk-payment-inline"
                  order={bulkPaymentStatusModal.summaryOrder}
                  orderPaymentDetails={bulkPaymentStatusModal.orderPaymentDetails}
                  paymentOption={bulkPaymentStatusModal.paymentOption}
                  advanceAmount={bulkPaymentStatusModal.advanceAmount}
                  onAdvanceAmountChange={(e) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      advanceAmount: e.target.value,
                      paymentError: '',
                    }))
                  }
                  installmentAmount={bulkPaymentStatusModal.installmentAmount || ''}
                  onInstallmentAmountChange={(e) => {
                    const value = e.target.value
                    const remaining = Number(
                      bulkPaymentStatusModal.orderPaymentDetails?.remainingAmount ?? 0
                    )
                    if (value !== '' && Number(value) > remaining) {
                      setBulkPaymentStatusModal((prev) => ({
                        ...prev,
                        paymentError: 'Installment amount cannot exceed the remaining amount',
                      }))
                      return
                    }
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      installmentAmount: value,
                      paymentError: '',
                      ...(prev.paymentOption === 'credit'
                        ? {
                            paymentFields: getCreditChequePaymentFields(
                              prev.paymentFields,
                              value,
                              prev.paymentMethodConfig
                            ),
                          }
                        : {}),
                    }))
                  }}
                  creditNote={bulkPaymentStatusModal.creditNote || ''}
                  onCreditNoteChange={(e) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      creditNote: e.target.value,
                      paymentError: '',
                    }))
                  }
                  paymentMethod={bulkPaymentStatusModal.paymentMethod}
                  paymentMethodConfig={bulkPaymentStatusModal.paymentMethodConfig}
                  advancePaymentMethods={advancePaymentMethods}
                  loadingAdvancePaymentMethods={loadingAdvancePaymentMethods}
                  onPaymentMethodChange={(e) => {
                    const methodId = e.target.value
                    setBulkPaymentStatusModal((prev) => {
                      const payload = buildPaymentMethodChangePayload(
                        methodId,
                        prev.orderPaymentDetails
                      )
                      if (prev.paymentOption === 'credit' && payload.paymentMethodConfig?.type === 'cheque') {
                        payload.paymentFields = getCreditChequePaymentFields(
                          payload.paymentFields,
                          prev.installmentAmount ?? '',
                          payload.paymentMethodConfig
                        )
                      }
                      return { ...prev, ...payload }
                    })
                  }}
                  paymentFields={bulkPaymentStatusModal.paymentFields}
                  paymentFiles={bulkPaymentStatusModal.paymentFiles}
                  onFieldChange={(key, value) => {
                    if (key === 'cheque_amount' && bulkPaymentStatusModal.orderPaymentDetails) {
                      return
                    }
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      paymentFields: { ...prev.paymentFields, [key]: value },
                      paymentError: '',
                    }))
                  }}
                  onFileChange={(key, file) =>
                    setBulkPaymentStatusModal((prev) => ({
                      ...prev,
                      paymentFiles: { ...prev.paymentFiles, [key]: file },
                      paymentError: '',
                    }))
                  }
                  itemsLimit={3}
                  totalLabel="Reference total"
                  summaryBanner={
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Applying {bulkPaymentStatusModal.paymentOption === 'credit' ? 'credit payment' : 'payment'} to{' '}
                      <strong>{unpaidCount}</strong> unpaid order
                      {unpaidCount > 1 ? 's' : ''}.
                    </Typography>
                  }
                />
              </Box>
            )
          })()}

          {['failed', 'pending', 'outstanding'].includes(
            bulkPaymentStatusModal.selectedPaymentStatus
          ) && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Note"
                value={bulkPaymentStatusModal.creditNote || ''}
                onChange={(e) =>
                  setBulkPaymentStatusModal((prev) => ({
                    ...prev,
                    creditNote: e.target.value,
                    paymentError: '',
                  }))
                }
                placeholder="Enter note"
                multiline
                minRows={3}
                required
                disabled={bulkPaymentStatusModal.isUpdating}
              />
            </Box>
          )}

          {bulkPaymentStatusModal.selectedPaymentStatus === 'refunded' &&
            (() => {
              const apiDetails = bulkPaymentStatusModal.orderPaymentDetails
              const totalAmount = Number(
                apiDetails?.totalAmount ??
                  bulkPaymentStatusModal.summaryOrder?.total_amount ??
                  bulkPaymentStatusModal.summaryOrder?.total ??
                  0
              )
              const advancePaidTotal = getAdvancePaymentEntries(
                bulkPaymentStatusModal.summaryOrder || bulkPaymentStatusModal
              ).reduce((sum, entry) => sum + Number(entry.advance_amount || 0), 0)
              const paidAmountFallback =
                advancePaidTotal > 0
                  ? advancePaidTotal
                  : (bulkPaymentStatusModal.summaryOrder?.amount_paid ??
                      bulkPaymentStatusModal.summaryOrder?.paid ??
                      0)
              const paidAmount = Number(apiDetails?.paidAmount ?? paidAmountFallback)
              const pendingAmount = Number(
                apiDetails?.remainingAmount ?? Math.max(0, totalAmount - paidAmount)
              )
              const maxAmount = paidAmount
              const refundAmount = Number(bulkPaymentStatusModal.refundAmount || 0)
              const isExceeding =
                bulkPaymentStatusModal.refundAmount !== '' &&
                (!Number.isFinite(refundAmount) || refundAmount <= 0 || refundAmount > maxAmount)
              const formatAmount = (n) =>
                Number.isNaN(n)
                  ? '0.00'
                  : n.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })

              return (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ mb: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                      fullWidth
                      label="Refund Amount"
                      type="number"
                      value={bulkPaymentStatusModal.refundAmount ?? ''}
                      placeholder="Enter refund amount"
                      inputProps={{
                        min: 0,
                        max: maxAmount,
                        step: 0.01,
                      }}
                      error={isExceeding}
                      helperText={
                        isExceeding
                          ? 'Refund amount cannot exceed the available paid amount'
                          : ''
                      }
                      onChange={(e) =>
                        setBulkPaymentStatusModal((prev) => ({
                          ...prev,
                          refundAmount: e.target.value,
                        }))
                      }
                      disabled={bulkPaymentStatusModal.isUpdating}
                    />
                    <Box display="flex" gap={2} mt={2} width="100%">
                      <TextField
                        label="Total"
                        value={formatAmount(totalAmount)}
                        size="small"
                        fullWidth
                        disabled
                      />
                      <TextField
                        label="Paid"
                        value={formatAmount(paidAmount)}
                        size="small"
                        fullWidth
                        disabled
                      />
                      <TextField
                        label="Pending"
                        value={formatAmount(pendingAmount)}
                        size="small"
                        fullWidth
                        disabled
                      />
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="Note"
                      value={bulkPaymentStatusModal.creditNote || ''}
                      onChange={(e) =>
                        setBulkPaymentStatusModal((prev) => ({
                          ...prev,
                          creditNote: e.target.value,
                          paymentError: '',
                        }))
                      }
                      placeholder="Enter note"
                      multiline
                      minRows={3}
                      required
                      sx={{ mt: 2 }}
                      disabled={bulkPaymentStatusModal.isUpdating}
                    />
                  </Box>
                </Box>
              )
            })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseBulkPaymentStatusModal}
            disabled={bulkPaymentStatusModal.isUpdating}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>

          {bulkPaymentStatusModal.paymentOption === 'credit' &&
            shouldShowCreditSubmitButton({
              ...bulkPaymentStatusModal,
              order: bulkPaymentStatusModal.summaryOrder,
            }) && (
            <Button
              variant="contained"
              disabled={
                bulkPaymentStatusModal.isUpdating ||
                !canSubmitCreditPayment({
                  ...bulkPaymentStatusModal,
                  order: bulkPaymentStatusModal.summaryOrder,
                })
              }
              onClick={handleConfirmBulkCreditPaymentDetails}
              startIcon={bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <Payment />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Submit Payment'}
            </Button>
          )}

          {bulkPaymentStatusModal.paymentOption === 'remaining' && (
            <Button
              variant="contained"
              disabled={
                bulkPaymentStatusModal.isUpdating ||
                !canSubmitPaidPayment(bulkPaymentStatusModal)
              }
              onClick={handleConfirmBulkPaidPaymentDetails}
              startIcon={bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <Payment />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Submit Payment'}
            </Button>
          )}

          {bulkPaymentStatusModal.selectedPaymentStatus !== 'paid' &&
            bulkPaymentStatusModal.selectedPaymentStatus !== 'credit' && (
            <Button
              onClick={handleBulkPaymentStatusUpdateFromModal}
              variant="contained"
              color="primary"
              disabled={
                !bulkPaymentStatusModal.selectedPaymentStatus ||
                bulkPaymentStatusModal.isUpdating ||
                isPaymentUpdateDisabled({
                  ...bulkPaymentStatusModal.summaryOrder,
                  payment_status: {
                    name: bulkPaymentStatusModal.initialPaymentStatus || '',
                  },
                }) ||
                (() => {
                  const ps = bulkPaymentStatusModal.selectedPaymentStatus
                  if (
                    ['failed', 'pending', 'outstanding'].includes(ps) &&
                    !String(bulkPaymentStatusModal.creditNote || '').trim()
                  ) {
                    return true
                  }
                  if (ps !== 'refunded') return false
                  if (!String(bulkPaymentStatusModal.creditNote || '').trim()) return true
                  const refundNum = Number(bulkPaymentStatusModal.refundAmount)
                  const apiDetails = bulkPaymentStatusModal.orderPaymentDetails
                  const advancePaidTotal = getAdvancePaymentEntries(
                    bulkPaymentStatusModal.summaryOrder || bulkPaymentStatusModal
                  ).reduce((sum, entry) => sum + Number(entry.advance_amount || 0), 0)
                  const paidFallback =
                    advancePaidTotal > 0
                      ? advancePaidTotal
                      : (bulkPaymentStatusModal.summaryOrder?.amount_paid ??
                          bulkPaymentStatusModal.summaryOrder?.paid ??
                          0)
                  const maxRefund = Number(apiDetails?.paidAmount ?? paidFallback)
                  return (
                    !Number.isFinite(refundNum) ||
                    refundNum <= 0 ||
                    refundNum > maxRefund
                  )
                })()
              }
              startIcon={bulkPaymentStatusModal.isUpdating ? <CircularProgress size={16} /> : <Payment />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {bulkPaymentStatusModal.isUpdating ? 'Updating...' : 'Update Payment'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Box>
  )
}

export default BulkProductList  